/* ====================================================================================================
PRICE DISPLAY (currency symbol position + trailing zeros)
==================================================================================================== */
(() => {
  function stripTrailingZerosFromMoney(moneyStr) {
    return String(moneyStr).replace(
      /([,.])(\d*?)0+(\D*)$/,
      (_, sep, digits, suffix) => {
        if (!digits) return suffix;
        return sep + digits + suffix;
      },
    );
  }

  function formatThemeMoney(cents) {
    const format = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;
    if (theme?.Currency?.formatMoney && format) {
      return theme.Currency.formatMoney(Number(cents) || 0, format);
    }
    return String((Number(cents) || 0) / 100);
  }

  function patchFormatMoney() {
    if (!window.theme || !theme.Currency || !theme.Currency.formatMoney) return;
    if (theme.Currency.__stoyikPriceDisplayPatched) return;

    const original = theme.Currency.formatMoney.bind(theme.Currency);
    theme.Currency.formatMoney = (cents, format = "") => {
      let result = original(cents, format);
      if (theme.settings && theme.settings.priceDecimalsDisplay === "strip_trailing_zeros") {
        result = stripTrailingZerosFromMoney(result);
      }
      return result;
    };
    theme.Currency.stripTrailingZerosFromMoney = stripTrailingZerosFromMoney;
    theme.Currency.formatThemeMoney = formatThemeMoney;
    theme.Currency.__stoyikPriceDisplayPatched = true;
  }

  patchFormatMoney();
  if (!theme.Currency || !theme.Currency.__stoyikPriceDisplayPatched) {
    document.addEventListener("DOMContentLoaded", patchFormatMoney);
  } else if (theme.Currency && !theme.Currency.formatThemeMoney) {
    theme.Currency.formatThemeMoney = formatThemeMoney;
  }
})();

/* ====================================================================================================
OVERLAY (Menu + Drawer)
==================================================================================================== */

/* Cart drawer ==================================================================================================== */

(() => {
  const formatMoney = (priceInCents, currencyCode) => {
    if (!priceInCents || priceInCents === 0) {
      const format = theme?.settings?.currencyCodeEnabled
        ? theme?.settings?.moneyWithCurrencyFormat
        : theme?.settings?.moneyFormat;

      if (theme?.Currency?.formatMoney && format) {
        return theme.Currency.formatMoney(0, format);
      }
      return "€0,00";
    }

    const format = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;

    if (theme?.Currency?.formatMoney && format) {
      return theme.Currency.formatMoney(priceInCents, format);
    }

    const numeric = Number(priceInCents);
    if (!Number.isFinite(numeric)) {
      const format = theme?.settings?.currencyCodeEnabled
        ? theme?.settings?.moneyWithCurrencyFormat
        : theme?.settings?.moneyFormat;

      if (theme?.Currency?.formatMoney && format) {
        return theme.Currency.formatMoney(0, format);
      }
      return "€0,00";
    }

    const amount = numeric / 100;
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currencyCode || "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const fetchShippingRates = async (countryCode) => {
    try {
      const cartDrawer = document.querySelector("cart-drawer");
      if (!cartDrawer) return null;

      const sectionId = cartDrawer.dataset.sectionId;
      const zip = cartDrawer.dataset.customerZip || "";
      const province = cartDrawer.dataset.customerProvince || "";

      const body = JSON.stringify({
        shipping_address: { zip, country: countryCode, province },
      });

      let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;
      sectionUrl = sectionUrl.replace("//", "/");

      const response = await fetch(sectionUrl, {
        ...theme.utils.fetchConfig("javascript"),
        ...{ body },
      });

      if (!response.ok) return null;

      const data = await response.json();
      return data.shipping_rates || null;
    } catch (error) {
      console.error("Error fetching shipping rates:", error);
      return null;
    }
  };

  const calculateShippingRange = (shippingRates) => {
    if (!shippingRates || shippingRates.length === 0) return null;

    const prices = shippingRates
      .map((rate) => {
        const price = rate.price || 0;
        const parsed = typeof price === "string" ? parseFloat(price) : price;
        return !isNaN(parsed) ? parsed * 100 : null;
      })
      .filter((price) => price !== null && price >= 0);

    if (prices.length === 0) return null;

    const min = Math.min(...prices);
    const max = Math.max(...prices);

    const cartDrawer = document.querySelector("cart-drawer");
    const currencyCode = cartDrawer?.dataset.cartCurrency || "EUR";

    return {
      min: formatMoney(min, currencyCode),
      max: formatMoney(max, currencyCode),
    };
  };

  const updateShippingRange = async () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    const scrollableFooter = cartDrawer.querySelector(
      "[data-scrollable-footer]",
    );
    if (!scrollableFooter) return;

    const shippingRangeElement = scrollableFooter.querySelector(
      "[data-shipping-range]",
    );
    if (!shippingRangeElement) return;

    // Mode metafield : plage calculée depuis les paliers metaobject (voir module dédié).
    if (
      scrollableFooter.querySelector(
        '[data-shipping-estimate-mode="metafield"]',
      ) ||
      cartDrawer.querySelector("[data-cart-shipping-metafield]")
    ) {
      if (typeof window.__stoyikUpdateMetafieldShippingRange === "function") {
        window.__stoyikUpdateMetafieldShippingRange();
      }
      return;
    }

    const countryCode = cartDrawer.dataset.localizationCountry;
    if (!countryCode) {
      shippingRangeElement.textContent = "";
      return;
    }

    shippingRangeElement.textContent = "Chargement...";

    const shippingRates = await fetchShippingRates(countryCode);
    const range = calculateShippingRange(shippingRates);

    if (range) {
      if (range.min === range.max) {
        shippingRangeElement.textContent = range.min;
      } else {
        shippingRangeElement.textContent = `${range.min} - ${range.max}`;
      }
    } else {
      shippingRangeElement.textContent = "";
    }
  };

  const initCartNoteAutoSave = () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    const scrollableFooter = cartDrawer.querySelector(
      "[data-scrollable-footer]",
    );
    if (!scrollableFooter) return;

    const textarea = scrollableFooter.querySelector('textarea[name="note"]');
    if (!textarea) return;

    let saveTimeout;
    const saveCartNote = async () => {
      clearTimeout(saveTimeout);
      saveTimeout = setTimeout(async () => {
        try {
          const note = textarea.value;
          const response = await fetch(`${theme.routes.cart_url}/update.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ note }),
          });

          if (!response.ok) {
            console.error("Failed to save cart note");
          }
        } catch (error) {
          console.error("Error saving cart note:", error);
        }
      }, 1000);
    };

    textarea.addEventListener("input", saveCartNote);
    textarea.addEventListener("blur", () => {
      clearTimeout(saveTimeout);
      saveCartNote();
    });
  };

  const initShippingCalculatorTrigger = () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    const scrollableFooter = cartDrawer.querySelector(
      "[data-scrollable-footer]",
    );
    if (!scrollableFooter) return;

    const trigger = scrollableFooter.querySelector(
      "[data-shipping-calculator-trigger]",
    );
    if (!trigger) return;

    const sectionId = cartDrawer.dataset.sectionId;
    const modal = cartDrawer.querySelector(
      `#ShippingCalculatorScrollable-${sectionId}`,
    );

    if (!modal) return;

    trigger.addEventListener("click", (event) => {
      if (modal.tagName === "MODAL-ELEMENT") {
        if (typeof modal.show === "function") {
          event.preventDefault();
          event.stopPropagation();
          modal.show(trigger);
        }
      }
    });
  };

  const initScrollableFooter = () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;

    const scrollableFooter = cartDrawer.querySelector(
      "[data-scrollable-footer]",
    );
    if (!scrollableFooter) return;

    updateShippingRange();
    initCartNoteAutoSave();
    initShippingCalculatorTrigger();
  };

  document.addEventListener("DOMContentLoaded", () => {
    initScrollableFooter();
  });

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.querySelector("cart-drawer")) {
      initScrollableFooter();
    }
  });

  document.addEventListener("cart:updated", (event) => {
    const cart = event && event.detail && event.detail.cart ? event.detail.cart : null;
    const cartDrawer = document.querySelector("cart-drawer");
    const metafieldMode =
      cartDrawer &&
      (cartDrawer.querySelector('[data-shipping-estimate-mode="metafield"]') ||
        cartDrawer.querySelector("[data-cart-shipping-metafield]"));
    if (
      metafieldMode &&
      typeof window.__stoyikUpdateMetafieldShippingRange === "function"
    ) {
      window.__stoyikUpdateMetafieldShippingRange(cart || undefined);
      return;
    }
    updateShippingRange();
  });
})();

/* ====================================================================================================
SAFE STICKY — copie conforme d'Impact (assets/theme.js.liquid safe-sticky.js)
Traduit vers un constructeur ordinaire appliqué aux éléments existants du DOM Concept.

IMPORTANT — pourquoi on n'utilise PAS transform ici :
  transform crée un nouveau "containing block" pour tout position:fixed à l'intérieur.
  .product__info contient des drawers, overlays et éléments fixed du thème — les appliquer
  sur l'élément parent les ferait se décaler. will-change:transform a le même effet.
  → on utilise style.top exactement comme Impact.

Logique :
  throttle (rAF) → 1 seule exécution de _checkPosition par frame d'animation.
  _checkPosition lit scrollY, calcule le delta, met à jour currentTop (clamped), écrit style.top.
  IntersectionObserver 500px = équivalent exact de inView({ margin:"500px" }) d'Impact.
  .product__preview exclu : c'est un <sticky-element> géré par theme.js.
==================================================================================================== */

(() => {
  // Impact throttle (theme.js.liquid:159-176) — ES5, sémantique identique.
  function throttle(callback) {
    var requestId = null;
    function later() {
      requestId = null;
      callback();
    }
    function throttled() {
      if (requestId === null) requestId = requestAnimationFrame(later);
    }
    throttled.cancel = function () {
      cancelAnimationFrame(requestId);
      requestId = null;
    };
    return throttled;
  }

  function SafeSticky(el) {
    this.el = el;
    this._initialTop = 0;
    this._lastKnownY = 0;
    this._currentTop = 0;
    this._position = "relative";
    this._inViewAttached = false;

    this._resizeObserver = new ResizeObserver(
      this._recalculateStyles.bind(this),
    );
    this._scrollListener = throttle(this._checkPosition.bind(this));

    var self = this;
    this._io = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          if (self._inViewAttached) return;
          self._inViewAttached = true;
          self._resizeObserver.observe(self.el);
          window.addEventListener("scroll", self._scrollListener, {
            passive: true,
          });
          self._recalculateStyles();
        } else {
          if (!self._inViewAttached) return;
          self._inViewAttached = false;
          self._scrollListener.cancel();
          self._resizeObserver.unobserve(self.el);
          window.removeEventListener("scroll", self._scrollListener);
          self.el.style.removeProperty("top");
        }
      },
      { rootMargin: "500px 0px 500px 0px" },
    );
    this._io.observe(el);
  }

  // Impact recalculateStyles_fn (theme.js.liquid:728-734)
  SafeSticky.prototype._recalculateStyles = function () {
    this._scrollListener.cancel();
    this.el.style.removeProperty("top");
    var cs = getComputedStyle(this.el);
    this._initialTop = parseInt(cs.top) || 0;
    this._position = cs.position;
    this._checkPosition();
  };

  // Impact checkPosition_fn (theme.js.liquid:736-749) — traduction directe ligne par ligne.
  SafeSticky.prototype._checkPosition = function () {
    if (this._position !== "sticky") {
      return this.el.style.removeProperty("top");
    }
    var bounds = this.el.getBoundingClientRect();
    var maxTop =
      bounds.top + window.scrollY - this.el.offsetTop + this._initialTop;
    var minTop = this.el.clientHeight - window.innerHeight + 20;
    if (window.scrollY < this._lastKnownY) {
      this._currentTop = this._currentTop - (window.scrollY - this._lastKnownY);
    } else {
      this._currentTop = this._currentTop + (this._lastKnownY - window.scrollY);
    }
    this._currentTop = Math.min(
      Math.max(this._currentTop, -minTop),
      maxTop,
      this._initialTop,
    );
    this._lastKnownY = window.scrollY;
    this.el.style.top = Math.round(this._currentTop) + "px";
  };

  SafeSticky.prototype.destroy = function () {
    this._io.disconnect();
    this._scrollListener.cancel();
    this._resizeObserver.disconnect();
    window.removeEventListener("scroll", this._scrollListener);
    this.el.style.removeProperty("top");
  };

  function init() {
    document
      .querySelectorAll(
        ".product__info.lg\\:sticky, .product__gallery-container.lg\\:sticky",
      )
      .forEach(function (el) {
        new SafeSticky(el);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ====================================================================================================
[CUSTOM] COLLAGE SWEEP — plus de drag-to-scroll custom.
Le balayage est désormais géré nativement par slider-element (même système que Multicolonne)
via les classes CSS slider--tablet et slider--desktop du thème.
Aucun JS supplémentaire n'est nécessaire pour le mode balayage.
==================================================================================================== */

/* ====================================================================================================
[CUSTOM] HELP DRAWER — zone d’expédition (select + texte dynamique sous le select)
==================================================================================================== */

(function () {
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c];
    });
  }

  function normalizeSubzonesPayload(v) {
    if (v == null || v === "") return v;
    if (Array.isArray(v)) return v;
    if (typeof v === "string") {
      var s = v.trim();
      if (s.charAt(0) === "[" && s.charAt(s.length - 1) === "]") {
        try {
          var parsed = JSON.parse(s);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {}
      }
      /* Phrase type metafield_text (« a, b, c ») → tableau pour formatage unifié */
      if (s.indexOf(",") !== -1) {
        var bits = s
          .split(/\s*,\s*/)
          .map(function (x) {
            return x.replace(/\s+$/g, "").replace(/^\s+/g, "");
          })
          .filter(Boolean);
        if (bits.length > 1) return bits;
      }
    }
    return v;
  }

  function formatSubzonesHtml(v, i18n) {
    if (!i18n) i18n = {};
    v = normalizeSubzonesPayload(v);
    if (v == null || v === "") return "";
    var intro = (i18n.zones_intro != null && String(i18n.zones_intro).trim())
      ? String(i18n.zones_intro).trim()
      : "";
    var introHtml = intro
      ? '<p class="help-shipping-zones-intro not-rte">' +
        escapeHtml(intro) +
        "</p>"
      : "";
    var body = "";
    if (Array.isArray(v)) {
      var parts = v
        .map(function (x) {
          if (x == null || x === "") return "";
          if (typeof x === "object") {
            return (
              x.display_name ||
              x.name ||
              x.handle ||
              (x.system && (x.system.display_name || x.system.handle)) ||
              ""
            ).toString();
          }
          return String(x);
        })
        .filter(Boolean);
      if (!parts.length) return "";
      var line =
        parts
          .map(function (p) {
            return escapeHtml(p);
          })
          .join(", ") + ".";
      body = "<p>" + line + "</p>";
    } else if (typeof v === "object") {
      body = "<p>" + escapeHtml(JSON.stringify(v)) + "</p>";
    } else {
      var one = String(v).trim();
      if (!one) return "";
      var end = one.endsWith(".") ? "" : ".";
      body = "<p>" + escapeHtml(one) + end + "</p>";
    }
    return introHtml + body;
  }

  function tpl(pattern, map) {
    if (!pattern) return "";
    var s = String(pattern);
    Object.keys(map).forEach(function (k) {
      var v = map[k] == null ? "" : String(map[k]);
      s = s.split("[[" + k + "]]").join(v);
    });
    return s;
  }

  function getShippingI18n(root) {
    try {
      var sid = root.getAttribute("data-section-id");
      var el = sid && document.getElementById("HelpDrawerShippingI18n-" + sid);
      if (!el) return {};
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return {};
    }
  }

  function formatThemeMoneyCents(cents) {
    if (theme && theme.Currency && typeof theme.Currency.formatThemeMoney === "function") {
      return theme.Currency.formatThemeMoney(cents);
    }
    var format =
      theme &&
      theme.settings &&
      (theme.settings.currencyCodeEnabled
        ? theme.settings.moneyWithCurrencyFormat
        : theme.settings.moneyFormat);
    if (theme && theme.Currency && theme.Currency.formatMoney && format) {
      return theme.Currency.formatMoney(Number(cents) || 0, format);
    }
    return String((Number(cents) || 0) / 100);
  }

  function tierMinLabel(tier) {
    if (!tier) return "—";
    if (tier.minC != null && isFinite(Number(tier.minC))) {
      return formatThemeMoneyCents(tier.minC);
    }
    return (tier.minFormatted || "").trim() || "—";
  }

  function tierMaxLabel(tier) {
    if (!tier) return "";
    if (tier.maxC == null || tier.maxC === "" || !isFinite(Number(tier.maxC))) {
      return "";
    }
    return formatThemeMoneyCents(tier.maxC);
  }

  function formatOrderRange(i18n, minF, maxF) {
    var min = (minF || "").trim() || "—";
    var max = (maxF || "").trim();
    if (!max) {
      return tpl(i18n.above_order || "Above [[min]] (purchase):", { min: min });
    }
    return tpl(
      i18n.between || "Between [[min]] and [[max]] (purchase):",
      { min: min, max: max },
    );
  }

  function boldFeeHtml(fee) {
    var f = (fee || "").trim() || "—";
    return '<strong class="font-semibold">' + escapeHtml(f) + "</strong>";
  }

  function formatFeeHtml(i18n, feeFormatted, feeIsZero, feeC) {
    var freeText =
      i18n.fee_free_label != null ? String(i18n.fee_free_label).trim() : "";
    var isFree =
      feeIsZero === true ||
      feeIsZero === "true" ||
      (feeC != null && feeC !== "" && isFinite(Number(feeC)) && Number(feeC) === 0);
    if (freeText !== "" && isFree) {
      return (
        '<strong class="font-semibold">' + escapeHtml(freeText) + "</strong>"
      );
    }
    if (feeC != null && feeC !== "" && isFinite(Number(feeC))) {
      return boldFeeHtml(formatThemeMoneyCents(feeC));
    }
    var f = (feeFormatted || "").trim() || "—";
    return boldFeeHtml(f);
  }

  function tierLinePrefixAndBoldFee(i18n, minF, maxF, tier) {
    var min = tierMinLabel(tier);
    var max = tierMaxLabel(tier);
    var prefix;
    if (!max) {
      prefix = tpl(i18n.tier_above_prefix || "Above [[min]] (purchase): ", {
        min: min,
      });
    } else {
      prefix = tpl(
        i18n.tier_between_prefix || "Between [[min]] and [[max]] (purchase): ",
        {
          min: min,
          max: max || "—",
        },
      );
    }
    return (
      "<li>" +
      escapeHtml(prefix) +
      formatFeeHtml(i18n, tier.feeFormatted, tier.feeIsZero, tier.feeC) +
      "</li>"
    );
  }

  function pairKey(t) {
    if (t.dmin == null || t.dmax == null) return null;
    var a = Number(t.dmin);
    var b = Number(t.dmax);
    if (!isFinite(a) || !isFinite(b)) return null;
    return String(a) + "|" + String(b);
  }

  function delayTotals(t, proc) {
    var p = Number(proc) || 0;
    var pk = pairKey(t);
    if (pk == null) return null;
    var lo = Number(t.dmin) + p;
    var hi = Number(t.dmax) + p;
    if (!isFinite(lo) || !isFinite(hi)) return null;
    return { lo: lo, hi: hi };
  }

  function isWeekdayDate(d) {
    var day = d.getDay();
    return day !== 0 && day !== 6;
  }

  function pad2(n) {
    return (n < 10 ? "0" : "") + n;
  }

  function dateKeyLocal(d) {
    return (
      d.getFullYear() +
      "-" +
      pad2(d.getMonth() + 1) +
      "-" +
      pad2(d.getDate())
    );
  }

  /** Normalise une entrée JSON (chaîne ISO, ou objet date Shopify) en clé locale YYYY-MM-DD. */
  function holidayIsoKeyFromEntry(entry) {
    if (entry == null) return "";
    if (typeof entry === "object") {
      if (typeof entry.date === "string") return holidayIsoKeyFromEntry(entry.date);
      if (typeof entry.value === "string") return holidayIsoKeyFromEntry(entry.value);
    }
    var s = String(entry).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    var m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  }

  /** ISO dates (YYYY-MM-DD) from the selected zone’s metaobject JSON (`holidays`). */
  function holidaySetFromRow(row) {
    var set = Object.create(null);
    if (!row || !Array.isArray(row.holidays)) return set;
    row.holidays.forEach(function (entry) {
      var k = holidayIsoKeyFromEntry(entry);
      if (k) set[k] = true;
    });
    return set;
  }

  function isHolidayDate(d, holidaySet) {
    return !!holidaySet[dateKeyLocal(d)];
  }

  function isBusinessDate(d, holidaySet) {
    return isWeekdayDate(d) && !isHolidayDate(d, holidaySet);
  }

  function addCalendarDays(d, numCalendarDays) {
    var out = new Date(d.getTime());
    out.setHours(12, 0, 0, 0);
    var k = Math.max(0, Math.round(Number(numCalendarDays) || 0));
    for (var i = 0; i < k; i++) {
      out.setDate(out.getDate() + 1);
    }
    return out;
  }

  /** n jours ouvrés (lun–ven, hors fériés) après la date start (premier jour ouvré férié exclu). */
  function addWorkingDaysFromBusinessDate(start, numWorkingDays, holidaySet) {
    var d = new Date(start.getTime());
    d.setHours(12, 0, 0, 0);
    while (!isBusinessDate(d, holidaySet)) {
      d.setDate(d.getDate() + 1);
    }
    var target = Math.max(0, Math.round(Number(numWorkingDays) || 0));
    var counted = 0;
    while (counted < target) {
      d.setDate(d.getDate() + 1);
      if (isBusinessDate(d, holidaySet)) counted++;
    }
    return d;
  }

  /**
   * Calendrier : délai de traitement ; puis jours ouvrés d’acheminement.
   * +1 jour ouvré seulement si l’heure locale est ≥ 12h (sinon pas d’extra).
   */
  function estimateDeliveryEnd(procCalendar, transitWorking, holidaySet) {
    var now = new Date();
    var extraWorking = now.getHours() >= 12 ? 1 : 0;
    var d = new Date(now.getTime());
    d.setHours(12, 0, 0, 0);
    d = addCalendarDays(d, procCalendar);
    var tw = Math.max(0, Math.round(Number(transitWorking) || 0));
    return addWorkingDaysFromBusinessDate(d, tw + extraWorking, holidaySet);
  }

  function formatEstDeliveryParen(i18n, proc, tier, row) {
    if (!tier) return "";
    var dmin = Number(tier.dmin);
    var dmax = Number(tier.dmax);
    if (!isFinite(dmin) || !isFinite(dmax)) return "";
    var wd = i18n.weekdays_abbr;
    var mo = i18n.months_abbr;
    if (!Array.isArray(wd) || !Array.isArray(mo) || wd.length < 7 || mo.length < 12) {
      return "";
    }
    var holidaySet = holidaySetFromRow(row);
    var p = Number(proc) || 0;
    var dLo = estimateDeliveryEnd(p, dmin, holidaySet);
    var dHi = estimateDeliveryEnd(p, dmax, holidaySet);
    if (dHi.getTime() < dLo.getTime()) {
      var tmp = dLo;
      dLo = dHi;
      dHi = tmp;
    }
    var w0 = wd[dLo.getDay()];
    var w1 = wd[dHi.getDay()];
    if (w0 == null || w1 == null) return "";
    w0 = String(w0);
    w1 = String(w1);
    var day0 = dLo.getDate();
    var day1 = dHi.getDate();
    var m0 = dLo.getMonth();
    var m1 = dHi.getMonth();
    var y0 = dLo.getFullYear();
    var y1 = dHi.getFullYear();
    var inner;
    if (m0 === m1 && y0 === y1) {
      var monthStr = mo[m0];
      if (monthStr == null) return "";
      inner =
        escapeHtml(w0) +
        " " +
        day0 +
        " - " +
        escapeHtml(w1) +
        " " +
        day1 +
        " " +
        escapeHtml(String(monthStr));
    } else {
      var ms0 = mo[m0];
      var ms1 = mo[m1];
      if (ms0 == null || ms1 == null) return "";
      inner =
        escapeHtml(w0) +
        " " +
        day0 +
        " " +
        escapeHtml(String(ms0)) +
        " - " +
        escapeHtml(w1) +
        " " +
        day1 +
        " " +
        escapeHtml(String(ms1));
    }
    return " (" + inner + ")";
  }

  function tierBracketMin(t) {
    if (t == null) return Number.POSITIVE_INFINITY;
    if (t.minSort != null && t.minSort !== "") {
      var n = Number(t.minSort);
      if (isFinite(n)) return n;
    }
    return Number.POSITIVE_INFINITY;
  }

  function tierBracketMax(t) {
    if (t == null) return Number.POSITIVE_INFINITY;
    if (t.maxSort != null && t.maxSort !== "") {
      var n = Number(t.maxSort);
      if (isFinite(n)) return n;
    }
    return Number.POSITIVE_INFINITY;
  }

  function sortShippingTiersByBracket(list) {
    return list.sort(function (a, b) {
      var ma = tierBracketMin(a);
      var mb = tierBracketMin(b);
      if (ma !== mb) return ma - mb;
      return tierBracketMax(a) - tierBracketMax(b);
    });
  }

  function formatDelayLineHtml(i18n, dt, proc, tier, row) {
    if (!dt) return "";
    var suffix = i18n.working_days_suffix || "";
    var core = tpl(i18n.delay_line || "[[low]] - [[high]] [[suffix]]", {
      low: dt.lo,
      high: dt.hi,
      suffix: suffix,
    });
    var prefix = i18n.delay_prefix;
    if (prefix == null) prefix = "";
    return (
      escapeHtml(String(prefix)) +
      escapeHtml(core) +
      formatEstDeliveryParen(i18n, proc, tier, row)
    );
  }

  function formatOptionBody(tiers, i18n, proc, row) {
    if (!Array.isArray(tiers) || !tiers.length) return "";
    tiers = sortShippingTiersByBracket(tiers.slice());
    var n = tiers.length;
    if (n === 1) {
      var t1 = tiers[0];
      return (
        '<ul role="list"><li>' +
        formatFeeHtml(i18n, t1.feeFormatted, t1.feeIsZero, t1.feeC) +
        "</li></ul>"
      );
    }

    var allComplete =
      tiers.length > 0 &&
      tiers.every(function (t) {
        return pairKey(t) != null;
      });
    var keys = {};
    tiers.forEach(function (t) {
      var pk = pairKey(t);
      if (pk != null) keys[pk] = true;
    });
    var uniqueCount = Object.keys(keys).length;

    if (uniqueCount >= 2) {
      var partsB = [];
      tiers.forEach(function (t) {
        var range = formatOrderRange(i18n, tierMinLabel(t), tierMaxLabel(t));
        var dt = delayTotals(t, proc);
        var delayLi = "";
        if (dt) {
          delayLi =
            "<li>" + formatDelayLineHtml(i18n, dt, proc, t, row) + "</li>";
        }
        var amountPrefix = i18n.amount_prefix;
        if (amountPrefix == null) amountPrefix = "";
        partsB.push(
          "<li>" +
            escapeHtml(range) +
            '<ul role="list" class="help-shipping-tier-nested">' +
            "<li>" +
            escapeHtml(String(amountPrefix)) +
            formatFeeHtml(i18n, t.feeFormatted, t.feeIsZero, t.feeC) +
            "</li>" +
            delayLi +
            "</ul></li>",
        );
      });
      return (
        '<ul role="list" class="help-shipping-tiers-multi">' +
        partsB.join("") +
        "</ul>"
      );
    }

    if (uniqueCount === 1 && allComplete) {
      var t0 = tiers[0];
      for (var i = 0; i < tiers.length; i++) {
        if (pairKey(tiers[i])) {
          t0 = tiers[i];
          break;
        }
      }
      var dt0 = delayTotals(t0, proc);
      var partsC = [];
      if (dt0) {
        partsC.push(
          "<li>" + formatDelayLineHtml(i18n, dt0, proc, t0, row) + "</li>",
        );
      }
      tiers.forEach(function (t) {
        partsC.push(
          tierLinePrefixAndBoldFee(i18n, t.minFormatted, t.maxFormatted, t),
        );
      });
      return '<ul role="list">' + partsC.join("") + "</ul>";
    }

    var partsD = [];
    tiers.forEach(function (t) {
      partsD.push(
        tierLinePrefixAndBoldFee(i18n, t.minFormatted, t.maxFormatted, t),
      );
    });
    return '<ul role="list">' + partsD.join("") + "</ul>";
  }

  function formatShippingOptionsHtml(row, root) {
    var opts = row.shippingOptions;
    if (!Array.isArray(opts) || !opts.length) return "";
    var i18n = getShippingI18n(root);
    var proc = row.processingDays != null ? row.processingDays : 0;
    var blocks = [];
    opts.forEach(function (opt) {
      var body = formatOptionBody(opt.tiers || [], i18n, proc, row);
      if (!body) return;
      blocks.push(
        '<div class="help-shipping-option">' +
          '<p class="help-shipping-option__title not-rte h5 font-medium leading-tight">' +
          escapeHtml(String(opt.name || "")) +
          "</p>" +
          body +
          "</div>",
      );
    });
    if (!blocks.length) return "";
    var wrapClass =
      "help-shipping-options flex flex-col" +
      (opts.length > 1 ? " gap-4 md:gap-5" : "");
    return '<div class="' + wrapClass + '">' + blocks.join("") + "</div>";
  }

  function shippingMountHtml(row, root) {
    var optHtml = formatShippingOptionsHtml(row, root);
    return optHtml || "";
  }

  function clearHelpDrawerDynamicMounts(root) {
    root
      .querySelectorAll("[data-help-returns-mount], [data-help-returns-inline]")
      .forEach(function (mount) {
        mount.innerHTML = "";
      });
    root
      .querySelectorAll("[data-help-shipping-subzones-mount]")
      .forEach(function (mount) {
        mount.innerHTML = "";
      });
  }

  function returnsMetaIsTrue(val) {
    return val === true || val === "true";
  }

  /** Template retours (vrai/faux) associé au bloc Retours qui contient `el`. */
  function helpReturnsTemplateFor(el, sectionId, useTrue) {
    var li = el.closest('[data-help-content-purpose="returns"]');
    if (!li || !li.id || li.id.indexOf("shopify-block-") !== 0) return null;
    var bid = li.id.replace("shopify-block-", "");
    var tid =
      "HelpReturns" + (useTrue ? "True" : "False") + "-" + sectionId + "-" + bid;
    return document.getElementById(tid);
  }

  /** Contenu retours « déballé » (sans le wrapper .rte ni le <p> unique) pour le jeton inline [return_message]. */
  function helpReturnsInlineHtml(tmpl) {
    if (!tmpl || !tmpl.content) return "";
    var wrap = document.createElement("div");
    wrap.appendChild(tmpl.content.cloneNode(true));
    var rte = wrap.querySelector(".rte") || wrap;
    if (
      rte.children.length === 1 &&
      rte.firstElementChild &&
      rte.firstElementChild.tagName === "P"
    ) {
      return rte.firstElementChild.innerHTML;
    }
    return rte.innerHTML;
  }

  /**
   * Convertit tout jeton littéral [return_message] (saisi dans le 1er/2e texte d'un bloc Retours)
   * en cible inline data-help-returns-inline. Filet de sécurité côté client : fonctionne même quand
   * le remplacement Liquid n'a pas eu lieu (ex. injection brute de la valeur par l'éditeur de thème).
   * Quand un jeton est promu, le bloc séparé data-help-returns-mount du même item est retiré (anti-doublon).
   */
  function promoteHelpReturnsTokens(root) {
    root
      .querySelectorAll('[data-help-content-purpose="returns"]')
      .forEach(function (li) {
        var replaced = false;
        li.querySelectorAll(".rte").forEach(function (rte) {
          if (rte.innerHTML.indexOf("[return_message]") !== -1) {
            rte.innerHTML = rte.innerHTML
              .split("[return_message]")
              .join('<span data-help-returns-inline aria-live="polite"></span>');
            replaced = true;
          }
        });
        if (replaced) {
          li.querySelectorAll("[data-help-returns-mount]").forEach(function (m) {
            m.remove();
          });
        }
      });
  }

  function updateReturnsMounts(root, row) {
    var sectionId = root.getAttribute("data-section-id");
    if (!sectionId) return;
    promoteHelpReturnsTokens(root);
    if (!row) {
      clearHelpDrawerDynamicMounts(root);
      return;
    }
    var useTrue = returnsMetaIsTrue(row.returns);
    // Blocs séparés (data-help-returns-mount) : contenu .rte complet.
    root
      .querySelectorAll("[data-help-returns-mount]")
      .forEach(function (mount) {
        var tmpl = helpReturnsTemplateFor(mount, sectionId, useTrue);
        mount.innerHTML = "";
        if (tmpl && tmpl.content) {
          mount.appendChild(tmpl.content.cloneNode(true));
        }
      });
    // Jetons inline [return_message] (data-help-returns-inline) : contenu déballé, dans le flux du texte.
    root
      .querySelectorAll("[data-help-returns-inline]")
      .forEach(function (span) {
        var tmpl = helpReturnsTemplateFor(span, sectionId, useTrue);
        span.innerHTML = helpReturnsInlineHtml(tmpl);
      });
  }

  function updateShippingSubzonesMounts(root, row) {
    if (!row) {
      root
        .querySelectorAll("[data-help-shipping-subzones-mount]")
        .forEach(function (mount) {
          mount.innerHTML = "";
        });
      return;
    }
    var html = shippingMountHtml(row, root);
    root
      .querySelectorAll("[data-help-shipping-subzones-mount]")
      .forEach(function (mount) {
        var li = mount.closest('[data-help-content-purpose="shipping"]');
        if (!li) return;
        mount.innerHTML = html;
      });
  }

  function onShippingChange(ev) {
    var t = ev.target;
    if (!t || !t.matches || !t.matches("[data-help-shipping-select]")) return;
    var root = document.getElementById("HelpDrawer");
    if (!root) return;
    var detailId = t.getAttribute("data-shipping-detail");
    var dataId = t.getAttribute("data-shipping-data");
    var detail = detailId ? document.getElementById(detailId) : null;
    var dataEl = dataId ? document.getElementById(dataId) : null;
    if (!detail || !dataEl) return;
    var map;
    try {
      map = JSON.parse(dataEl.textContent.trim());
    } catch (e) {
      return;
    }
    var id = t.value;
    if (!id) {
      detail.hidden = true;
      detail.innerHTML = "";
      clearHelpDrawerDynamicMounts(root);
      return;
    }
    var row = map[id];
    if (!row && id != null) {
      row = map[String(id)];
    }
    if (!row) {
      detail.hidden = true;
      detail.innerHTML = "";
      clearHelpDrawerDynamicMounts(root);
      return;
    }
    detail.hidden = false;
    detail.innerHTML = formatSubzonesHtml(row.subzones, getShippingI18n(root));
    updateReturnsMounts(root, row);
    updateShippingSubzonesMounts(root, row);
  }

  function init() {
    var root = document.getElementById("HelpDrawer");
    if (!root) return;
    root.addEventListener("change", onShippingChange);
    // Promeut les jetons [return_message] dès le chargement (cible vide tant qu'aucune zone n'est choisie).
    promoteHelpReturnsTokens(root);
    var anySelected = false;
    root
      .querySelectorAll("[data-help-shipping-select]")
      .forEach(function (sel) {
        if (sel.value) {
          anySelected = true;
          onShippingChange({ target: sel });
        }
      });
    if (!anySelected) {
      clearHelpDrawerDynamicMounts(root);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  // Ré-initialise quand la section du tiroir est rechargée dans l'éditeur de thème.
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector("#HelpDrawer")) {
      init();
    }
  });

  // [STOYIK] API partagée : réutilisée par le module « SHIPPING RATES » (page.shipping)
  // pour reproduire à l'identique le calcul des délais, le formatage des prix et des sous-zones.
  window.StoyikHelpShipping = {
    escapeHtml: escapeHtml,
    tpl: tpl,
    formatFeeHtml: formatFeeHtml,
    formatThemeMoneyCents: formatThemeMoneyCents,
    tierMinLabel: tierMinLabel,
    tierMaxLabel: tierMaxLabel,
    delayTotals: delayTotals,
    formatDelayLineHtml: formatDelayLineHtml,
    formatEstDeliveryParen: formatEstDeliveryParen,
    sortShippingTiersByBracket: sortShippingTiersByBracket,
    formatSubzonesHtml: formatSubzonesHtml,
    i18nFromScriptId: function (id) {
      try {
        var el = id && document.getElementById(id);
        if (!el) return {};
        return JSON.parse(el.textContent.trim());
      } catch (e) {
        return {};
      }
    },
  };
})();


/* ====================================================================================================
[CUSTOM] FAQ RETURN ZONE — bloc « Frais de retour » de la section FAQ (page.return)
Réutilise le sélecteur de zone + les données JSON du Help Drawer (help-drawer-shipping-zone-select)
et l'API StoyikHelpShipping. À chaque changement de pays : affiche les sous-zones desservies puis
bascule la balise colorée (retour gratuit / à la charge du client) selon `return_covered` de la zone.
==================================================================================================== */
(function () {
  function api() {
    return window.StoyikHelpShipping || null;
  }

  function returnsIsTrue(v) {
    return v === true || v === "true";
  }

  function update(container) {
    var a = api();
    if (!a) return;
    var select = container.querySelector("[data-help-shipping-select]");
    if (!select) return;
    var detail = document.getElementById(
      select.getAttribute("data-shipping-detail"),
    );
    var dataEl = document.getElementById(
      select.getAttribute("data-shipping-data"),
    );
    var badge = container.querySelector("[data-return-badge]");
    var i18n = a.i18nFromScriptId(
      "FaqReturnZoneI18n-" + container.getAttribute("data-section-id"),
    );

    var map = {};
    if (dataEl) {
      try {
        map = JSON.parse(dataEl.textContent.trim());
      } catch (e) {
        map = {};
      }
    }
    var id = select.value;
    var row = id ? map[id] || map[String(id)] : null;

    // Sous-zones desservies (facultatif) — même rendu que le tiroir d'aide.
    if (detail) {
      var subHtml = row ? a.formatSubzonesHtml(row.subzones, i18n) : "";
      detail.innerHTML = subHtml;
      detail.hidden = !subHtml;
    }

    // Balise colorée retour gratuit / à charge client.
    // Comme le tiroir d'aide : `return_covered` vrai → gratuit, sinon (faux ou absent) → à charge.
    if (badge) {
      if (row) {
        var useTrue = returnsIsTrue(row.returns);
        var tmpl = container.querySelector(
          useTrue ? "[data-return-free]" : "[data-return-paid]",
        );
        badge.innerHTML = tmpl ? tmpl.innerHTML : "";
        badge.classList.toggle("faq-return-zone__badge--free", useTrue);
        badge.classList.toggle("faq-return-zone__badge--paid", !useTrue);
        badge.hidden = badge.innerHTML.trim() === "";
      } else {
        badge.hidden = true;
        badge.innerHTML = "";
        badge.classList.remove(
          "faq-return-zone__badge--free",
          "faq-return-zone__badge--paid",
        );
      }
    }
  }

  function init() {
    document
      .querySelectorAll("[data-faq-return-zone]")
      .forEach(function (container) {
        var select = container.querySelector("[data-help-shipping-select]");
        if (!select || select.__faqReturnBound) return;
        select.__faqReturnBound = true;
        select.addEventListener("change", function () {
          update(container);
        });
        update(container); // état initial (zone présélectionnée selon le pays visiteur)
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  // Ré-initialise quand la section FAQ est rechargée dans l'éditeur de thème.
  document.addEventListener("shopify:section:load", function (e) {
    if (
      e.target &&
      e.target.querySelector &&
      e.target.querySelector("[data-faq-return-zone]")
    ) {
      init();
    }
  });
})();


/* ====================================================================================================
[CUSTOM] SHIPPING RATES — tableau des tarifs/délais d'expédition
Réutilise le sélecteur de zone + les données JSON du Help Drawer (help-drawer-shipping-zone-select)
et l'API StoyikHelpShipping pour afficher, par zone choisie, un tableau :
méthode d'expédition | délai (avec estimation réelle) | montant de commande | prix.
Utilisé par : section page.shipping, modal « i » du cart drawer, bloc Expédition (mode tableau) du Help Drawer.
==================================================================================================== */

(function () {
  var API = window.StoyikHelpShipping;
  if (!API) return;

  function esc(s) {
    return API.escapeHtml(String(s == null ? "" : s));
  }

  function amountCellHtml(tier) {
    var min =
      API.tierMinLabel != null
        ? API.tierMinLabel(tier)
        : (tier.minFormatted || "").trim() || "—";
    var max =
      API.tierMaxLabel != null
        ? API.tierMaxLabel(tier)
        : (tier.maxFormatted || "").trim();
    if (max) return esc(min || "—") + " - " + esc(max);
    return esc(min || "—") + " +";
  }

  function delayCellHtml(i18n, proc, tier, row) {
    var dt = API.delayTotals(tier, proc);
    if (!dt) return "";
    // Ligne 1 : plage de jours ouvrés. Ligne 2 (dessous, plus petite, en gras) :
    // estimation réelle de la date de livraison, entre parenthèses.
    var core = API.tpl(i18n.delay_line || "[[low]] - [[high]] [[suffix]]", {
      low: dt.lo,
      high: dt.hi,
      suffix: i18n.working_days_suffix || "",
    });
    var html = esc(core);
    var dateText = API.formatEstDeliveryParen(i18n, proc, tier, row).trim();
    if (dateText) {
      html += '<span class="shipping-rates__delay-date">' + dateText + "</span>";
    }
    return html;
  }

  function delayKey(proc, tier) {
    var dt = API.delayTotals(tier, proc);
    return dt ? dt.lo + "|" + dt.hi : null;
  }

  function buildTable(row, i18n, labels) {
    var opts = row.shippingOptions;
    if (!Array.isArray(opts) || !opts.length) return "";
    var proc = row.processingDays != null ? row.processingDays : 0;

    var body = "";
    opts.forEach(function (opt) {
      var tiers = API.sortShippingTiersByBracket((opt.tiers || []).slice());
      if (!tiers.length) return;

      var delays = tiers.map(function (t) {
        return delayKey(proc, t);
      });
      var sharedDelay = delays.every(function (d) {
        return d != null && d === delays[0];
      });

      tiers.forEach(function (t, i) {
        body += "<tr>";
        if (i === 0) {
          body +=
            '<td class="shipping-rates__cell--method" rowspan="' +
            tiers.length +
            '">' +
            esc(opt.name) +
            "</td>";
        }
        if (sharedDelay) {
          if (i === 0) {
            body +=
              '<td class="shipping-rates__cell--delay" rowspan="' +
              tiers.length +
              '">' +
              delayCellHtml(i18n, proc, tiers[0], row) +
              "</td>";
          }
        } else {
          body +=
            '<td class="shipping-rates__cell--delay">' +
            delayCellHtml(i18n, proc, t, row) +
            "</td>";
        }
        body +=
          '<td class="shipping-rates__cell--amount">' +
          amountCellHtml(t) +
          "</td>";
        body +=
          '<td class="shipping-rates__cell--price">' +
          API.formatFeeHtml(i18n, t.feeFormatted, t.feeIsZero, t.feeC) +
          "</td>";
        body += "</tr>";
      });
    });

    if (!body) return "";

    return (
      "<table>" +
      "<thead><tr>" +
      "<th>" +
      esc(labels.method) +
      "</th>" +
      "<th>" +
      esc(labels.delay) +
      "</th>" +
      "<th>" +
      esc(labels.amount) +
      "</th>" +
      "<th>" +
      esc(labels.price) +
      "</th>" +
      "</tr></thead><tbody>" +
      body +
      "</tbody></table>"
    );
  }

  function resolveShippingSelect(root) {
    var select = root.querySelector("[data-help-shipping-select]");
    if (select) return select;
    // Help Drawer : le sélecteur est un bloc séparé ; le tableau vit dans le bloc Expédition.
    var host = root.closest("#HelpDrawer");
    if (!host) host = document.getElementById("HelpDrawer");
    return host ? host.querySelector("[data-help-shipping-select]") : null;
  }

  function initSection(root) {
    if (!root || root.getAttribute("data-shipping-rates-ready") === "1") return;
    var select = resolveShippingSelect(root);
    var tableMount = root.querySelector("[data-shipping-rates-table]");
    if (!select || !tableMount) return;

    var dataEl = document.getElementById(
      select.getAttribute("data-shipping-data"),
    );
    var detail = document.getElementById(
      select.getAttribute("data-shipping-detail"),
    );
    var sectionId = root.getAttribute("data-section-id");
    var i18n = API.i18nFromScriptId("ShippingRatesI18n-" + sectionId);
    // Pas de préfixe « Délai : » dans le tableau : l'en-tête de colonne le porte déjà.
    i18n.delay_prefix = "";
    var labels = API.i18nFromScriptId("ShippingRatesLabels-" + sectionId);

    var map = {};
    try {
      map = JSON.parse(dataEl.textContent.trim());
    } catch (e) {
      return;
    }

    function render() {
      var id = select.value;
      var row = id ? map[id] || map[String(id)] : null;
      if (detail) {
        var html = row ? API.formatSubzonesHtml(row.subzones, i18n) : "";
        detail.innerHTML = html;
        detail.hidden = !html;
      }
      tableMount.innerHTML = row ? buildTable(row, i18n, labels) : "";
    }

    select.addEventListener("change", render);
    root.setAttribute("data-shipping-rates-ready", "1");
    render();
  }

  function init() {
    document
      .querySelectorAll("[data-shipping-rates]")
      .forEach(function (root) {
        initSection(root);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", function (e) {
    if (
      e.target &&
      e.target.querySelector &&
      e.target.querySelector("[data-shipping-rates]")
    ) {
      init();
    }
  });
  // Le cart drawer remplace son innerHTML à chaque cart:updated : ré-init du tableau.
  document.addEventListener("cart:updated", init);
})();


/* ====================================================================================================
[CUSTOM] CART DRAWER — estimation livraison metafield (remplace le calculateur natif)
Sélecteur de zone (help-drawer-shipping-zone-select) + liste « Nom : tarif » pour le
sous-total panier courant. La plage min–max sous le sous-total du footer suit le pays
boutique (localization), pas le pays choisi dans le modal, et le palier du sous-total
(comparaison en centimes via minC/maxC).
==================================================================================================== */

(function () {
  var API = window.StoyikHelpShipping;
  if (!API) return;

  var lastCartCents = null;

  function esc(s) {
    return API.escapeHtml(String(s == null ? "" : s));
  }

  function toCents(v) {
    var n = Math.round(Number(v));
    return isFinite(n) && n >= 0 ? n : null;
  }

  /** Sous-total affiché (cart.total_price en centimes) — source prioritaire. */
  function subtotalCentsFromCart(cart) {
    if (!cart || typeof cart !== "object") return null;
    var fromTotal = toCents(cart.total_price);
    if (fromTotal != null) return fromTotal;
    var fromItems = toCents(cart.items_subtotal_price);
    if (fromItems != null) return fromItems;
    return null;
  }

  /** Sources sync Liquid / barre (mises à jour au re-render section). */
  function subtotalCentsFromDom() {
    var drawer = document.querySelector("cart-drawer");
    if (drawer) {
      var fromDrawer = toCents(drawer.getAttribute("data-cart-subtotal-cents"));
      if (fromDrawer != null) return fromDrawer;
    }
    var roots = document.querySelectorAll("[data-cart-shipping-metafield]");
    for (var i = 0; i < roots.length; i++) {
      var fromRoot = toCents(roots[i].getAttribute("data-cart-subtotal-cents"));
      if (fromRoot != null) return fromRoot;
    }
    var bar = document.querySelector(
      "cart-drawer [data-cart-initial-subtotal], [data-cart-initial-subtotal]",
    );
    if (bar) {
      var fromBar = toCents(bar.getAttribute("data-cart-initial-subtotal"));
      if (fromBar != null) return fromBar;
    }
    return null;
  }

  function cartJsUrl() {
    if (theme && theme.routes && theme.routes.cart_url) {
      return String(theme.routes.cart_url).replace(/\/$/, "") + ".js";
    }
    var root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    return String(root).replace(/\/?$/, "/") + "cart.js";
  }

  function resolveCartCents(cart) {
    var fromCart = subtotalCentsFromCart(cart);
    if (fromCart != null) return fromCart;
    var fromDom = subtotalCentsFromDom();
    if (fromDom != null) return fromDom;
    if (lastCartCents != null && isFinite(lastCartCents)) return lastCartCents;
    return 0;
  }

  /** Borne palier en centimes (minC/maxC, repli minSort/maxSort en unités majeures). */
  function tierMinCents(t) {
    if (t == null) return 0;
    if (t.minC != null && t.minC !== "" && isFinite(Number(t.minC))) {
      return Math.max(0, Math.round(Number(t.minC)));
    }
    if (t.minSort != null && t.minSort !== "" && isFinite(Number(t.minSort))) {
      return Math.max(0, Math.round(Number(t.minSort) * 100));
    }
    return 0;
  }

  function tierMaxCents(t) {
    if (t == null) return Number.POSITIVE_INFINITY;
    if (t.maxC === null || t.maxC === undefined || t.maxC === "") {
      // maxC absent → plafond ouvert, sauf si maxSort numérique
      if (t.maxSort != null && t.maxSort !== "" && isFinite(Number(t.maxSort))) {
        return Math.round(Number(t.maxSort) * 100);
      }
      return Number.POSITIVE_INFINITY;
    }
    if (isFinite(Number(t.maxC))) {
      return Math.round(Number(t.maxC));
    }
    return Number.POSITIVE_INFINITY;
  }

  function pickTierForCart(tiers, cartCents) {
    var sorted = API.sortShippingTiersByBracket((tiers || []).slice());
    if (!sorted.length) return null;
    var cc = Math.max(0, Number(cartCents) || 0);
    for (var i = 0; i < sorted.length; i++) {
      var t = sorted[i];
      if (cc >= tierMinCents(t) && cc <= tierMaxCents(t)) return t;
    }
    return sorted[sorted.length - 1];
  }

  function feesForRow(row, cartCents, i18n) {
    var opts = row && row.shippingOptions;
    if (!Array.isArray(opts) || !opts.length) return [];
    var out = [];
    opts.forEach(function (opt) {
      var tier = pickTierForCart(opt.tiers || [], cartCents);
      if (!tier) return;
      var feeC =
        tier.feeC != null && isFinite(Number(tier.feeC))
          ? Math.round(Number(tier.feeC))
          : parseFeeToCents(tier.feeFormatted);
      var isZero =
        !!(tier.feeIsZero === true || tier.feeIsZero === "true") ||
        (feeC != null && isFinite(feeC) && feeC === 0);
      if (isZero) feeC = 0;
      var feeHtml = API.formatFeeHtml(
        i18n,
        tier.feeFormatted,
        isZero,
        feeC,
      );
      // Footer (plage) : toujours le montant formaté (0€).
      // Modal (liste) : feeHtml peut afficher « GRATUIT ».
      var feeFormatted = formatMoneyFallback(
        isZero
          ? 0
          : feeC != null && isFinite(feeC)
            ? feeC
            : parseFeeToCents(tier.feeFormatted) || 0,
      );
      out.push({
        name: String(opt.name || "").trim(),
        feeHtml: feeHtml,
        feeFormatted: feeFormatted,
        feeIsZero: isZero,
        feeC: isZero ? 0 : feeC,
      });
    });
    return out;
  }

  function parseFeeToCents(feeFormatted) {
    if (feeFormatted == null || feeFormatted === "") return null;
    var s = String(feeFormatted)
      .replace(/[^\d.,-]/g, "")
      .replace(/\s/g, "")
      .replace(",", ".");
    if ((s.match(/\./g) || []).length > 1) {
      s = s.replace(/\./g, function (m, i, str) {
        return i === str.lastIndexOf(".") ? "." : "";
      });
    }
    var n = parseFloat(s);
    if (!isFinite(n)) return null;
    return Math.round(n * 100);
  }

  function buildListHtml(fees) {
    if (!fees.length) return "";
    var items = fees
      .map(function (f) {
        var name = f.name || "—";
        return (
          "<li class=\"cart-shipping-metafield__item\">" +
          '<span class="cart-shipping-metafield__name">' +
          esc(name) +
          "</span>" +
          " : " +
          f.feeHtml +
          "</li>"
        );
      })
      .join("");
    return (
      '<ul class="cart-shipping-metafield__options list-disc m-0" role="list">' +
      items +
      "</ul>"
    );
  }

  function formatRangeText(fees, i18n) {
    if (!fees.length) return "";
    // Plage du footer panier : toujours des montants (0€), jamais « GRATUIT ».
    var cents = fees
      .map(function (f) {
        return f.feeIsZero ? 0 : f.feeC;
      })
      .filter(function (c) {
        return c != null && isFinite(c);
      });
    if (!cents.length) {
      var labels = fees
        .map(function (f) {
          if (f.feeIsZero || Number(f.feeC) === 0) return formatMoneyFallback(0);
          if (f.feeC != null && isFinite(f.feeC)) return formatMoneyFallback(f.feeC);
          return f.feeFormatted || "—";
        })
        .filter(Boolean);
      var uniq = labels.filter(function (v, i, a) {
        return a.indexOf(v) === i;
      });
      if (uniq.length === 1) return uniq[0];
      if (uniq.length > 1) return uniq[0] + " - " + uniq[uniq.length - 1];
      return "";
    }
    var min = Math.min.apply(null, cents);
    var max = Math.max.apply(null, cents);
    if (min === max) return formatMoneyFallback(min);
    return formatMoneyFallback(min) + " - " + formatMoneyFallback(max);
  }

  function formatMoneyFallback(cents) {
    if (theme?.Currency?.formatThemeMoney) {
      return theme.Currency.formatThemeMoney(cents);
    }
    var format = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;
    if (theme?.Currency?.formatMoney && format) {
      return theme.Currency.formatMoney(cents, format);
    }
    var cartDrawer = document.querySelector("cart-drawer");
    var currencyCode = (cartDrawer && cartDrawer.dataset.cartCurrency) || "EUR";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currencyCode,
    }).format((Number(cents) || 0) / 100);
  }

  /**
   * @param {function(number)} cb
   * @param {{ cart?: object, force?: boolean }} [opts]
   */
  function getCartCents(cb, opts) {
    opts = opts || {};
    if (opts.cart && typeof opts.cart === "object") {
      lastCartCents = resolveCartCents(opts.cart);
      cb(lastCartCents);
      return;
    }
    if (
      !opts.force &&
      lastCartCents != null &&
      isFinite(lastCartCents) &&
      lastCartCents > 0
    ) {
      cb(lastCartCents);
      return;
    }
    // Sync immédiat depuis le DOM Liquid (évite le flash « panier 0 »).
    var domCents = subtotalCentsFromDom();
    if (domCents != null) {
      lastCartCents = domCents;
      cb(domCents);
    }
    fetch(cartJsUrl())
      .then(function (r) {
        if (!r.ok) throw new Error("cart.js " + r.status);
        return r.json();
      })
      .then(function (cart) {
        var cents = resolveCartCents(cart);
        if (domCents == null || cents !== domCents) {
          lastCartCents = cents;
          cb(cents);
        } else {
          lastCartCents = cents;
        }
      })
      .catch(function () {
        if (domCents != null) return;
        var fallback =
          lastCartCents != null && isFinite(lastCartCents) ? lastCartCents : 0;
        lastCartCents = fallback;
        cb(fallback);
      });
  }

  function rowFromMap(map, id) {
    if (!id) return null;
    return map[id] || map[String(id)] || null;
  }

  function storeZoneIdFromSelect(select) {
    if (!select) return "";
    var fromData = (select.getAttribute("data-store-zone-id") || "").trim();
    if (fromData) return fromData;
    var selectedOpt = select.querySelector("option[selected]");
    return selectedOpt ? selectedOpt.value : "";
  }

  function initRoot(root, cartCents) {
    if (!root) return;
    var select = root.querySelector("[data-help-shipping-select]");
    var listMount = root.querySelector("[data-cart-shipping-metafield-list]");
    if (!select || !listMount) return;

    var dataEl = document.getElementById(
      select.getAttribute("data-shipping-data"),
    );
    var detail = document.getElementById(
      select.getAttribute("data-shipping-detail"),
    );
    var sectionId = root.getAttribute("data-section-id");
    var i18n = API.i18nFromScriptId("CartShippingMetafieldI18n-" + sectionId);
    var storeZoneId = storeZoneIdFromSelect(select);

    var map = {};
    try {
      map = JSON.parse((dataEl && dataEl.textContent) || "");
    } catch (e) {
      return;
    }

    function cartCentsNow() {
      if (cartCents != null && isFinite(cartCents)) return Number(cartCents);
      if (lastCartCents != null && isFinite(lastCartCents)) return lastCartCents;
      var fromRoot = toCents(root.getAttribute("data-cart-subtotal-cents"));
      if (fromRoot != null) return fromRoot;
      return subtotalCentsFromDom() || 0;
    }

    function renderList() {
      var row = rowFromMap(map, select.value);
      if (detail) {
        var subHtml = row ? API.formatSubzonesHtml(row.subzones, i18n) : "";
        detail.innerHTML = subHtml;
        detail.hidden = !subHtml;
      }
      var fees = row ? feesForRow(row, cartCentsNow(), i18n) : [];
      listMount.innerHTML = buildListHtml(fees);
    }

    function renderStoreRange() {
      var row = rowFromMap(map, storeZoneId);
      var fees = row ? feesForRow(row, cartCentsNow(), i18n) : [];
      updateRangeFromFees(fees, i18n);
    }

    function render() {
      renderList();
      renderStoreRange();
    }

    if (root.getAttribute("data-cart-shipping-metafield-ready") !== "1") {
      select.addEventListener("change", function () {
        getCartCents(
          function (cents) {
            lastCartCents = cents;
            cartCents = cents;
            renderList();
          },
          { force: true },
        );
      });
      root.setAttribute("data-cart-shipping-metafield-ready", "1");
    }
    render();
  }

  function updateRangeFromFees(fees, i18n) {
    var cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) return;
    var el = cartDrawer.querySelector("[data-shipping-range]");
    if (!el) return;
    el.textContent = formatRangeText(fees, i18n || {});
  }

  function applyCentsToRoots(cents) {
    lastCartCents = cents;
    var roots = document.querySelectorAll("[data-cart-shipping-metafield]");
    if (!roots.length) {
      var el = document.querySelector("[data-shipping-range]");
      if (el) el.textContent = "";
      return;
    }
    roots.forEach(function (root) {
      if (!document.body.contains(root)) return;
      initRoot(root, cents);
    });
  }

  /** @param {object|number} [cartOrCents] — cart API, ou centimes déjà calculés */
  function updateMetafieldShippingRange(cartOrCents) {
    if (typeof cartOrCents === "number" && isFinite(cartOrCents)) {
      applyCentsToRoots(Math.max(0, Math.round(cartOrCents)));
      return;
    }
    if (cartOrCents && typeof cartOrCents === "object") {
      applyCentsToRoots(resolveCartCents(cartOrCents));
      return;
    }
    getCartCents(applyCentsToRoots, { force: true });
  }

  window.__stoyikUpdateMetafieldShippingRange = updateMetafieldShippingRange;

  function init(cart) {
    getCartCents(
      applyCentsToRoots,
      cart ? { cart: cart } : { force: true },
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init();
    });
  } else {
    init();
  }
  document.addEventListener("shopify:section:load", function () {
    init();
  });
  document.addEventListener("cart:updated", function (e) {
    var cart = e && e.detail && e.detail.cart ? e.detail.cart : null;
    if (cart) {
      applyCentsToRoots(resolveCartCents(cart));
      return;
    }
    lastCartCents = null;
    init();
  });
})();


/* ====================================================================================================
SOCIAL PROOF — recoupe le texte complémentaire entre la 1re ligne (avec badge) et la ligne overflow
(même logique que STOYIK_Shopify_Impact_Theme_Dev/assets/custom.js)
==================================================================================================== */

document.addEventListener("DOMContentLoaded", function () {
  const selector = ".social-proof";

  /**
   * Texte complet pour le split : toujours depuis le conteneur (Liquid), jamais depuis
   * textContent du span (tronqué après le 1er layout), sinon le texte ne remonte plus au resize.
   */
  function getFullText(block, span) {
    const fromRoot = block.getAttribute("data-complementary-plain");
    const fromSpan =
      span.getAttribute("data-social-proof-full-text") ||
      span.getAttribute("data-full-text");
    const raw =
      (fromRoot != null && String(fromRoot).length ? fromRoot : null) ||
      (fromSpan != null && String(fromSpan).length ? fromSpan : null) ||
      "";
    const fullText = String(raw).trim();

    span.setAttribute("data-full-text", fullText);
    span.setAttribute("data-social-proof-full-text", fullText);
    span.dataset.fullText = fullText;

    return fullText;
  }

  function countWords(text) {
    return String(text).trim().split(/\s+/).filter(Boolean).length;
  }

  function pruneEmptyNodes(node) {
    if (!node) return;

    Array.from(node.childNodes).forEach(function (child) {
      pruneEmptyNodes(child);
    });

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    if (node.childNodes.length === 0) {
      node.remove();
      return;
    }

    const hasMeaningfulText = (node.textContent || "").trim().length > 0;
    if (!hasMeaningfulText) {
      node.remove();
    }
  }

  function removeWordsFromEnd(root, wordsToRemove) {
    if (!root || wordsToRemove <= 0) {
      return;
    }

    const textNodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    let removed = 0;
    for (let i = textNodes.length - 1; i >= 0 && removed < wordsToRemove; i--) {
      const node = textNodes[i];
      const text = node.textContent || "";
      const wordMatches = text.match(/\S+/g) || [];

      if (wordMatches.length <= wordsToRemove - removed) {
        removed += wordMatches.length;
        node.textContent = "";
        continue;
      }

      let newText = text;
      let count = wordsToRemove - removed;
      while (count > 0) {
        newText = newText.replace(/\s*\S+\s*$/, "");
        count--;
        removed++;
      }
      node.textContent = newText;
      break;
    }

    pruneEmptyNodes(root);
  }

  function removeWordsFromStart(root, wordsToRemove) {
    if (!root || wordsToRemove <= 0) {
      return;
    }

    const textNodes = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    let removed = 0;
    for (let i = 0; i < textNodes.length && removed < wordsToRemove; i++) {
      const node = textNodes[i];
      const text = node.textContent || "";
      const wordMatches = text.match(/\S+/g) || [];

      if (wordMatches.length <= wordsToRemove - removed) {
        removed += wordMatches.length;
        node.textContent = "";
        continue;
      }

      let newText = text;
      let count = wordsToRemove - removed;
      while (count > 0) {
        newText = newText.replace(/^\s*\S+\s*/, "");
        count--;
        removed++;
      }
      node.textContent = newText;
      break;
    }

    pruneEmptyNodes(root);
  }

  function splitHtmlByWordCount(html, primaryWordCount) {
    const sourceHtml = String(html || "").trim();
    if (!sourceHtml) {
      return { primary: "", overflow: "" };
    }

    const container = document.createElement("div");
    container.innerHTML = sourceHtml;
    const totalWords = countWords(container.textContent);

    if (primaryWordCount >= totalWords) {
      return { primary: sourceHtml, overflow: "" };
    }

    if (primaryWordCount <= 0) {
      return { primary: "", overflow: sourceHtml };
    }

    const primaryRoot = container.cloneNode(true);
    const overflowRoot = container.cloneNode(true);

    removeWordsFromEnd(primaryRoot, totalWords - primaryWordCount);
    removeWordsFromStart(overflowRoot, primaryWordCount);

    return {
      primary: primaryRoot.innerHTML.trim(),
      overflow: overflowRoot.innerHTML.trim(),
    };
  }

  function applySplitContent(span, overflowSpan, primaryContent, overflowContent, useHtml) {
    if (useHtml) {
      span.innerHTML = primaryContent;
      overflowSpan.innerHTML = overflowContent;
      return;
    }

    span.textContent = primaryContent;
    overflowSpan.textContent = overflowContent;
  }

  /**
   * Overlay gallery : largeur fit-content → clientWidth du texte souvent trop petit
   * (tout part en overflow). On verrouille temporairement à max-width (px calculés)
   * pour mesurer comme dans les banners (max-w-full).
   */
  function lockOverlayMeasureWidth(overlay) {
    if (!overlay) return null;
    const cs = getComputedStyle(overlay);
    if (cs.display === "none") return null;
    const maxW = parseFloat(cs.maxWidth);
    if (!Number.isFinite(maxW) || maxW <= 0) return null;
    const prev = overlay.style.width;
    overlay.style.width = Math.floor(maxW) + "px";
    return prev;
  }

  function adjustBlock(block) {
    const overlay = block.closest("[data-social-proof-gallery-overlay]");
    if (overlay && getComputedStyle(overlay).display === "none") {
      return;
    }

    const prevOverlayWidth = lockOverlayMeasureWidth(overlay);

    try {
      adjustBlockMeasure(block);
    } finally {
      if (overlay && prevOverlayWidth !== null) {
        overlay.style.width = prevOverlayWidth;
      }
    }
  }

  function adjustBlockMeasure(block) {
    const wrapper = block.querySelector(".social-proof__supporting");
    const complementarySpan = wrapper
      ? wrapper.querySelector(".social-proof__supporting-text")
      : null;
    const excessSpan = block.querySelector(".social-proof__overflow-text");

    if (!wrapper || !complementarySpan || !excessSpan) {
      return;
    }

    const useHtmlSplit = block.classList.contains("social-proof--verify-avatar");

    if (useHtmlSplit && !complementarySpan.dataset.initialHtml) {
      const initialHtml = complementarySpan.innerHTML.trim();
      if (initialHtml) {
        complementarySpan.dataset.initialHtml = initialHtml;
      }
    }

    const fullText = getFullText(block, complementarySpan);

    if (!fullText) {
      applySplitContent(complementarySpan, excessSpan, "", "", useHtmlSplit);
      return;
    }

    const words = fullText.split(/\s+/).filter(Boolean);
    const primaryWords = words.slice();
    const overflowWords = [];

    /* D’abord tout sur la ligne 1 + ligne 2 vide : la largeur utile du flex se met à jour. */
    applySplitContent(
      complementarySpan,
      excessSpan,
      useHtmlSplit ? complementarySpan.dataset.initialHtml || fullText : primaryWords.join(" "),
      "",
      useHtmlSplit
    );

    void wrapper.offsetWidth;
    void complementarySpan.offsetWidth;

    const fits = function () {
      const room = complementarySpan.clientWidth;
      if (room <= 0) {
        return false;
      }

      return complementarySpan.scrollWidth <= room + 0.5;
    };

    if (complementarySpan.clientWidth <= 0) {
      applySplitContent(
        complementarySpan,
        excessSpan,
        useHtmlSplit ? complementarySpan.dataset.initialHtml || fullText : fullText,
        "",
        useHtmlSplit
      );
      return;
    }

    if (!fits()) {
      while (primaryWords.length > 0) {
        overflowWords.unshift(primaryWords.pop());
        applySplitContent(
          complementarySpan,
          excessSpan,
          useHtmlSplit
            ? splitHtmlByWordCount(
                complementarySpan.dataset.initialHtml,
                primaryWords.length
              ).primary
            : primaryWords.join(" "),
          "",
          useHtmlSplit
        );

        if (fits()) {
          break;
        }
      }
    }

    if (!fits()) {
      overflowWords.unshift(...primaryWords);
      primaryWords.length = 0;
    }

    if (useHtmlSplit) {
      const split = splitHtmlByWordCount(
        complementarySpan.dataset.initialHtml,
        primaryWords.length
      );
      applySplitContent(
        complementarySpan,
        excessSpan,
        split.primary,
        split.overflow,
        true
      );
      return;
    }

    applySplitContent(
      complementarySpan,
      excessSpan,
      primaryWords.join(" "),
      overflowWords.join(" "),
      false
    );
  }

  function adjustAllBlocks() {
    document.querySelectorAll(selector).forEach(adjustBlock);
  }

  const scheduleAdjust = (() => {
    let frame = null;
    return function () {
      if (frame) {
        cancelAnimationFrame(frame);
      }

      /* Double rAF : après resize / ResizeObserver le parent flex n’a pas toujours sa largeur finale au 1er frame. */
      frame = requestAnimationFrame(() => {
        adjustAllBlocks();
        frame = requestAnimationFrame(() => {
          frame = null;
          adjustAllBlocks();
        });
      });
    };
  })();

  function bindResizeObservers() {
    document.querySelectorAll(selector).forEach(function (el) {
      if (el.dataset.socialProofResizeBound === "true") {
        return;
      }

      el.dataset.socialProofResizeBound = "true";

      if (typeof ResizeObserver === "function") {
        const ro = new ResizeObserver(function () {
          scheduleAdjust();
        });
        ro.observe(el);
        const primaryLine = el.querySelector(".social-proof__primary-line");
        if (primaryLine) {
          ro.observe(primaryLine);
        }
        const textCol = el.querySelector(".social-proof__text");
        if (textCol) {
          ro.observe(textCol);
        }
      }
    });
  }

  adjustAllBlocks();
  bindResizeObservers();

  window.addEventListener("resize", scheduleAdjust);
  document.addEventListener("social-proof:reflow", scheduleAdjust);
  document.addEventListener("shopify:section:load", function () {
    scheduleAdjust();
    bindResizeObservers();
  });
});

/* ====================================================================================================
[CUSTOM] SOCIAL PROOF — overlay sur la 1re image du carrousel
Hors du slider (ne suit pas le scroll des images).
Desktop : visible sur la 1re slide ; text-2xs ; priorité 1re colonne, fallback centre
si trop large. Mobile : 1re apparition sans délai jusqu'au 1er swipe ; ensuite auto-hide.
==================================================================================================== */
(() => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const SELECTOR = "[data-social-proof-gallery-overlay]";
  const DESKTOP_MQ = window.matchMedia("(min-width: 1024px)");
  const instances = new WeakMap();
  // Persistance par id de media-gallery : l'overlay est détruit quand
  // ProductInfo remplace le innerHTML de ProductGallery au variant:change.
  const persistedByGallery = new Map();

  function getGalleryKey(el) {
    return el.closest("media-gallery")?.id || null;
  }

  function persistOverlayState(el, state) {
    const key = getGalleryKey(el);
    if (!key || !state) return;
    persistedByGallery.set(key, {
      visible: el.classList.contains("is-visible"),
      hasNavigatedAway: Boolean(state.hasNavigatedAway),
    });
  }

  function clearHideTimer(state) {
    if (state.hideTimer != null) {
      clearTimeout(state.hideTimer);
      state.hideTimer = null;
    }
  }

  function updateTimelineClearance(el, visible) {
    const wrap = el.closest(".relative");
    if (!wrap) return;

    if (DESKTOP_MQ.matches || !visible) {
      wrap.style.setProperty("--sp-overlay-timeline-lift", "0px");
      wrap.classList.remove("has-sp-overlay-visible");
      return;
    }

    // Hauteur du pill → padding-end du dégradé (ancré en bas, taille ↑)
    requestAnimationFrame(() => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      const lift = Math.max(0, height + 8);
      wrap.style.setProperty("--sp-overlay-timeline-lift", lift + "px");
      wrap.classList.add("has-sp-overlay-visible");
    });
  }

  /**
   * Desktop colonnes : priorité 1re image ; si le contenu dépasse la 1re colonne,
   * bascule au centre des 2 images (.is-spanning-columns).
   */
  function layoutDesktopOverlay(el) {
    const twoColHost = el.closest(".product--columns, .product--scroll");
    if (!DESKTOP_MQ.matches || !twoColHost) {
      el.classList.remove("is-spanning-columns");
      return;
    }

    const parent = el.offsetParent || el.parentElement;
    if (!parent) return;

    el.classList.remove("is-spanning-columns");
    void el.offsetWidth;

    const parentW = parent.clientWidth;
    const firstColBudget = parentW * 0.5 - 24;
    const contentW = el.scrollWidth;

    if (contentW > firstColBudget) {
      el.classList.add("is-spanning-columns");
    }
  }

  function setVisible(el, visible) {
    el.classList.toggle("is-visible", Boolean(visible));
    el.setAttribute("aria-hidden", visible ? "false" : "true");
    updateTimelineClearance(el, visible);
    const state = instances.get(el);
    if (state) persistOverlayState(el, state);
    if (visible) {
      layoutDesktopOverlay(el);
      document.dispatchEvent(new CustomEvent("social-proof:reflow"));
      // Recalcule le lift après la recoupe texte (hauteur peut changer)
      requestAnimationFrame(() => updateTimelineClearance(el, true));
    }
  }

  function isOverlayActiveForViewport(el) {
    const desktop = el.dataset.overlayDesktop === "true";
    const mobile = el.dataset.overlayMobile === "true";
    return DESKTOP_MQ.matches ? desktop : mobile;
  }

  function getHideDelayMs(el) {
    const seconds = parseFloat(el.dataset.hideDelay || "4");
    if (!Number.isFinite(seconds) || seconds <= 0) return 4000;
    return Math.round(seconds * 1000);
  }

  // [CUSTOM] Overlay dans le modal produit (quick-view)
  function isInModal(el) {
    return !!el.closest(".quick-view, .x-modal");
  }

  // Fiche produit : logique de délai sur mobile uniquement.
  // Modal : logique de délai sur mobile ET desktop (auto-hide partout).
  function usesDelayLogic(el) {
    return !DESKTOP_MQ.matches || isInModal(el);
  }

  function isFirstSlideActive(slider) {
    if (!slider) return true;
    if (typeof slider.currentPage === "number" && slider.currentPage > 0) {
      return slider.currentPage === 1;
    }
    return true;
  }

  function startMobileHideTimer(el, state) {
    clearHideTimer(state);
    if (!usesDelayLogic(el)) return;
    if (!isOverlayActiveForViewport(el)) return;

    const delay = getHideDelayMs(el);
    state.hideTimer = setTimeout(() => {
      state.hideTimer = null;
      if (!usesDelayLogic(el)) return;
      if (!isOverlayActiveForViewport(el)) return;
      setVisible(el, false);
    }, delay);
  }

  function armVariantDrivenWindow(state) {
    state.variantDrivenSlideChange = true;
    if (state.variantDrivenTimeout != null) {
      clearTimeout(state.variantDrivenTimeout);
    }
    state.variantDrivenTimeout = setTimeout(() => {
      state.variantDrivenSlideChange = false;
      state.variantDrivenTimeout = null;
    }, 1000);
  }

  /**
   * Restaure l'état d'apparition/disparition après remplacement du ProductGallery.
   * Le changement de variante ne doit rien modifier à la visibilité du social proof.
   */
  function applyPersistedState(el, state, saved) {
    clearHideTimer(state);
    state.hasNavigatedAway = Boolean(saved.hasNavigatedAway);
    state.isFirstSlide = isFirstSlideActive(state.slider);
    armVariantDrivenWindow(state);

    if (!isOverlayActiveForViewport(el)) {
      setVisible(el, false);
      return;
    }

    setVisible(el, Boolean(saved.visible));

    if (saved.visible && saved.hasNavigatedAway && usesDelayLogic(el)) {
      startMobileHideTimer(el, state);
    }
  }

  /**
   * @param {boolean} isFirstSlide
   * @param {"enter-first"|"leave-first"|"sync"} reason
   *
   * Mobile — 1re apparition : visible sans délai tant qu'on ne change pas d'image.
   * Le délai démarre au premier leave. Ensuite : enter = show + délai (comme avant).
   */
  function syncVisibility(el, state, isFirstSlide, reason) {
    const wasFirst = state.isFirstSlide;
    state.isFirstSlide = isFirstSlide;

    if (!isOverlayActiveForViewport(el)) {
      clearHideTimer(state);
      setVisible(el, false);
      return;
    }

    // Desktop hors modal : visible uniquement sur la 1re image, pas d'auto-hide
    if (DESKTOP_MQ.matches && !isInModal(el)) {
      clearHideTimer(state);
      setVisible(el, isFirstSlide && el.dataset.overlayDesktop === "true");
      return;
    }

    // Mobile OU modal (desktop inclus) : 1re image visible sans délai ;
    // le délai démarre au 1er changement d'image, puis au retour sur la 1re.
    if (reason === "leave-first") {
      state.hasNavigatedAway = true;
      persistOverlayState(el, state);
      // 1re sortie : active le délai (au chargement il n'avait pas démarré)
      if (!state.hideTimer && el.classList.contains("is-visible")) {
        startMobileHideTimer(el, state);
      }
      return;
    }

    if (
      isFirstSlide &&
      (reason === "enter-first" || (!wasFirst && reason === "sync"))
    ) {
      setVisible(el, true);
      // Délai seulement après qu'on ait déjà quitté la 1re image une fois
      if (state.hasNavigatedAway) {
        startMobileHideTimer(el, state);
      } else {
        clearHideTimer(state);
      }
      return;
    }

    if (isFirstSlide && reason === "sync") {
      return;
    }
  }

  function initOverlay(el, options = {}) {
    if (instances.has(el)) return;

    const gallery = el.closest("media-gallery");
    const slider =
      gallery?.querySelector("slider-element") ||
      el.closest(".relative")?.querySelector("slider-element");

    const state = {
      hideTimer: null,
      isFirstSlide: false,
      hasNavigatedAway: false,
      // [CUSTOM] setActiveMedia (variant:change) ne doit pas toucher la visibilité
      variantDrivenSlideChange: false,
      variantDrivenTimeout: null,
      slider,
      gallery,
      onSlideChange: null,
      onMqChange: null,
      onResize: null,
    };

    state.onSlideChange = (event) => {
      const page = event?.detail?.currentPage;
      const isFirst =
        typeof page === "number" ? page === 1 : isFirstSlideActive(slider);
      const wasFirst = state.isFirstSlide;

      // Scroll suite à variant:change : maj page courante uniquement, pas la visibilité
      if (state.variantDrivenSlideChange) {
        state.isFirstSlide = isFirst;
        return;
      }

      if (isFirst && !wasFirst) {
        syncVisibility(el, state, true, "enter-first");
      } else if (!isFirst && wasFirst) {
        syncVisibility(el, state, false, "leave-first");
      } else {
        state.isFirstSlide = isFirst;
      }
    };

    state.onMqChange = () => {
      const isFirst = isFirstSlideActive(slider);
      if (usesDelayLogic(el) && isFirst) {
        syncVisibility(el, state, true, "enter-first");
      } else {
        syncVisibility(el, state, isFirst, "sync");
      }
      layoutDesktopOverlay(el);
    };

    state.onResize = () => {
      if (el.classList.contains("is-visible")) {
        layoutDesktopOverlay(el);
        updateTimelineClearance(el, true);
      }
    };

    if (slider) {
      slider.addEventListener("slider:change", state.onSlideChange);
    }

    if (typeof DESKTOP_MQ.addEventListener === "function") {
      DESKTOP_MQ.addEventListener("change", state.onMqChange);
    } else if (typeof DESKTOP_MQ.addListener === "function") {
      DESKTOP_MQ.addListener(state.onMqChange);
    }

    window.addEventListener("resize", state.onResize);

    instances.set(el, state);

    const galleryKey = gallery?.id || null;
    const saved =
      options.restorePersisted && galleryKey
        ? persistedByGallery.get(galleryKey)
        : null;

    if (saved) {
      applyPersistedState(el, state, saved);
      return;
    }

    const initiallyFirst = isFirstSlideActive(slider);
    // Déjà hors 1re image au load → comportement « normal » ensuite
    if (!initiallyFirst) {
      state.hasNavigatedAway = true;
    }
    syncVisibility(
      el,
      state,
      initiallyFirst,
      initiallyFirst ? "enter-first" : "sync",
    );
    persistOverlayState(el, state);
  }

  function initAll(root, options = {}) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTOR).forEach((el) => initOverlay(el, options));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });

  // [CUSTOM] Modal produit (quick-view) : le contenu est injecté via AJAX, sans
  // shopify:section:load. On (ré)initialise les overlays à l'ouverture du modal.
  function initOpenModal() {
    const modal = document.querySelector(".quick-view[open], .x-modal[open]");
    if (modal) requestAnimationFrame(() => initAll(modal));
  }
  document.addEventListener("quickview:loaded", initOpenModal);
  document.addEventListener("quickview:open", initOpenModal);
  document.addEventListener("modal:afterShow", (event) => {
    const modal = event.target;
    if (modal && typeof modal.querySelectorAll === "function") {
      requestAnimationFrame(() => initAll(modal));
    }
  });

  /**
   * [CUSTOM] Au variant:change, ProductInfo remplace le innerHTML de ProductGallery
   * (donc détruit l'overlay). On ré-init le nouvel overlay et on restaure
   * l'état d'apparition/disparition — le changement de variante ne doit rien
   * changer à la visibilité du social proof.
   */
  function reinstateOverlaysAfterVariantChange(form) {
    document.querySelectorAll("media-gallery").forEach((gallery) => {
      if (form) {
        const formId = gallery.getAttribute("form");
        const galleryForm = formId ? document.forms[formId] : null;
        if (galleryForm && galleryForm !== form) return;
      }
      if (
        !gallery.querySelector(SELECTOR) &&
        !persistedByGallery.has(gallery.id)
      ) {
        return;
      }
      initAll(gallery, { restorePersisted: true });
    });
  }

  document.addEventListener(
    "variant:change",
    (event) => {
      const form =
        event.target && event.target.tagName === "FORM"
          ? event.target
          : event.target?.closest?.("form");
      // Après le remplacement HTML + setActiveMedia (même tick) → frame suivante
      requestAnimationFrame(() => {
        reinstateOverlaysAfterVariantChange(form || null);
      });
    },
    true,
  );
})();

/* ====================================================================================================
BANNER BOX — PIN FIRST + DERNIÈRE LIGNE
(sections image/vidéo avec superposition de texte)

Les marges inline sont gérées en CSS pur (voir custom.css, section "MARGES STATIQUES SELON ALIGNEMENT").
Ce script gère uniquement :
  1. Réordonnancement "pin first" : quand 2 blocs sont sur la même ligne, déplace le bloc marqué
     .pin-first-on-same-line en tête de ligne.
  2. Classe banner-last-line : appliquée aux blocs de la dernière ligne pour le margin-block-start.
==================================================================================================== */
(() => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const PIN_SELECTOR = ".pin-first-on-same-line";
  const TOLERANCE = 4; // px de tolérance "même ligne"

  // ── Utilitaires pin-first ──────────────────────────────────────────────────────────────────────
  function ensureInit(el) {
    if (el._pinFirstInit) return;
    el._pinFirstInit = true;
    el._pinFirstParent = el.parentElement;
    el._pinFirstNextSibling = el.nextElementSibling;
  }

  function restoreOriginalPosition(el) {
    const parent = el._pinFirstParent;
    if (!parent || !parent.isConnected || el.parentElement !== parent) return;
    const ref = el._pinFirstNextSibling;
    if (ref && ref.parentElement === parent) {
      if (el.nextElementSibling !== ref) parent.insertBefore(el, ref);
    } else if (parent.lastElementChild !== el) {
      parent.appendChild(el);
    }
  }

  // ── Traitement d'une boîte ─────────────────────────────────────────────────────────────────────
  function updateBox(box) {
    var children = Array.from(box.children);
    if (!children.length) return;

    // ── 1. Retire les classes de détection
    for (var i = 0; i < children.length; i++) {
      children[i].classList.remove("banner-last-line");
    }

    // ── 2. Restaure la position DOM d'origine des boutons pin-first
    var pinEls = [];
    for (var i = 0; i < children.length; i++) {
      if (children[i].matches(PIN_SELECTOR) && children[i].isConnected) {
        ensureInit(children[i]);
        restoreOriginalPosition(children[i]);
        pinEls.push(children[i]);
      }
    }

    // ── 3. Reflow + mesure des positions (marges CSS actives)
    void box.offsetWidth;
    var liveChildren = Array.from(box.children);
    var rects = liveChildren.map(function (el) {
      return el.getBoundingClientRect();
    });

    // ── 4. Pin first : si le bouton a un voisin sur la même ligne, le déplace en tête de ligne
    var moved = false;
    for (var p = 0; p < pinEls.length; p++) {
      var el = pinEls[p];
      var idx = liveChildren.indexOf(el);
      if (idx < 0) continue;
      var myRect = rects[idx];
      if (!myRect || (myRect.width === 0 && myRect.height === 0)) continue;

      // Vérifie qu'il y a au moins un voisin sur la même ligne
      var hasSameLineNeighbor = false;
      for (var j = 0; j < liveChildren.length; j++) {
        if (j === idx) continue;
        var rj = rects[j];
        if (rj.width === 0 && rj.height === 0) continue;
        if (
          rj.bottom - TOLERANCE > myRect.top &&
          rj.top + TOLERANCE < myRect.bottom
        ) {
          hasSameLineNeighbor = true;
          break;
        }
      }
      if (!hasSameLineNeighbor) continue;

      // Cherche le frère précédent le plus éloigné sur la même ligne
      var target = null;
      var cur = el.previousElementSibling;
      while (cur) {
        var curIdx = liveChildren.indexOf(cur);
        if (curIdx >= 0) {
          var r = rects[curIdx];
          if (r.width > 0 || r.height > 0) {
            var sameLine =
              r.bottom - TOLERANCE > myRect.top &&
              r.top + TOLERANCE < myRect.bottom;
            if (sameLine) target = cur;
            else break;
          }
        }
        cur = cur.previousElementSibling;
      }
      if (target && target.parentElement === el._pinFirstParent) {
        el._pinFirstParent.insertBefore(el, target);
        moved = true;
      }
    }

    // ── 5. Si déplacement DOM → reflow + re-mesure
    if (moved) {
      void box.offsetWidth;
      liveChildren = Array.from(box.children);
      rects = liveChildren.map(function (el) {
        return el.getBoundingClientRect();
      });
    }

    // ── 6. Applique banner-last-line aux éléments de la dernière ligne
    var lastRect = null;
    for (var i = liveChildren.length - 1; i >= 0; i--) {
      var r = rects[i];
      if (r.width > 0 || r.height > 0) {
        lastRect = r;
        break;
      }
    }
    if (!lastRect) return;

    for (var i = 0; i < liveChildren.length; i++) {
      var ri = rects[i];
      if (ri.width === 0 && ri.height === 0) continue;
      if (
        ri.bottom - TOLERANCE > lastRect.top &&
        ri.top + TOLERANCE < lastRect.bottom
      ) {
        liveChildren[i].classList.add("banner-last-line");
      }
    }
  }

  // ── Planification (un seul RAF partagé) ───────────────────────────────────────────────────────
  var scheduled = false;
  function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      var boxes = document.querySelectorAll(".banner__box");
      for (var b = 0; b < boxes.length; b++) updateBox(boxes[b]);
    });
  }

  function init() {
    scheduleUpdate();
    window.addEventListener("resize", scheduleUpdate);
    document.addEventListener("shopify:section:load", scheduleUpdate);
    document.addEventListener("shopify:section:reorder", scheduleUpdate);
    document.addEventListener("shopify:block:select", scheduleUpdate);
    document.addEventListener("shopify:block:deselect", scheduleUpdate);
    if (
      document.fonts &&
      document.fonts.ready &&
      typeof document.fonts.ready.then === "function"
    ) {
      document.fonts.ready.then(scheduleUpdate).catch(function () {});
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ====================================================================================================
   DESCRIPTION COMPACTE — Voir plus / Voir moins
   ==================================================================================================== */

(function () {
  function applyTimelineGroupRules() {
    var groups = new Map();
    document
      .querySelectorAll(
        "[data-description-compact][data-timeline-compact='true'][data-description-apply-all='false']",
      )
      .forEach(function (el) {
        var root =
          el.closest(".shopify-section") ||
          el.closest("[id^='shopify-section-']") ||
          document.body;
        if (!groups.has(root)) groups.set(root, []);
        groups.get(root).push(el);
      });

    groups.forEach(function (wrappers) {
      var tallest = null;
      var tallestFullItemHeight = 0;

      wrappers.forEach(function (w) {
        if (typeof w._recalcCompact === "function") w._recalcCompact();
        var fullBody = Number(w._timelineFullHeight || 0);
        if (typeof w._timelineMeasureItemHeightForBody !== "function") return;
        var fullItemH = w._timelineMeasureItemHeightForBody(fullBody);
        if (fullItemH > tallestFullItemHeight) {
          tallestFullItemHeight = fullItemH;
          tallest = w;
        }
      });

      if (!tallest) return;

      var tallestNoDesc = tallest._timelineMeasureItemHeightForBody(0);
      var tallestCharBody = Number(tallest._timelineCharHeight || 0);
      var tallestCharItem = tallest._timelineMeasureItemHeightForBody(tallestCharBody);

      // Si la description n'augmente pas la hauteur du bloc le plus haut:
      // pas de "Voir plus" pour les autres blocs.
      if (tallestFullItemHeight <= tallestNoDesc + 1) {
        wrappers.forEach(function (w) {
          if (typeof w._applyTimelineExactBodyHeight === "function") {
            w._applyTimelineExactBodyHeight(Number(w._timelineFullHeight || 0));
          }
        });
        return;
      }

      var targetItemHeight = tallestCharItem;
      wrappers.forEach(function (w) {
        var fullBody = Number(w._timelineFullHeight || 0);
        if (w === tallest) {
          if (typeof w._applyTimelineExactBodyHeight === "function") {
            w._applyTimelineExactBodyHeight(tallestCharBody);
          }
          return;
        }

        if (
          typeof w._timelineMeasureItemHeightForBody !== "function" ||
          typeof w._timelineMaxBodyForTargetHeight !== "function"
        ) {
          return;
        }

        var fullItemH = w._timelineMeasureItemHeightForBody(fullBody);
        // Ne limite que si ce bloc atteint le niveau du plus haut.
        if (fullItemH <= targetItemHeight + 1) {
          if (typeof w._applyTimelineExactBodyHeight === "function") {
            w._applyTimelineExactBodyHeight(fullBody);
          }
          return;
        }

        var maxBody = w._timelineMaxBodyForTargetHeight(targetItemHeight);
        if (typeof w._applyTimelineExactBodyHeight === "function") {
          w._applyTimelineExactBodyHeight(maxBody);
        }
      });
    });
  }

  function isInInfiniteSliderTrack(wrapper) {
    return !!wrapper.closest(
      "testimonials-slider .testimonials-slider__track",
    );
  }

  // [CUSTOM] Ne pas mesurer tant que la carte n'a pas de largeur réelle
  // (cart drawer fermé = content-visibility/largeur 0 → hauteur figée à ~1 ligne).
  function canMeasureCompact(wrapper) {
    return wrapper.getBoundingClientRect().width >= 2;
  }

  function initCompactDescription(wrapper) {
    if (wrapper.dataset.descCompactInit) return;
    if (!canMeasureCompact(wrapper)) return;
    wrapper.dataset.descCompactInit = "true";

    var body = wrapper.querySelector(".product-description__body");
    var showMore = wrapper.querySelector(".product-description__show-more");
    var btnMore = wrapper.querySelector(".product-description__btn-more");
    if (!body || !showMore || !btnMore) return;
    function applyAllBlocks() {
      return wrapper.getAttribute("data-description-apply-all") === "true";
    }

    // Hauteur de troncature pré-calculée (mise en cache, jamais mesurée pendant une animation)
    var collapsedHeight = 0;

    // ── Mesure la line-height réelle du corps ──────────────────────────────────
    function getLineHeight() {
      var style = getComputedStyle(body);
      var lh = parseFloat(style.lineHeight);
      if (!isFinite(lh)) lh = parseFloat(style.fontSize) * 1.6;
      return lh;
    }

    // ── Synchronise --desc-lh : hauteur de l'overlay = 1 ligne exacte ─────────
    function syncLineHeightVar() {
      wrapper.style.setProperty("--desc-lh", getLineHeight() + "px");
    }

    // ── Injecte "Voir moins" inline dans le dernier élément de texte ──────────
    var btnLess = wrapper.querySelector(".product-description__btn-less");
    if (!btnLess) {
      btnLess = document.createElement("button");
      btnLess.className = "product-description__btn-less link";
      btnLess.type = "button";
      btnLess.textContent = "Voir moins";
      // Cible le dernier bloc de texte RÉEL (et non un <p></p> vide de fin, ou un
      // bloc qui ne porte pas de texte) pour que « Voir moins » s'affiche bien à la
      // suite du dernier mot, et jamais seul sur une nouvelle ligne. On descend dans
      // les listes pour viser le dernier <li> plutôt que le <ul>/<ol> lui-même.
      var textBlocks = body.querySelectorAll(
        "p, li, blockquote, h1, h2, h3, h4, h5, h6, figcaption, div",
      );
      var lastTextEl = body;
      for (var i = textBlocks.length - 1; i >= 0; i--) {
        if (textBlocks[i].textContent && textBlocks[i].textContent.trim()) {
          lastTextEl = textBlocks[i];
          break;
        }
      }
      lastTextEl.appendChild(btnLess);
    }

    // ── Ajuste margin-left de "Voir moins" : 0 en début de ligne, 0.6em sinon ─
    function adjustBtnLessMargin() {
      btnLess.style.marginLeft = "0";
      var btnLeft = btnLess.getBoundingClientRect().left;
      var bodyLeft = body.getBoundingClientRect().left;
      if (Math.round(btnLeft) > Math.round(bodyLeft) + 2) {
        btnLess.style.marginLeft = "0.6em";
      }
    }

    // ── Calcule et met en cache la hauteur de troncature via l'API Range ───────
    //    Mesure faite sur le corps réel (enroulement identique au rendu), et
    //    UNIQUEMENT à l'état replié (jamais pendant une animation d'ouverture/
    //    fermeture) : la géométrie des Range n'implique aucun reflow susceptible de
    //    casser la transition CSS max-height.
    function computeAndCacheCollapsedHeight() {
      syncLineHeightVar();

      // Breakpoint "téléphone" : 480 px max (standard phone vs tablet).
      // Le thème ne définit pas ce seuil nativement (son seul breakpoint est 767 px),
      // donc on crée ce breakpoint dédié au comportement mobile de la description.
      var isMobile = window.matchMedia("(max-width: 480px)").matches;
      var linesDesktop = parseInt(wrapper.dataset.descriptionLines, 10) || 3;
      var linesMobile = parseInt(wrapper.dataset.descriptionLinesMobile, 10) || 0;
      var targetLines = Math.max(
        1,
        isMobile && linesMobile > 0 ? linesMobile : linesDesktop,
      );

      // Mesure directement sur le CORPS RÉEL (et non sur un clone). Un clone à
      // largeur recalculée pouvait s'enrouler sur un nombre de lignes différent du
      // corps réel → hauteur de troncature faussée d'une ligne → « Voir plus » sur
      // une ligne fantôme, de façon dépendante de la largeur d'écran. La géométrie
      // des Range reste exacte même si le corps est clippé (overflow:hidden ne
      // déplace pas le texte, il ne fait que le masquer visuellement).
      //
      // On masque « Voir moins » AVANT toute mesure et on le laisse masqué : cette
      // fonction n'est appelée qu'à l'état replié, où « Voir moins » doit de toute
      // façon rester caché. Sinon, visible, il s'enroulerait sur une ligne
      // supplémentaire et gonflerait body.scrollHeight → « Voir plus » à tort.
      btnLess.style.display = "none";

      function createTextWalker() {
        return document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
          acceptNode: function (n) {
            return btnLess.contains(n)
              ? NodeFilter.FILTER_REJECT
              : NodeFilter.FILTER_ACCEPT;
          },
        });
      }

      var bodyTop = body.getBoundingClientRect().top;
      var lh = getLineHeight();

      // Bottom (relatif au corps) de la ligne contenant le caractère `index` du
      // nœud `n`. On SÉLECTIONNE le caractère (et non un caret réduit) : un caret
      // posé sur une césure douce renvoie un rect ambigu (fin de ligne L ou début
      // de ligne L+1 selon le navigateur). Mesurer la box du caractère lui-même
      // donne sans ambiguïté la ligne où il se trouve.
      function lineBottomOf(n, index) {
        try {
          var nodeLen = n.textContent.length;
          var s = Math.min(Math.max(index, 0), nodeLen);
          var e = Math.min(s + 1, nodeLen);
          if (e === s && s > 0) s -= 1;
          var r = document.createRange();
          r.setStart(n, s);
          r.setEnd(n, e);
          var rect = r.getBoundingClientRect();
          return rect.height > 0 ? rect.bottom - bodyTop : null;
        } catch (e) {
          return null;
        }
      }

      var fullHeight = body.scrollHeight;
      var height;

      // Bas de la N-ième ligne visible (point de troncature).
      // Seuil « Voir plus » : uniquement si le texte fait PLUS de N+1 lignes
      // (ex. N=3 → 4 lignes affichées en entier ; « Voir plus » dès 5 lignes).
      var truncBottom = null;
      var currentLine = 0;
      var prevBottom = null;
      var overflowThreshold = targetLines + 1;
      var walker = createTextWalker();
      var textNode;

      while ((textNode = walker.nextNode())) {
        var text = textNode.textContent;
        for (var ci = 0; ci < text.length; ci++) {
          var bottom = lineBottomOf(textNode, ci);
          if (bottom == null) continue;

          var isNewLine =
            prevBottom != null && bottom - prevBottom > lh * 0.5;
          if (prevBottom == null || isNewLine) {
            currentLine++;
          }

          if (currentLine <= targetLines) {
            truncBottom = bottom;
          }
          if (currentLine > overflowThreshold) {
            break;
          }
          prevBottom = bottom;
        }
        if (currentLine > overflowThreshold) break;
      }

      if (!truncBottom || currentLine <= overflowThreshold) {
        // Contenu sur N+1 lignes ou moins → rien à masquer (affiche tout).
        height = fullHeight;
      } else {
        // Au moins N+2 lignes → tronque à N et affiche « Voir plus ».
        height = truncBottom;
      }

      var charHeight = Math.max(height, getLineHeight());

      var contentCol = wrapper.closest(".timeline__item-content");
      var item = wrapper.closest(".timeline__item");
      var mediaEl =
        item &&
        item.querySelector(
          ":scope > .flex > picture.media, :scope > .flex > div.media",
        );
      wrapper._timelineNonBodyHeight = contentCol
        ? Math.max(0, contentCol.scrollHeight - body.scrollHeight)
        : 0;
      wrapper._timelineMediaHeight = mediaEl
        ? Math.max(0, mediaEl.getBoundingClientRect().height)
        : 0;
      wrapper._timelineCharHeight = charHeight;
      wrapper._timelineFullHeight = fullHeight;
      collapsedHeight = charHeight;
    }

    // ── État réduit ────────────────────────────────────────────────────────────
    function applyCollapsed() {
      btnMore.style.opacity = "";
      btnMore.style.transition = "";
      body.style.overflow = "";
      computeAndCacheCollapsedHeight(); // mesure sur le corps réel, masque « Voir moins »
      body.style.maxHeight = collapsedHeight + "px";
      var hasOverflow = body.scrollHeight > collapsedHeight + 4;
      wrapper.classList.toggle("desc-no-overflow", !hasOverflow);
      wrapper.classList.remove("is-expanded");
      btnMore.setAttribute("aria-expanded", "false");
      btnLess.style.display = "none";
    }

    // ── État étendu ────────────────────────────────────────────────────────────
    // Référence au listener transitionend de l'ouverture, pour pouvoir l'annuler
    // si l'utilisateur clique "Voir moins" avant la fin de l'animation d'ouverture.
    // Sans ça, onExpanded se déclencherait à la fin de l'animation de FERMETURE
    // et remettrait overflow:visible sur un bloc replié → texte caché réapparait.
    var pendingExpandListener = null;

    function cancelPendingExpand() {
      if (pendingExpandListener) {
        body.removeEventListener("transitionend", pendingExpandListener);
        pendingExpandListener = null;
      }
    }

    function applyExpanded() {
      cancelPendingExpand();
      btnMore.style.opacity = "";
      btnMore.style.transition = "";
      wrapper.classList.add("is-expanded");
      btnMore.setAttribute("aria-expanded", "true");
      btnLess.style.display = "";
      requestAnimationFrame(function () {
        // overflow:hidden est conservé pendant l'animation pour que le contenu
        // soit progressivement révélé (et non affiché instantanément).
        // overflow:visible est activé uniquement après la fin de la transition,
        // pour que toute la surface de btnLess soit cliquable.
        body.style.maxHeight = body.scrollHeight + "px";

        pendingExpandListener = function onExpanded(e) {
          if (e.propertyName !== "max-height") return;
          cancelPendingExpand();
          body.style.overflow = "visible";
          adjustBtnLessMargin();
        };
        body.addEventListener("transitionend", pendingExpandListener);
      });
    }

    // ── Init ───────────────────────────────────────────────────────────────────
    applyCollapsed();
    wrapper._recalcCompact = applyCollapsed;
    wrapper._applyTimelineExactBodyHeight = function (exactBodyHeight) {
      var full = wrapper._timelineFullHeight || body.scrollHeight;
      collapsedHeight = Math.max(0, Math.min(full, exactBodyHeight));
      body.style.maxHeight = collapsedHeight + "px";
      var hasOverflow = body.scrollHeight > collapsedHeight + 4;
      wrapper.classList.toggle("desc-no-overflow", !hasOverflow);
    };
    wrapper._timelineMeasureItemHeightForBody = function (bodyHeight) {
      var item = wrapper.closest(".timeline__item");
      if (!item) return 0;
      var prevTransition = body.style.transition;
      var prevMaxHeight = body.style.maxHeight;
      var prevOverflow = body.style.overflow;
      body.style.transition = "none";
      body.style.overflow = "hidden";
      body.style.maxHeight = Math.max(0, bodyHeight) + "px";
      var h = item.getBoundingClientRect().height;
      body.style.maxHeight = prevMaxHeight;
      body.style.overflow = prevOverflow;
      body.style.transition = prevTransition;
      return h;
    };
    wrapper._timelineMaxBodyForTargetHeight = function (targetHeight) {
      var full = Number(wrapper._timelineFullHeight || body.scrollHeight);
      var lo = 0;
      var hi = Math.max(0, full);
      for (var i = 0; i < 12; i++) {
        var mid = (lo + hi) / 2;
        var h = wrapper._timelineMeasureItemHeightForBody(mid);
        if (h <= targetHeight + 0.5) lo = mid;
        else hi = mid;
      }
      return lo;
    };

    btnMore.addEventListener("click", applyExpanded);

    btnLess.addEventListener("click", function () {
      // Annule le listener d'ouverture si l'animation n'était pas encore terminée
      cancelPendingExpand();

      // 1. Rétablit overflow:hidden avant l'animation (évite les débordements visuels)
      body.style.overflow = "";

      // 2. Cache btnMore instantanément (inline, sans transition)
      //    → le conteneur est encore display:none (is-expanded présent), rien n'est visible
      btnMore.style.transition = "none";
      btnMore.style.opacity = "0";

      // 3. Snapshot de la hauteur complète (point de départ de l'animation)
      body.style.maxHeight = body.scrollHeight + "px";
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          // 4. Déclenche l'animation de fermeture avec la hauteur MISE EN CACHE.
          //    Aucune mesure ici → aucun forced-reflow sur le corps → la transition
          //    CSS max-height (scrollHeight → collapsedHeight) se déclenche correctement.
          syncLineHeightVar();
          body.style.maxHeight = collapsedHeight + "px";

          // 5. Retire is-expanded → show-more passe de display:none à display:flex
          //    Le corps reprend sa hauteur animée. btnMore est opacity:0 (inline).
          var hasOverflow = body.scrollHeight > collapsedHeight + 4;
          wrapper.classList.toggle("desc-no-overflow", !hasOverflow);
          wrapper.classList.remove("is-expanded");
          btnMore.setAttribute("aria-expanded", "false");
          btnLess.style.display = "none";

          // 6. Force un reflow : le navigateur rend btnMore à opacity:0 (visible car
          //    son conteneur est maintenant display:flex)
          btnMore.getBoundingClientRect();

          // 7. Restaure la transition CSS du bouton
          btnMore.style.transition = "";

          // 8. RAF suivant : déclenche le fade-in opacity 0→1 en parallèle de
          //    l'animation de hauteur (départ quasi-simultané)
          requestAnimationFrame(function () {
            btnMore.style.opacity = "";
          });

          // 9. Scroll si la description est sortie de l'écran
          setTimeout(function () {
            var rect = wrapper.getBoundingClientRect();
            if (rect.top < 0) {
              wrapper.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
          }, 400);
        });
      });
    });

    // ── Recalcule à chaque redimensionnement ───────────────────────────────────
    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (wrapper.classList.contains("is-expanded")) {
          // Désactive la transition le temps du recalcul pour éviter une animation parasite
          body.style.transition = "none";
          body.style.maxHeight = body.scrollHeight + "px";
          body.getBoundingClientRect(); // commit
          body.style.transition = "";
          adjustBtnLessMargin();
        } else {
          computeAndCacheCollapsedHeight();
          body.style.transition = "none";
          body.style.maxHeight = collapsedHeight + "px";
          body.getBoundingClientRect(); // commit
          body.style.transition = "";
          var hasOverflow = body.scrollHeight > collapsedHeight + 4;
          wrapper.classList.toggle("desc-no-overflow", !hasOverflow);
          if (wrapper.getAttribute("data-timeline-compact") === "true") {
            clearTimeout(window.__stoyikTimelineCompactRulesT);
            window.__stoyikTimelineCompactRulesT = setTimeout(
              applyTimelineGroupRules,
              40,
            );
          }
        }
      }, 100);
    });
  }

  function refreshCompactDescriptions(root) {
    var scope = root || document;
    scope.querySelectorAll("[data-description-compact]").forEach(function (el) {
      // [CUSTOM] cloneNode copie data-desc-compact-init mais pas _recalcCompact :
      // il faut réinitialiser ces clones, sinon ils gardent un max-height faux (~1 ligne).
      if (el.dataset.descCompactInit && typeof el._recalcCompact !== "function") {
        delete el.dataset.descCompactInit;
        el.classList.remove("desc-no-overflow", "is-expanded");
        var staleBody = el.querySelector(".product-description__body");
        if (staleBody) {
          staleBody.style.maxHeight = "";
          staleBody.style.overflow = "";
        }
      }
      if (!el.dataset.descCompactInit) {
        initCompactDescription(el);
      } else if (
        typeof el._recalcCompact === "function" &&
        !el.classList.contains("is-expanded") &&
        canMeasureCompact(el)
      ) {
        el._recalcCompact();
      }
    });
    requestAnimationFrame(applyTimelineGroupRules);
  }

  function initAll() {
    document.querySelectorAll("[data-description-compact]").forEach(function (el) {
      // [CUSTOM] Slider infini : init après layout (custom_testimonials-slider.js).
      if (isInInfiniteSliderTrack(el)) return;
      initCompactDescription(el);
    });
    requestAnimationFrame(applyTimelineGroupRules);
  }

  window.__stoyikCompactDescription = {
    refresh: refreshCompactDescriptions,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Rechargement dans l'éditeur Shopify
  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:block:select", initAll);

  // Réinitialise pour les éléments injectés par le modal (quickview)
  document.addEventListener("quickview:loaded", initAll);
})();

/* ====================================================================================================
CART — barre livraison gratuite (paliers metaobjects, sync help drawer)
==================================================================================================== */
(() => {
  function formatMoneyCents(priceInCents) {
    var c = Number(priceInCents) || 0;
    if (theme?.Currency?.formatThemeMoney) {
      return theme.Currency.formatThemeMoney(c);
    }
    var format = theme?.settings?.currencyCodeEnabled
      ? theme?.settings?.moneyWithCurrencyFormat
      : theme?.settings?.moneyFormat;
    if (theme?.Currency?.formatMoney && format) {
      return theme.Currency.formatMoney(c, format);
    }
    return String(c / 100);
  }

  function moneyBold(cents) {
    return '<span class="font-bold">' + formatMoneyCents(cents) + "</span>";
  }

  function formatMoneyCentsNoTrailingZeroDecimals(priceInCents) {
    return formatMoneyCents(priceInCents);
  }

  /**
   * Sous-total marchandise pour les paliers livraison (montant net remises).
   * Base principale: somme des final_line_price (réductions ligne incluses).
   * Si un code panier global existe, on sécurise avec original_total_price - total_discount
   * et on garde la valeur la plus basse des deux (évite d'ignorer une remise panier non ventilée).
   * Repli : items_subtotal_price (API), puis total_price ; moins emballage cadeau si configuré.
   */
  function adjustedCartSubtotalCents(cart, giftWrapVariantId) {
    if (!cart) return 0;
    var linesSum = 0;
    var linesOriginalSum = 0;
    var giftFromLines = 0;
    var giftOriginalFromLines = 0;
    if (Array.isArray(cart.items)) {
      cart.items.forEach(function (line) {
        var fp = Number(line.final_line_price);
        if (!isFinite(fp)) return;
        var r = Math.round(fp);
        linesSum += r;
        var op = Math.round(Number(line.original_line_price));
        if (isFinite(op) && op > 0) linesOriginalSum += op;
        if (
          giftWrapVariantId &&
          Number(line.variant_id) === Number(giftWrapVariantId)
        ) {
          giftFromLines += r;
          if (isFinite(op) && op > 0) giftOriginalFromLines += op;
        }
      });
    }
    var base;
    if (linesSum > 0) {
      base = linesSum;
    } else {
      var subApi = NaN;
      if (cart.items_subtotal_price != null && cart.items_subtotal_price !== "") {
        var s = Math.round(Number(cart.items_subtotal_price));
        if (isFinite(s) && s >= 0) subApi = s;
      }
      if (!isNaN(subApi)) {
        base = subApi;
      } else {
        var tp = NaN;
        if (cart.total_price != null && cart.total_price !== "") {
          var n = Math.round(Number(cart.total_price));
          if (isFinite(n) && n >= 0) tp = n;
        }
        base = !isNaN(tp) ? tp : 0;
      }
    }
    if (giftFromLines > 0) {
      base = Math.max(0, base - giftFromLines);
    }

    var netFromOriginal = NaN;
    var cartDiscount = Math.round(Number(cart.total_discount));
    var originalTotal = Math.round(Number(cart.original_total_price));
    if (isFinite(cartDiscount) && cartDiscount >= 0) {
      var originalBase =
        isFinite(originalTotal) && originalTotal >= 0
          ? originalTotal
          : linesOriginalSum > 0
            ? linesOriginalSum
            : NaN;
      if (isFinite(originalBase)) {
        originalBase = Math.max(0, originalBase - giftOriginalFromLines);
        netFromOriginal = Math.max(0, originalBase - cartDiscount);
      }
    }
    if (isFinite(netFromOriginal)) {
      base = Math.min(base, netFromOriginal);
    }
    return Math.max(0, Math.round(base));
  }

  // Exposé pour l’estimation livraison metafield (même sous-total que la barre).
  window.__stoyikAdjustedCartSubtotalCents = adjustedCartSubtotalCents;

  function readBarConfig(bar) {
    var uid = bar.getAttribute("data-bar-uid");
    if (!uid) return null;
    var scr = document.getElementById("CartShippingBarConfig-" + uid);
    if (!scr) return null;
    try {
      return JSON.parse(scr.textContent.trim());
    } catch (e) {
      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        console.warn("[CartShippingBar] Invalid JSON config", uid, e);
      }
      return null;
    }
  }

  /** Plafond « panier ≤ palier » : max fini ; sinon illimité (palier sans max). */
  function stepMaxSort(s) {
    if (s == null || s.maxC == null) return Infinity;
    var m = Number(s.maxC);
    if (!isFinite(m) || m <= 0) return Infinity;
    return m;
  }

  function finiteStepMax(s) {
    if (!s || s.maxC == null) return null;
    var m = Number(s.maxC);
    return isFinite(m) && m > 0 ? m : null;
  }

  /** Plancher pour la zone dynamique (palier intermédiaire) : max fini du palier précédent, sinon min. */
  function prevTierFloorCents(prevStep) {
    if (!prevStep) return 0;
    var mx = finiteStepMax(prevStep);
    if (mx != null && mx > 0) return mx;
    return Math.max(0, Number(prevStep.minC) || 0);
  }

  function feeZero(s) {
    return !!s && (s.feeIsZero || Number(s.feeC) === 0);
  }

  /**
   * Palier sans max fini exploitable pour le tri (on trie alors sur le min).
   */
  function usesMinSortKey(s) {
    if (s == null) return true;
    if (s.maxC == null) return true;
    var m = Number(s.maxC);
    return !(isFinite(m) && m > 0);
  }

  /**
   * Ordre global : d’abord tous les max finis (clé = max), puis les paliers sans max
   * (clé = min). À clé numérique égale, le palier **avec** max passe avant celui **sans** max.
   */
  function cmpSteps(a, b) {
    var aHas = !usesMinSortKey(a);
    var bHas = !usesMinSortKey(b);
    var pa = aHas ? Number(a.maxC) : Number(a.minC) || 0;
    var pb = bHas ? Number(b.maxC) : Number(b.minC) || 0;
    if (pa !== pb) return pa - pb;
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    if (aHas && bHas) {
      var mia = Number(a.minC) || 0;
      var mib = Number(b.minC) || 0;
      if (mia !== mib) return mia - mib;
      return String(a.optId || "").localeCompare(String(b.optId || ""));
    }
    var mia2 = Number(a.minC) || 0;
    var mib2 = Number(b.minC) || 0;
    if (mia2 !== mib2) return mia2 - mib2;
    return String(a.optId || "").localeCompare(String(b.optId || ""));
  }

  /**
   * Ordre d’affichage barre (paliers visibles) : max finis croissants, puis sans max (min croissant),
   * puis retrait des paliers « min seuls » redondants (même option, max précédent = min du suivant, pas en dernière position).
   */
  function sortFiniteMaxDisplay(a, b) {
    var ma = Number(a.maxC) || 0;
    var mb = Number(b.maxC) || 0;
    if (ma !== mb) return ma - mb;
    return String(a.optId || "").localeCompare(String(b.optId || ""));
  }

  function sortOpenMinDisplay(a, b) {
    var mia = Number(a.minC) || 0;
    var mib = Number(b.minC) || 0;
    if (mia !== mib) return mia - mib;
    return String(a.optId || "").localeCompare(String(b.optId || ""));
  }

  function buildBarDisplayOrder(steps) {
    var finite = steps
      .filter(function (s) {
        return !usesMinSortKey(s);
      })
      .slice()
      .sort(sortFiniteMaxDisplay);
    var open = steps
      .filter(function (s) {
        return usesMinSortKey(s);
      })
      .slice()
      .sort(sortOpenMinDisplay);
    var combined = finite.concat(open);
    var n = combined.length;
    var out = [];
    for (var i = 0; i < n; i++) {
      var curS = combined[i];
      if (usesMinSortKey(curS) && i < n - 1) {
        var prevS = combined[i - 1];
        if (
          prevS &&
          !usesMinSortKey(prevS) &&
          String(prevS.optId) === String(curS.optId) &&
          Number(prevS.maxC) === Number(curS.minC)
        ) {
          continue;
        }
      }
      out.push(curS);
    }
    return out;
  }

  function indexInDisplayOrder(order, step) {
    for (var i = 0; i < order.length; i++) {
      if (order[i] === step) return i;
    }
    for (var j = 0; j < order.length; j++) {
      if (stepEquals(order[j], step)) return j;
    }
    return -1;
  }

  /**
   * Si `cur` n’est pas dans la liste affichée (palier ouvert ignoré après un max fusionné),
   * retourne le dernier segment affiché pour la même option.
   */
  function resolveMetaBarDisplayGi(displayOrder, ctx, cur) {
    var gi = indexInDisplayOrder(displayOrder, cur);
    if (gi >= 0) return gi;
    if (!displayOrder || !displayOrder.length || !ctx || !cur) return gi;
    var oid = String(ctx.optId || "");
    var lastIdx = -1;
    for (var i = 0; i < displayOrder.length; i++) {
      if (String(displayOrder[i].optId) === oid) lastIdx = i;
    }
    return lastIdx;
  }

  function structuralValidStep(s) {
    if (!s) return false;
    if (s.maxC == null) return true;
    var maxN = Number(s.maxC);
    if (isFinite(maxN) && maxN > 0) return true;
    return isFinite(Number(s.minC)) && Number(s.minC) > 0;
  }

  function mapJsonStep(s) {
    var maxRaw = s.maxC;
    var maxC =
      maxRaw == null
        ? null
        : isFinite(Number(maxRaw)) && Number(maxRaw) > 0
          ? Number(maxRaw)
          : null;
    return {
      minC: Math.max(0, Number(s.minC) || 0),
      maxC: maxC,
      feeC: Math.max(0, Number(s.feeC) || 0),
      feeIsZero: !!s.feeIsZero,
      suffix: s.suffix != null ? String(s.suffix).trim() : "",
      optId: s.optId != null ? String(s.optId) : "",
      liquidBarInclude: s.includeInBar !== false,
    };
  }

  /**
   * Tous les paliers valides (structure), y compris includeInBar JSON false,
   * pour la chaîne complète par option (sous-message palier). barInclude = affichage barre.
   */
  function normalizeSteps(cfg) {
    var raw = cfg && Array.isArray(cfg.steps) ? cfg.steps.slice() : [];
    raw.sort(cmpSteps);
    var out = raw.filter(structuralValidStep).map(mapJsonStep);
    applyShippingBarTierInclusion(out);
    return out;
  }

  /**
   * Palier sans max : retiré de la barre s’il existe **n’importe où** un palier avec
   * un max fini **strictement supérieur** à ce palier.min (sinon le « min seul »
   * resterait prioritaire avec plafond illimité et fausserait le choix du palier actif).
   */
  function applyShippingBarTierInclusion(steps) {
    steps.forEach(function (s) {
      s.barInclude = s.liquidBarInclude !== false;
    });
    var finiteMaxVals = steps
      .filter(function (x) {
        return !usesMinSortKey(x);
      })
      .map(function (x) {
        return Number(x.maxC);
      })
      .filter(function (m) {
        return isFinite(m) && m > 0;
      });
    steps.forEach(function (s) {
      if (!usesMinSortKey(s)) return;
      var tmin = Number(s.minC) || 0;
      for (var i = 0; i < finiteMaxVals.length; i++) {
        if (finiteMaxVals[i] > tmin) {
          s.barInclude = false;
          return;
        }
      }
    });
  }

  function groupStepsByOpt(steps) {
    var map = Object.create(null);
    for (var i = 0; i < steps.length; i++) {
      var s = steps[i];
      var k = s.optId || "_";
      if (!map[k]) map[k] = [];
      map[k].push(s);
    }
    Object.keys(map).forEach(function (k) {
      map[k].sort(cmpSteps);
    });
    return map;
  }

  /** Option dont le plafond de palier actif est le plus proche du panier (écart minimal). */
  function pickCartBarContext(steps, cartC) {
    var byOpt = groupStepsByOpt(steps);
    var keys = Object.keys(byOpt);
    var bestKey = null;
    var bestGap = Infinity;
    var bestJ = -1;
    for (var ki = 0; ki < keys.length; ki++) {
      var oid = keys[ki];
      var arr = byOpt[oid];
      var j = -1;
      for (var t = 0; t < arr.length; t++) {
        var cap = stepMaxSort(arr[t]);
        if (cartC <= cap) {
          j = t;
          break;
        }
      }
      if (j < 0) continue;
      var capJ = stepMaxSort(arr[j]);
      var gap = capJ === Infinity ? 0 : capJ - cartC;
      var better =
        gap < bestGap ||
        (gap === bestGap &&
          (bestKey == null || String(oid).localeCompare(String(bestKey)) < 0));
      if (better) {
        bestGap = gap;
        bestKey = oid;
        bestJ = j;
      }
    }
    if (bestKey == null || bestJ < 0) return null;
    return { optId: bestKey, arr: byOpt[bestKey], j: bestJ };
  }

  function tiersForOptSorted(steps, optId) {
    return steps
      .filter(function (s) {
        return String(s.optId) === String(optId);
      })
      .sort(cmpSteps);
  }

  function lastBarStep(steps) {
    return steps.length ? steps[steps.length - 1] : null;
  }

  function globalMaxFinite(steps) {
    return steps.reduce(function (m, s) {
      var c = stepMaxSort(s);
      return c === Infinity ? m : Math.max(m, c);
    }, -1);
  }

  function suffixTail(suf) {
    return suf ? " " + suf : "";
  }

  function tpl(str, map) {
    if (str == null) return "";
    var out = String(str);
    Object.keys(map).forEach(function (k) {
      var v = map[k] == null ? "" : String(map[k]);
      out = out.split("[[" + k + "]]").join(v);
    });
    return out;
  }

  function stepEquals(a, b) {
    if (!a || !b) return false;
    return (
      String(a.optId) === String(b.optId) &&
      Number(a.minC) === Number(b.minC) &&
      (a.maxC == null ? b.maxC == null : Number(a.maxC) === Number(b.maxC)) &&
      Number(a.feeC) === Number(b.feeC)
    );
  }

  function indexOfStepInArr(arr, step) {
    for (var i = 0; i < arr.length; i++) {
      if (stepEquals(arr[i], step)) return i;
    }
    return -1;
  }

  /** Sous-barre : check-circle légèrement plus petit (icon-xs, theme.css). */
  function iconCheckCircleSubHtml() {
    return (
      '<svg class="icon icon-check-circle icon-xs shrink-0" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path stroke-linecap="round" stroke-linejoin="round" d="M9 12L11 14L16 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"></path>' +
      "</svg>"
    );
  }

  /** Message palier final : même icône, taille icon-sm (thème). */
  function iconCheckCircleDoneHtml() {
    return (
      '<svg class="icon icon-check-circle icon-sm shrink-0" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path stroke-linecap="round" stroke-linejoin="round" d="M9 12L11 14L16 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"></path>' +
      "</svg>"
    );
  }

  /**
   * @param {boolean} [preserveTierAnimLayout] — si vrai, ne pas retirer mid / last / lead-instant
   *   (évite de casser une séquence `data-meta-tier-anim` quand un 2e cart:updated arrive avec le même palier).
   */
  function resetMetaBarDoneVisual(bar, msgEl, preserveTierAnimLayout) {
    if (msgEl) {
      msgEl.className = "free-shipping-bar__msg text-xs leading-tight";
    }
    if (!bar) return;
    bar.classList.remove("free-shipping-bar--meta-done-full");
    bar.classList.remove("free-shipping-bar--meta-black-at-zero");
    if (preserveTierAnimLayout) return;
    bar.classList.remove("free-shipping-bar--meta-mid-progress");
    bar.classList.remove("free-shipping-bar--meta-lead-instant");
    bar.classList.remove("free-shipping-bar--meta-last-tier");
  }

  function clearMetaSubmsg(subEl) {
    if (!subEl) return;
    subEl.innerHTML = "";
    subEl.style.display = "none";
    subEl.style.removeProperty("opacity");
    subEl.style.removeProperty("height");
    subEl.style.removeProperty("min-height");
    subEl.style.removeProperty("margin-top");
    subEl.style.removeProperty("overflow");
    subEl.setAttribute("hidden", "");
  }

  function clearMetaMaxLabel(maxEl) {
    if (!maxEl) return;
    maxEl.textContent = "";
    maxEl.classList.add("is-hidden");
    maxEl.setAttribute("hidden", "");
  }

  function clearMetaTierAnim(bar) {
    if (!bar || !bar._metaTierTimers) return;
    bar._metaTierTimers.forEach(function (id) {
      clearTimeout(id);
    });
    bar._metaTierTimers.length = 0;
    bar.classList.remove("free-shipping-bar--meta-black-at-zero");
  }

  function pushMetaTierTimer(bar, timeoutId) {
    if (!bar._metaTierTimers) bar._metaTierTimers = [];
    bar._metaTierTimers.push(timeoutId);
  }

  function metaTierAnimPrefersReduced() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  /**
   * Clé palier : inclut l’index d’affichage `gi` (ordre barre) pour éviter les collisions
   * entre paliers avec le même isMid (ex. premier vs dernier, tous deux non « mid »).
   */
  function metaTierKey(ctx, cur, isMid, displayGi) {
    if (!ctx || !cur) return "";
    var mx = finiteStepMax(cur);
    var g =
      displayGi != null && isFinite(Number(displayGi))
        ? Number(displayGi)
        : -1;
    return [
      String(ctx.optId),
      String(ctx.j),
      String(cur.minC),
      mx == null ? "" : String(mx),
      isMid ? "1" : "0",
      String(g),
    ].join("|");
  }

  function dynamicLeadStepPct(displayOrder) {
    var paidCount = displayOrder.filter(function (s) {
      return !feeZero(s);
    }).length;
    return paidCount > 0 ? 60 / paidCount : 0;
  }

  function computeLeadPct(displayOrder, gi) {
    var unit = dynamicLeadStepPct(displayOrder);
    if (unit <= 0 || gi <= 0) return 0;
    var passedPaid = 0;
    for (var i = 0; i < gi && i < displayOrder.length; i++) {
      if (!feeZero(displayOrder[i])) passedPaid++;
    }
    return Math.max(0, Math.min(60, passedPaid * unit));
  }

  function setMetaLeadPct(bar, pct, opts) {
    opts = opts || {};
    var noIntermediateMode =
      !!bar &&
      bar.getAttribute &&
      bar.getAttribute("data-meta-no-intermediate") === "1";
    var p = Number(pct);
    if (!isFinite(p)) p = 0;
    if (p < 0) p = 0;
    if (p > 60) p = 60;
    if (noIntermediateMode) p = 0;
    bar.style.setProperty("--meta-lead-pct", p + "%");
    var forceMid = !!opts.forceMid;
    var forceMidOff = !!opts.forceMidOff;
    if (noIntermediateMode || forceMidOff) {
      bar.classList.remove("free-shipping-bar--meta-mid-progress");
    } else if (p > 0.05 || forceMid) {
      bar.classList.add("free-shipping-bar--meta-mid-progress");
    } else {
      bar.classList.remove("free-shipping-bar--meta-mid-progress");
    }
    return p;
  }

  function boldGratuiteWord(html) {
    var s = String(html || "");
    if (!s) return s;
    return s.replace(/\bgratuite\b/gi, "<strong>$&</strong>");
  }

  /**
   * Le thème remplace le HTML du tiroir / page panier à chaque mise à jour (cart.js :
   * miniCart.innerHTML = …), ce qui recrée la barre avec --meta-progress-fill: 0 et
   * efface data-meta-tier-key. On mémorise le dernier état par bar_uid pour enchaîner
   * la transition depuis la dernière valeur, pas depuis 0.
   */
  var metaBarLastVisualState = Object.create(null);
  /** Dernier index d’affichage `gi` appliqué pour ce `bar_uid` (survit à innerHTML ; sert quand `metaBarLastVisualState` est encore vide). */
  var lastAppliedMetaGiByUid = Object.create(null);
  /** Dernier contenu texte (haut + bas) pour permettre un crossfade après re-render DOM. */
  var metaBarLastTextState = Object.create(null);

  function readLastAppliedMetaGi(uid) {
    var k = uid || "_";
    var v = lastAppliedMetaGiByUid[k];
    return isFinite(Number(v)) ? Number(v) : null;
  }

  function setLastAppliedMetaGi(uid, gi) {
    var k = uid || "_";
    if (gi == null || !isFinite(Number(gi)) || Number(gi) < 0) {
      delete lastAppliedMetaGiByUid[k];
      return;
    }
    lastAppliedMetaGiByUid[k] = Number(gi);
  }

  function readMetaBarVisualState(uid) {
    var k = uid || "_";
    return metaBarLastVisualState[k] || null;
  }

  function writeMetaBarVisualState(
    uid,
    fill,
    tierKey,
    wasMid,
    leadPct,
    cartC,
    gi,
    isLastTierFlag,
    tierAnimInflight
  ) {
    var k = uid || "_";
    var f = Number(fill);
    if (!isFinite(f)) f = 0;
    if (f < 0) f = 0;
    if (f > 1) f = 1;
    var prevStore = metaBarLastVisualState[k];
    var inflight;
    if (arguments.length > 8) {
      inflight = tierAnimInflight;
    } else if (prevStore && prevStore.tierAnimInflight) {
      inflight = prevStore.tierAnimInflight;
    } else {
      inflight = null;
    }
    metaBarLastVisualState[k] = {
      fill: f,
      tierKey: tierKey == null ? "" : String(tierKey),
      wasMid: !!wasMid,
      leadPct: Math.max(0, Math.min(60, Number(leadPct) || 0)),
      cartC: Math.max(0, Number(cartC) || 0),
      gi: isFinite(Number(gi)) ? Number(gi) : -1,
      isLastTier: !!isLastTierFlag,
      tierAnimInflight: inflight || null,
    };
  }

  function clearMetaBarVisualState(uid) {
    var k = uid || "_";
    delete metaBarLastVisualState[k];
    delete lastAppliedMetaGiByUid[k];
    delete metaBarLastTextState[k];
  }

  /**
   * Capture l’état visuel courant d’une/des barres avant remplacement DOM (innerHTML).
   * Appelé par cart.js / quick-order-list.js juste avant le swap des sections.
   */
  function captureMetaBarVisualStateFromRoot(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll("[data-free-shipping-meta]").forEach(function (bar) {
      var uid = bar.getAttribute("data-bar-uid") || "_";
      var prog = bar.querySelector("[data-free-shipping-meta-progress]");
      if (!prog) return;
      var fill = Number(prog.style.getPropertyValue("--meta-progress-fill"));
      if (!isFinite(fill)) fill = 0;
      var leadRaw = String(bar.style.getPropertyValue("--meta-lead-pct") || "0").replace("%", "");
      var leadPct = Number(leadRaw);
      if (!isFinite(leadPct)) leadPct = 0;
      var store = readMetaBarVisualState(uid);
      var tierKey = bar.getAttribute("data-meta-tier-key") || "";
      if (bar.classList.contains("free-shipping-bar--meta-done-full")) {
        tierKey = "__done__";
      } else if (!tierKey) {
        var prevStoreTier = store && typeof store.tierKey === "string" ? store.tierKey : "";
        if (prevStoreTier === "__done__" && bar.classList.contains("free-shipping-bar--meta-last-tier")) {
          tierKey = "__done__";
        }
      }
      var wasMid = bar.getAttribute("data-meta-was-mid") === "1";
      var isLastTier = bar.classList.contains("free-shipping-bar--meta-last-tier");
      var cartCFromStore = store && isFinite(store.cartC) ? Number(store.cartC) : NaN;
      var cartCFromAttr = Number(bar.getAttribute("data-cart-initial-subtotal"));
      var cartC = isFinite(cartCFromStore)
        ? cartCFromStore
        : isFinite(cartCFromAttr)
          ? cartCFromAttr
          : 0;
      var gi = store && isFinite(store.gi) ? Number(store.gi) : -1;
      var msgEl = bar.querySelector("[data-free-shipping-meta-msg]");
      var subEl = bar.querySelector("[data-free-shipping-meta-submsg]");
      var maxEl = bar.querySelector("[data-free-shipping-meta-max]");
      metaBarLastTextState[uid] = {
        msgHtml: msgEl ? msgEl.innerHTML || "" : "",
        subHtml: subEl ? subEl.innerHTML || "" : "",
        subVisible: !!(subEl && !subEl.hasAttribute("hidden")),
        maxText: maxEl ? maxEl.textContent || "" : "",
        maxVisible: !!(maxEl && !maxEl.hasAttribute("hidden") && !maxEl.classList.contains("is-hidden")),
      };
      writeMetaBarVisualState(
        uid,
        fill,
        tierKey,
        wasMid,
        leadPct,
        cartC,
        gi,
        isLastTier,
        store && store.tierAnimInflight ? store.tierAnimInflight : null
      );
    });
  }
  if (typeof window !== "undefined") {
    window.__stoyikCaptureMetaBarState = captureMetaBarVisualStateFromRoot;
  }

  /**
   * Applique le remplissage cible ; si même palier (tierKey) qu’en mémoire, repart de
   * l’ancien fill (sans transition) puis anime vers la cible — évite le départ à 0
   * après remplacement DOM.
   */
  function applyMetaProgressPreservingTransition(
    prog,
    uid,
    newTierKey,
    targetFillFrac,
    wasMidFlag,
    leadPct,
    cartC,
    gi,
    isLastTierFlag,
    hintStartFillFrac
  ) {
    var tf = Math.min(1, Math.max(0, Number(targetFillFrac) || 0));
    var store = readMetaBarVisualState(uid);
    var memFill =
      store && isFinite(store.fill) ? Math.min(1, Math.max(0, Number(store.fill))) : NaN;
    var hintFill =
      isLastTierFlag && isFinite(Number(hintStartFillFrac))
        ? Math.min(1, Math.max(0, Number(hintStartFillFrac)))
        : NaN;
    /* Dernier palier : ne jamais partir d’un fill mémoire à 0 si l’UI était à ~100 %. */
    var bridgeFrom = isFinite(memFill) ? memFill : 0;
    if (isLastTierFlag && isFinite(hintFill)) {
      bridgeFrom = Math.max(bridgeFrom, hintFill);
    }
    var tierMatch =
      !!(store && store.tierKey && newTierKey && store.tierKey === newTierKey);
    var bridge =
      tierMatch && isFinite(bridgeFrom) && Math.abs(bridgeFrom - tf) > 0.001;

    if (bridge) {
      prog.classList.add("free-shipping-bar__progress--instant");
      setMetaShippingProgressFraction(prog, bridgeFrom);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          prog.classList.remove("free-shipping-bar__progress--instant");
          setMetaShippingProgressFraction(prog, tf);
          writeMetaBarVisualState(
            uid,
            tf,
            newTierKey,
            wasMidFlag,
            leadPct,
            cartC,
            gi,
            isLastTierFlag,
            null
          );
        });
      });
      return;
    }

    setMetaShippingProgressFraction(prog, tf);
    writeMetaBarVisualState(
      uid,
      tf,
      newTierKey,
      wasMidFlag,
      leadPct,
      cartC,
      gi,
      isLastTierFlag,
      null
    );
  }

  /** Remplissage barre meta 0–1 (transition CSS sur --meta-progress-fill). */
  function setMetaShippingProgressFraction(prog, frac) {
    var f = Number(frac);
    if (!isFinite(f)) f = 0;
    if (f < 0) f = 0;
    if (f > 1) f = 1;
    prog.style.setProperty("--meta-progress-fill", String(f));
  }

  function setMetaShippingProgressFromPct(prog, pct0to100) {
    setMetaShippingProgressFraction(prog, (Number(pct0to100) || 0) / 100);
  }

  /**
   * Pourcentage 0–100 pour le segment affiché `gi` : 1er palier = panier / max ;
   * paliers suivants = (panier − plancher palier précédent) / (max du palier − plancher),
   * pour que 100 % corresponde au max du palier courant uniquement (panier après réductions).
   */
  function computeMetaDisplayedTierProgressPct(displayOrder, gi, cartC) {
    if (!displayOrder || !displayOrder.length || gi < 0 || gi >= displayOrder.length) {
      return 0;
    }
    var step = displayOrder[gi];
    var cc = Math.max(0, Number(cartC) || 0);
    var hi = finiteStepMax(step);
    if (gi === 0 || displayOrder.length === 1) {
      if (hi != null && hi > 0) {
        return Math.min(100, Math.max(0, Math.round((cc / hi) * 100)));
      }
      return 100;
    }
    var prevBar = displayOrder[gi - 1];
    var lo = prevTierFloorCents(prevBar);
    if (hi != null && hi > lo) {
      return Math.min(
        100,
        Math.max(0, Math.round(((cc - lo) / (hi - lo)) * 100))
      );
    }
    if (hi != null && hi > 0) {
      return Math.min(100, Math.round((cc / hi) * 100));
    }
    return 100;
  }

  /**
   * Plafond du palier affiché (max du segment), pour « il reste X » et cohérence avec le %.
   */
  function currentDisplayedTierCapCents(displayOrder, gi, fallbackStep) {
    var step =
      displayOrder && gi >= 0 && gi < displayOrder.length
        ? displayOrder[gi]
        : fallbackStep;
    return finiteStepMax(step);
  }

  /**
   * Fraction de remplissage (0–1) pour un index d’affichage `gi` et un total panier,
   * même règle que dans applyMetaShippingBar (après re-render le DOM a souvent fill = 0).
   */
  function computeMetaProgressFillForDisplayGi(displayOrder, gi, cartC) {
    return Math.min(
      1,
      Math.max(0, computeMetaDisplayedTierProgressPct(displayOrder, gi, cartC) / 100)
    );
  }

  function applyMetaShippingBar(bar, cart) {
    var cfg = readBarConfig(bar);
    var allSteps = normalizeSteps(cfg);
    var steps = allSteps.filter(function (s) {
      return s.barInclude !== false;
    });
    var msgEl = bar.querySelector("[data-free-shipping-meta-msg]");
    var prog = bar.querySelector("[data-free-shipping-meta-progress]");
    var subEl = bar.querySelector("[data-free-shipping-meta-submsg]");
    var maxEl = bar.querySelector("[data-free-shipping-meta-max]");
    if (!msgEl || !prog) return;
    msgEl.style.display = "block";

    var barUid = bar.getAttribute("data-bar-uid") || "_";
    var metaVisualMem = readMetaBarVisualState(barUid);
    var prevTextMem = metaBarLastTextState[barUid] || null;
    var tierAnimInFlightMem =
      metaVisualMem && metaVisualMem.tierAnimInflight
        ? metaVisualMem.tierAnimInflight
        : null;

    /* Après innerHTML, Liquid remet souvent des conteneurs texte vides.
       On réhydrate immédiatement l'ancien texte pour éviter le "trou" visuel avant crossfade. */
    if (prevTextMem) {
      if (msgEl && !msgEl.innerHTML && prevTextMem.msgHtml) {
        msgEl.innerHTML = prevTextMem.msgHtml;
      }
      if (subEl && !subEl.innerHTML && prevTextMem.subHtml) {
        subEl.innerHTML = prevTextMem.subHtml;
        if (prevTextMem.subVisible) {
          subEl.style.display = "block";
          subEl.style.removeProperty("opacity");
          subEl.removeAttribute("hidden");
        } else {
          subEl.style.display = "none";
          subEl.style.removeProperty("opacity");
          subEl.setAttribute("hidden", "");
        }
      }
      if (maxEl) {
        if (!maxEl.textContent && prevTextMem.maxText) {
          maxEl.textContent = prevTextMem.maxText;
        }
        if (prevTextMem.maxVisible) {
          maxEl.classList.remove("is-hidden");
          maxEl.removeAttribute("hidden");
        } else {
          maxEl.classList.add("is-hidden");
          maxEl.setAttribute("hidden", "");
        }
      }
    }
    if (subEl && !subEl.hasAttribute("hidden")) {
      bar.removeAttribute("data-meta-sub-hidden");
    } else {
      bar.setAttribute("data-meta-sub-hidden", "1");
    }

    if (!allSteps.length) {
      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        console.warn(
          "[CartShippingBar] No tiers in config (Liquid: empty zone match, wrong metaobject keys, or all tiers filtered). Bar uid:",
          bar.getAttribute("data-bar-uid")
        );
      }
      bar.setAttribute("hidden", "");
      msgEl.innerHTML = "";
      clearMetaSubmsg(subEl);
      clearMetaMaxLabel(maxEl);
      bar.setAttribute("data-meta-sub-hidden", "1");
      clearMetaTierAnim(bar);
      bar.removeAttribute("data-meta-tier-key");
      bar.removeAttribute("data-meta-was-mid");
      bar.removeAttribute("data-meta-tier-anim");
      clearMetaBarVisualState(barUid);
      resetMetaBarDoneVisual(bar, msgEl);
      return;
    }
    if (!steps.length) {
      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        console.warn(
          "[CartShippingBar] No tiers eligible for the bar (tier boolean filter or open-max rule). Bar uid:",
          bar.getAttribute("data-bar-uid")
        );
      }
      bar.setAttribute("hidden", "");
      msgEl.innerHTML = "";
      clearMetaSubmsg(subEl);
      clearMetaMaxLabel(maxEl);
      bar.setAttribute("data-meta-sub-hidden", "1");
      clearMetaTierAnim(bar);
      bar.removeAttribute("data-meta-tier-key");
      bar.removeAttribute("data-meta-was-mid");
      bar.removeAttribute("data-meta-tier-anim");
      clearMetaBarVisualState(barUid);
      resetMetaBarDoneVisual(bar, msgEl);
      return;
    }
    var prevTierKey = bar.getAttribute("data-meta-tier-key") || "";
    bar.removeAttribute("hidden");
    /* Ne pas vider le sous-texte ici : on le crossfade au moment seuil (0% / 100%). */

    var str = (cfg && cfg.strings) || {};
    var cartC = adjustedCartSubtotalCents(cart, cfg && cfg.giftWrapVariantId);
    var META_TIER_MS = 830;
    var META_GREEN_MS = 450;

    var gmf = globalMaxFinite(steps);
    var lastS = lastBarStep(steps);
    var pastAllFiniteCaps =
      gmf >= 0
        ? cartC > gmf
        : !!(lastS && lastS.maxC == null && cartC >= (lastS.minC || 0));

    if (pastAllFiniteCaps) {
      clearMetaTierAnim(bar);
      var doneDisplayOrder = buildBarDisplayOrder(steps);
      var doneHasNoIntermediateTiers = doneDisplayOrder.length === 2;
      if (doneHasNoIntermediateTiers) {
        bar.setAttribute("data-meta-no-intermediate", "1");
      } else {
        bar.removeAttribute("data-meta-no-intermediate");
      }
      var last = lastS;
      if (!last) {
        msgEl.innerHTML = "";
        clearMetaSubmsg(subEl);
        clearMetaMaxLabel(maxEl);
        bar.setAttribute("data-meta-sub-hidden", "1");
        bar.removeAttribute("data-meta-tier-key");
        bar.removeAttribute("data-meta-was-mid");
        bar.removeAttribute("data-meta-tier-anim");
        clearMetaBarVisualState(barUid);
        resetMetaBarDoneVisual(bar, msgEl);
        return;
      }
      var arrDoneFull = tiersForOptSorted(allSteps, last.optId);
      var firstFee = arrDoneFull.length ? arrDoneFull[0].feeC || 0 : 0;
      var doneInner = feeZero(last)
        ? tpl(str.doneFree, { suffix: suffixTail(last.suffix) })
        : tpl(str.doneDiscount, {
            fee_saved: moneyBold(
              Math.max(0, firstFee - (Number(last.feeC) || 0))
            ),
            suffix: suffixTail(last.suffix),
          });
      msgEl.className = "free-shipping-bar__msg text-xs leading-tight";
      var doneMsgHtml =
        '<span class="free-shipping-bar__msg-inner free-shipping-bar__msg-inner--success">' +
        iconCheckCircleDoneHtml() +
        '<span class="min-w-0 leading-tight">' +
        doneInner +
        "</span></span>";
      bar.classList.remove("free-shipping-bar--meta-done-full");
      bar.removeAttribute("data-meta-tier-key");
      bar.removeAttribute("data-meta-was-mid");
      var prevFillDone =
        metaVisualMem && isFinite(metaVisualMem.fill)
          ? Number(metaVisualMem.fill)
          : Number(prog.style.getPropertyValue("--meta-progress-fill")) || 0;
      var prevLeadDone =
        metaVisualMem && isFinite(metaVisualMem.leadPct)
          ? Number(metaVisualMem.leadPct)
          : Number((bar.style.getPropertyValue("--meta-lead-pct") || "0").replace("%", ""));
      var wasDoneAlready = !!(metaVisualMem && metaVisualMem.tierKey === "__done__");
      var doneTextsApplied = false;

      function doneTextCrossfade(el, nextHtml, opts) {
        if (!el) return;
        opts = opts || {};
        var hide = !!opts.hide;
        var animate = !!opts.animate;
        var allowHeightChange = !!opts.allowHeightChange;
        var isSubTextDone = el === subEl;
        var prevHtml = el.innerHTML || "";
        if (!prevHtml && prevTextMem) {
          prevHtml = el === msgEl ? (prevTextMem.msgHtml || "") : (prevTextMem.subHtml || "");
        }
        var targetHtml = hide ? "" : (nextHtml || "");
        if (!animate) {
          el.innerHTML = targetHtml;
          if (hide) {
            if (isSubTextDone) {
              el.style.display = "none";
              el.style.removeProperty("height");
              el.style.removeProperty("min-height");
              el.style.removeProperty("opacity");
              el.style.removeProperty("overflow");
            }
            el.setAttribute("hidden", "");
          } else {
            if (isSubTextDone) {
              el.style.display = "block";
            }
            el.removeAttribute("hidden");
          }
          return;
        }
        if (!prevHtml) {
          el.removeAttribute("hidden");
          if (isSubTextDone && !hide) {
            el.style.display = "block";
          }
          el.innerHTML = targetHtml;
          var msNoPrev = 280;
          el.style.opacity = hide ? "1" : "0";
          el.style.transition =
            "opacity " + msNoPrev + "ms cubic-bezier(0.65, 0, 0.35, 1)";
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              el.style.opacity = hide ? "0" : "1";
              var tidNoPrev = setTimeout(function () {
                if (hide) {
                  if (isSubTextDone) {
                    el.style.display = "none";
                    el.style.removeProperty("height");
                    el.style.removeProperty("min-height");
                    el.style.removeProperty("opacity");
                    el.style.removeProperty("overflow");
                  }
                  el.setAttribute("hidden", "");
                } else {
                  if (isSubTextDone) {
                    el.style.display = "block";
                  }
                  el.removeAttribute("hidden");
                }
                if (!hide) el.style.removeProperty("opacity");
                el.style.removeProperty("transition");
              }, msNoPrev + 35);
              pushMetaTierTimer(bar, tidNoPrev);
            });
          });
          return;
        }
        var ms = 280;
        var h = Math.max(el.getBoundingClientRect().height || 0, 1);
        var targetH = hide ? 0 : h;
        if (!hide) {
          var probe = document.createElement("div");
          probe.style.position = "absolute";
          probe.style.visibility = "hidden";
          probe.style.pointerEvents = "none";
          probe.style.left = "-9999px";
          probe.style.top = "0";
          probe.style.width = Math.max(
            el.clientWidth || 0,
            el.offsetWidth || 0,
            (el.parentElement && (el.parentElement.clientWidth || el.parentElement.offsetWidth)) || 0,
            1
          ) + "px";
          probe.innerHTML = targetHtml;
          document.body.appendChild(probe);
          targetH = Math.max(probe.getBoundingClientRect().height || 0, 1);
          probe.remove();
        }
        var animateHeight =
          allowHeightChange && (hide || Math.abs(targetH - h) > 2);
        var lockHeight = !allowHeightChange || (allowHeightChange && !hide && !animateHeight);
        el.removeAttribute("hidden");
        el.style.position = "relative";
        if (animateHeight || lockHeight) {
          el.style.overflow = "hidden";
          el.style.height = h + "px";
        }
        if (animateHeight) {
          el.style.transition =
            "height " + ms + "ms cubic-bezier(0.65, 0, 0.35, 1)";
        }
        var prevTransitionDone = el.style.transition || "";
        el.style.transition = (
          (el.style.transition ? el.style.transition + ", " : "") +
          "opacity " + ms + "ms cubic-bezier(0.65, 0, 0.35, 1)"
        );
        el.style.opacity = hide ? "1" : "0";

        var oldSpan = document.createElement("span");
        oldSpan.style.position = "absolute";
        oldSpan.style.inset = "0";
        oldSpan.style.display = "block";
        oldSpan.style.width = "100%";
        oldSpan.style.opacity = "1";
        oldSpan.style.pointerEvents = "none";
        oldSpan.style.transition = "opacity " + ms + "ms cubic-bezier(0.65, 0, 0.35, 1)";
        oldSpan.innerHTML = prevHtml;

        var newSpan = document.createElement("span");
        newSpan.style.position = "absolute";
        newSpan.style.inset = "0";
        newSpan.style.display = "block";
        newSpan.style.width = "100%";
        newSpan.style.opacity = "0";
        newSpan.style.pointerEvents = "none";
        newSpan.style.transition = "opacity " + ms + "ms cubic-bezier(0.65, 0, 0.35, 1)";
        newSpan.innerHTML = targetHtml;

        el.innerHTML = "";
        el.appendChild(oldSpan);
        el.appendChild(newSpan);

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            el.style.opacity = hide ? "0" : "1";
            oldSpan.style.opacity = "0";
            newSpan.style.opacity = hide ? "0" : "1";
            if (animateHeight) el.style.height = targetH + "px";
            var tid = setTimeout(function () {
              el.innerHTML = targetHtml;
              if (hide) {
                if (isSubTextDone) {
                  el.style.display = "none";
                }
                el.style.height = "0px";
                el.style.minHeight = "0";
                el.style.opacity = "0";
                el.setAttribute("hidden", "");
              } else {
                if (isSubTextDone) {
                  el.style.display = "block";
                }
                el.style.removeProperty("height");
                el.style.removeProperty("min-height");
                el.style.removeProperty("opacity");
                el.removeAttribute("hidden");
              }
              if (animateHeight || lockHeight) {
                el.style.removeProperty("overflow");
                el.style.removeProperty("height");
              }
              if (animateHeight) {
                el.style.removeProperty("transition");
              }
              if (!hide) {
                el.style.removeProperty("opacity");
              }
              if (prevTransitionDone) {
                el.style.transition = prevTransitionDone;
              }
              el.style.removeProperty("position");
            }, ms + 35);
            pushMetaTierTimer(bar, tid);
          });
        });
      }

      function applyDoneTexts(animate) {
        if (doneTextsApplied) return;
        doneTextsApplied = true;
        doneTextCrossfade(msgEl, doneMsgHtml, {
          animate: !!animate,
          allowHeightChange: true,
        });
        doneTextCrossfade(subEl, "", {
          animate: !!animate,
          hide: true,
          allowHeightChange: true,
        });
        transitionPinnedMaxEl(maxEl, "", {
          animate: !!animate,
          hide: true,
        });
        bar.setAttribute("data-meta-sub-hidden", "1");
        metaBarLastTextState[barUid] = {
          msgHtml: doneMsgHtml,
          subHtml: "",
          subVisible: false,
          maxText: "",
          maxVisible: false,
        };
      }

      function finalizeDoneFullVisual() {
        bar.removeAttribute("data-meta-tier-anim");
        bar.classList.add("free-shipping-bar--meta-done-full");
        bar.classList.add("free-shipping-bar--meta-last-tier");
        setMetaLeadPct(bar, 0, { forceMidOff: true });
        writeMetaBarVisualState(
          barUid,
          1,
          "__done__",
          false,
          0,
          cartC,
          -1,
          true,
          null
        );
      }

      if (!metaTierAnimPrefersReduced() && !wasDoneAlready) {
        bar.setAttribute("data-meta-tier-anim", "1");
        bar.classList.remove("free-shipping-bar--meta-done-full");
        bar.classList.remove("free-shipping-bar--meta-last-tier");
        bar.classList.add("free-shipping-bar--meta-lead-instant");
        setMetaLeadPct(bar, prevLeadDone, { forceMid: true });

        prog.classList.add("free-shipping-bar__progress--instant");
        setMetaShippingProgressFraction(
          prog,
          Math.min(1, Math.max(0, Number(prevFillDone) || 0))
        );
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            prog.classList.remove("free-shipping-bar__progress--instant");
            setMetaShippingProgressFraction(prog, 1);

            var tidDoneMorph = setTimeout(function () {
              applyDoneTexts(true);
              bar.classList.remove("free-shipping-bar--meta-lead-instant");
              bar.classList.add("free-shipping-bar--meta-last-tier");
              /**
               * Important : on garde provisoirement l’état mid-progress pendant le morph
               * pour que la pastille soit masquée par transition (opacity), pas supprimée
               * instantanément. La fusion finale (lead=0 / mid off) se fait dans finalize.
               */
              setMetaLeadPct(bar, prevLeadDone, { forceMid: true });
              var tidDoneFinalize = setTimeout(finalizeDoneFullVisual, META_TIER_MS);
              pushMetaTierTimer(bar, tidDoneFinalize);
            }, META_TIER_MS);
            pushMetaTierTimer(bar, tidDoneMorph);
          });
        });
      } else {
        applyDoneTexts(false);
        prog.classList.add("free-shipping-bar__progress--instant");
        setMetaShippingProgressFraction(prog, 1);
        void prog.offsetWidth;
        prog.classList.remove("free-shipping-bar__progress--instant");
        finalizeDoneFullVisual();
      }
      delete lastAppliedMetaGiByUid[barUid || "_"];
      return;
    }

    var ctx = pickCartBarContext(steps, cartC);
    if (!ctx) {
      bar.setAttribute("hidden", "");
      msgEl.innerHTML = "";
      clearMetaSubmsg(subEl);
      clearMetaTierAnim(bar);
      bar.removeAttribute("data-meta-tier-key");
      bar.removeAttribute("data-meta-was-mid");
      bar.removeAttribute("data-meta-tier-anim");
      bar.removeAttribute("data-meta-no-intermediate");
      clearMetaBarVisualState(barUid);
      resetMetaBarDoneVisual(bar, msgEl);
      return;
    }

    var cur = ctx.arr[ctx.j];
    var nextSame =
      ctx.j + 1 < ctx.arr.length ? ctx.arr[ctx.j + 1] : null;

    var displayOrder = buildBarDisplayOrder(steps);
    var gi = resolveMetaBarDisplayGi(displayOrder, ctx, cur);
    if (gi < 0 && displayOrder.length) gi = 0;
    var dispLast = displayOrder.length - 1;
    var isMidProgress =
      displayOrder.length > 1 && gi >= 1 && gi < dispLast;
    var hasNoIntermediateTiers = displayOrder.length === 2;
    if (hasNoIntermediateTiers) {
      bar.setAttribute("data-meta-no-intermediate", "1");
    } else {
      bar.removeAttribute("data-meta-no-intermediate");
    }
    var leadPctTarget = computeLeadPct(displayOrder, gi);
    var isLastDisplayedTier =
      displayOrder.length > 1 && gi === dispLast;
    if (hasNoIntermediateTiers && isLastDisplayedTier) {
      /* Mode 2 paliers (premier + dernier): jamais de segment/point vert intermédiaire. */
      leadPctTarget = 0;
    }
    var wasLastTier = !!(metaVisualMem && metaVisualMem.isLastTier);
    var prevGi =
      metaVisualMem && isFinite(metaVisualMem.gi)
        ? Number(metaVisualMem.gi)
        : null;

    var curMx = currentDisplayedTierCapCents(displayOrder, gi, cur);
    var toSpend =
      curMx != null ? Math.max(0, curMx - cartC) : 0;
    var pct = computeMetaDisplayedTierProgressPct(displayOrder, gi, cartC);

    var tierAnimActive = bar.getAttribute("data-meta-tier-anim") === "1";
    var newTierKey = metaTierKey(ctx, cur, isMidProgress, gi);
    var memGi =
      metaVisualMem && isFinite(metaVisualMem.gi)
        ? Number(metaVisualMem.gi)
        : null;
    /**
     * Après miniCart.innerHTML le DOM n’a pas data-meta-tier-key : on s’appuie sur la mémoire JS.
     * Si tierKey ne bouge pas (collision / ancienne écriture) mais l’index d’affichage `gi` change,
     * il faut quand même traiter un changement de palier — sinon applyMetaBarProgressDirect saute
     * toute animation (vert dernier palier + fill 0→100 % d’un coup).
     */
    var tierChanged =
      (prevTierKey !== "" && newTierKey !== prevTierKey) ||
      (!prevTierKey &&
        metaVisualMem &&
        metaVisualMem.tierKey &&
        metaVisualMem.tierKey !== newTierKey &&
        metaVisualMem.tierKey !== "__done__") ||
      (displayOrder.length > 1 &&
        metaVisualMem &&
        wasLastTier !== isLastDisplayedTier) ||
      (metaVisualMem &&
        metaVisualMem.tierKey !== "__done__" &&
        memGi != null &&
        memGi !== gi &&
        !tierAnimActive &&
        !tierAnimInFlightMem);

    var runEnterLastTierAnim =
      displayOrder.length > 1 &&
      isLastDisplayedTier &&
      !wasLastTier;
    var runExitLastTierAnim =
      displayOrder.length > 1 &&
      !isLastDisplayedTier &&
      wasLastTier;

    resetMetaBarDoneVisual(
      bar,
      msgEl,
      (tierAnimActive || tierAnimInFlightMem) && !tierChanged
    );
    var prevFill = metaVisualMem && isFinite(metaVisualMem.fill)
      ? Number(metaVisualMem.fill)
      : Number(prog.style.getPropertyValue("--meta-progress-fill")) || 0;
    var prevLeadPct =
      metaVisualMem && isFinite(metaVisualMem.leadPct)
        ? Number(metaVisualMem.leadPct)
        : Number((bar.style.getPropertyValue("--meta-lead-pct") || "0").replace("%", ""));
    var prevCartC =
      metaVisualMem && isFinite(metaVisualMem.cartC) ? Number(metaVisualMem.cartC) : cartC;

    if (
      displayOrder.length > 1 &&
      isLastDisplayedTier &&
      runEnterLastTierAnim
    ) {
      var gSynth =
        prevGi != null && prevGi >= 0 ? prevGi : Math.max(0, dispLast - 1);
      var cartSynth =
        metaVisualMem && isFinite(metaVisualMem.cartC)
          ? Number(metaVisualMem.cartC)
          : cartC;
      var fillSynth = computeMetaProgressFillForDisplayGi(
        displayOrder,
        gSynth,
        cartSynth
      );
      var fillPenultAtCurrentCart =
        dispLast >= 1
          ? computeMetaProgressFillForDisplayGi(
              displayOrder,
              dispLast - 1,
              cartC
            )
          : 0;
      prevFill = Math.max(
        Math.min(1, Math.max(0, Number(prevFill) || 0)),
        fillSynth,
        fillPenultAtCurrentCart
      );
    }
    if (
      displayOrder.length > 1 &&
      !isLastDisplayedTier &&
      runExitLastTierAnim
    ) {
      var gLast =
        prevGi != null && prevGi >= 0 && prevGi < displayOrder.length
          ? prevGi
          : dispLast;
      var cartSynthL =
        metaVisualMem && isFinite(metaVisualMem.cartC)
          ? Number(metaVisualMem.cartC)
          : cartC;
      var fillLast = computeMetaProgressFillForDisplayGi(
        displayOrder,
        gLast,
        cartSynthL
      );
      if (!isFinite(prevFill) || prevFill < 0.02) {
        prevFill = fillLast;
      }
    }

    /* Toujours dernier palier, même palier : repartir du fill cohérent avec l’ancien panier. */
    if (
      displayOrder.length > 1 &&
      isLastDisplayedTier &&
      wasLastTier &&
      !tierChanged &&
      metaVisualMem
    ) {
      var fillLastFromPrevCart = computeMetaProgressFillForDisplayGi(
        displayOrder,
        dispLast,
        prevCartC
      );
      prevFill = Math.max(
        Math.min(1, Math.max(0, Number(prevFill) || 0)),
        fillLastFromPrevCart
      );
    }

    var pendingMsgHtml = "";
    if (nextSame && feeZero(nextSame)) {
      pendingMsgHtml = tpl(str.remainFree, {
        amount: moneyBold(toSpend),
        suffix: suffixTail(cur.suffix),
      });
    } else if (nextSame && !feeZero(nextSame)) {
      /* Économie livraison au passage au palier suivant (tarif actuel − tarif suivant, ≥ 0). */
      var saveNext = Math.max(
        0,
        (Number(cur.feeC) || 0) - (Number(nextSame.feeC) || 0)
      );
      pendingMsgHtml = tpl(str.remainDiscount, {
        amount_remain: moneyBold(toSpend),
        fee_saved: moneyBold(saveNext),
        suffix: suffixTail(cur.suffix),
      });
    } else {
      pendingMsgHtml = tpl(str.remainFree, {
        amount: moneyBold(toSpend),
        suffix: suffixTail(cur.suffix),
      });
    }
    pendingMsgHtml = boldGratuiteWord(pendingMsgHtml);

    /* Sous-texte gauche (message palier) + label max séparé (absolu, hors flux). */
    var pendingMaxText = "";
    var pendingMaxVisible = false;
    var pendingSubHtml = "";
    var pendingSubVisible = false;
    if (subEl && displayOrder.length > 1 && gi >= 1) {
      var prevD = displayOrder[gi - 1];
      var chainPrevOpt = tiersForOptSorted(allSteps, prevD.optId);
      var pi = chainPrevOpt.indexOf(prevD);
      if (pi < 0) {
        pi = indexOfStepInArr(chainPrevOpt, prevD);
      }
      var nextAfterPrev =
        pi >= 0 && pi + 1 < chainPrevOpt.length
          ? chainPrevOpt[pi + 1]
          : null;
      if (nextAfterPrev) {
        var curOptChain = tiersForOptSorted(allSteps, cur.optId);
        var firstFeeCur = curOptChain.length
          ? Number(curOptChain[0].feeC) || 0
          : 0;
        var subFreeStr = str.subFree;
        var subDiscStr = str.subDiscount;
        if (
          subFreeStr == null ||
          subFreeStr === "" ||
          subDiscStr == null ||
          subDiscStr === ""
        ) {
          var docLangSub = (
            document.documentElement.getAttribute("lang") || ""
          ).toLowerCase();
          var frShopSub = docLangSub.indexOf("fr") === 0;
          if (subFreeStr == null || subFreeStr === "") {
            subFreeStr = frShopSub
              ? "Livraison gratuite[[suffix]]"
              : "Free shipping[[suffix]]";
          }
          if (subDiscStr == null || subDiscStr === "") {
            subDiscStr = frShopSub
              ? "Économie de [[amount]] sur la livraison[[suffix]]"
              : "Save [[amount]] on shipping[[suffix]]";
          }
        }
        var subSuffix = suffixTail(prevD.suffix);
        var subInner = feeZero(nextAfterPrev)
          ? tpl(subFreeStr, { suffix: subSuffix })
          : tpl(subDiscStr, {
              amount: moneyBold(
                Math.max(0, firstFeeCur - (Number(cur.feeC) || 0))
              ),
              suffix: subSuffix,
            });
        if (subInner) {
          pendingSubHtml =
            '<span class="free-shipping-bar__submsg-inner">' +
            iconCheckCircleSubHtml() +
            '<span class="min-w-0 leading-tight">' +
            subInner +
            "</span></span>";
          pendingSubVisible = true;
        }
      }
    }
    if (maxEl && curMx != null) {
      pendingMaxText = formatMoneyCentsNoTrailingZeroDecimals(curMx);
      pendingMaxVisible = true;
    }

    if (subEl) {
      if (!pendingSubVisible) {
        /* Ne pas couper l'animation de fermeture: display none seulement quand déjà fermé. */
        if (subEl.hasAttribute("hidden")) {
          subEl.style.display = "none";
          subEl.style.removeProperty("opacity");
          subEl.style.removeProperty("min-height");
        }
      }
    }

    /* Barre : paliers hors dernier = pic puis parallèle ; dernier palier = séquences dédiées (durée ~ 830 ms). */
    prog.classList.remove("free-shipping-bar__progress--instant");
    var pendingTextsApplied = false;
    function measureTargetTextHeight(el, targetHtml) {
      if (!el) return 0;
      var host = el.parentElement || document.body;
      var probe = el.cloneNode(false);
      probe.style.cssText = "";
      probe.style.position = "absolute";
      probe.style.visibility = "hidden";
      probe.style.pointerEvents = "none";
      probe.style.left = "-9999px";
      probe.style.top = "0";
      probe.style.height = "auto";
      probe.style.minHeight = "0";
      probe.style.overflow = "visible";
      var widthRef = Math.max(
        el.getBoundingClientRect().width || 0,
        el.clientWidth || 0,
        el.offsetWidth || 0,
        (el.parentElement &&
          (el.parentElement.getBoundingClientRect().width ||
            el.parentElement.clientWidth ||
            el.parentElement.offsetWidth)) ||
          0,
        1
      );
      probe.style.width = widthRef + "px";
      probe.innerHTML = targetHtml || "";
      host.appendChild(probe);
      var h = Math.max(probe.getBoundingClientRect().height || 0, 1);
      probe.remove();
      return h;
    }
    function transitionTextEl(el, html, opts) {
      if (!el) return;
      opts = opts || {};
      var isSubText = el === subEl;
      var subGapOffset = "calc(var(--sp-3, 0.75rem) * -1)";
      var hide = !!opts.hide;
      var animate = !!opts.animate;
      var allowHeightChange = !!opts.allowHeightChange;
      var nextHtml = hide ? "" : (html || "");
      var curHtml = el.innerHTML || "";
      if (!curHtml && prevTextMem) {
        if (el === msgEl) curHtml = prevTextMem.msgHtml || "";
        else if (el === subEl) curHtml = prevTextMem.subHtml || "";
      }
      var isHiddenNow = el.hasAttribute("hidden");
      var alreadyInTargetVisibility = hide ? isHiddenNow : !isHiddenNow;
      if (curHtml === nextHtml && alreadyInTargetVisibility) return;

      if (!animate) {
        el.innerHTML = nextHtml;
        if (hide) {
          el.style.display = el === subEl ? "none" : "";
          el.style.removeProperty("opacity");
          el.style.removeProperty("min-height");
          if (isSubText) el.style.removeProperty("margin-top");
          el.setAttribute("hidden", "");
          if (isSubText) bar.setAttribute("data-meta-sub-hidden", "1");
        } else {
          el.style.display = el === subEl ? "block" : "";
          el.removeAttribute("hidden");
          el.style.removeProperty("opacity");
          el.style.removeProperty("min-height");
          if (isSubText) el.style.removeProperty("margin-top");
          if (isSubText) bar.removeAttribute("data-meta-sub-hidden");
        }
        return;
      }
      if (!hide && el === subEl) {
        /* Affichage seulement au démarrage de l'animation (évite le gap en amont). */
        el.style.display = "block";
        bar.removeAttribute("data-meta-sub-hidden");
      }
      if (!curHtml) {
        el.removeAttribute("hidden");
        var msNoCur = 280;
        var hStartNoCur = Math.max(el.getBoundingClientRect().height || 0, 0);
        var hTargetNoCur = hide ? 0 : hStartNoCur;
        if (!hide && allowHeightChange) {
          hTargetNoCur = measureTargetTextHeight(el, nextHtml);
        }
        if (allowHeightChange) {
          el.style.overflow = "hidden";
          el.style.height = hStartNoCur + "px";
          el.style.transition =
            "height " + msNoCur + "ms cubic-bezier(0.65, 0, 0.35, 1), opacity " + msNoCur + "ms cubic-bezier(0.65, 0, 0.35, 1)";
        } else {
          el.style.transition =
            "opacity " + msNoCur + "ms cubic-bezier(0.65, 0, 0.35, 1)";
        }
        if (isSubText) {
          el.style.transition = (
            (el.style.transition ? el.style.transition + ", " : "") +
            "margin-top " + msNoCur + "ms cubic-bezier(0.65, 0, 0.35, 1)"
          );
          if (!hide) el.style.marginTop = subGapOffset;
        }
        el.innerHTML = nextHtml;
        el.style.opacity = hide ? "1" : "0";
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (allowHeightChange) el.style.height = hTargetNoCur + "px";
            el.style.opacity = hide ? "0" : "1";
            if (isSubText) el.style.marginTop = hide ? subGapOffset : "0px";
            var tidNoCur = setTimeout(function () {
              if (hide) {
                el.style.display = el === subEl ? "none" : "";
                el.style.removeProperty("opacity");
                el.setAttribute("hidden", "");
                if (isSubText) bar.setAttribute("data-meta-sub-hidden", "1");
              } else {
                el.style.display = el === subEl ? "block" : "";
                el.removeAttribute("hidden");
                el.style.removeProperty("opacity");
                if (isSubText) bar.removeAttribute("data-meta-sub-hidden");
              }
              if (allowHeightChange) {
                el.style.removeProperty("overflow");
                el.style.removeProperty("height");
              }
              if (isSubText) el.style.removeProperty("margin-top");
              el.style.removeProperty("transition");
            }, msNoCur + 35);
            pushMetaTierTimer(bar, tidNoCur);
          });
        });
        return;
      }

      var textTweenMs = 280;
      var h = Math.max(el.getBoundingClientRect().height || 0, 1);
      var targetH = 0;
      if (!hide) {
        targetH = measureTargetTextHeight(el, nextHtml);
      }
      var animateHeight =
        allowHeightChange && (hide || Math.abs(targetH - h) > 2);
      var lockHeight = !allowHeightChange || (allowHeightChange && !hide && !animateHeight);
      el.removeAttribute("hidden");
      el.style.position = "relative";
      if (animateHeight || lockHeight) {
        el.style.overflow = "hidden";
        el.style.height = h + "px";
      }
      if (animateHeight) {
        el.style.transition =
          "height " + textTweenMs + "ms cubic-bezier(0.65, 0, 0.35, 1)";
      }
      var prevTransition = el.style.transition || "";
      el.style.transition = (
        (el.style.transition ? el.style.transition + ", " : "") +
        "opacity " + textTweenMs + "ms cubic-bezier(0.65, 0, 0.35, 1)"
      );
      if (isSubText) {
        el.style.transition = (
          (el.style.transition ? el.style.transition + ", " : "") +
          "margin-top " + textTweenMs + "ms cubic-bezier(0.65, 0, 0.35, 1)"
        );
        if (!hide) el.style.marginTop = subGapOffset;
      }
      el.style.opacity = hide ? "1" : "0";

      var outSpan = document.createElement("span");
      outSpan.style.position = "absolute";
      outSpan.style.inset = "0";
      outSpan.style.display = "block";
      outSpan.style.width = "100%";
      outSpan.style.opacity = "1";
      outSpan.style.pointerEvents = "none";
      outSpan.style.transition =
        "opacity " + textTweenMs + "ms cubic-bezier(0.65, 0, 0.35, 1)";
      outSpan.innerHTML = curHtml;

      var inSpan = document.createElement("span");
      inSpan.style.position = "absolute";
      inSpan.style.inset = "0";
      inSpan.style.display = "block";
      inSpan.style.width = "100%";
      inSpan.style.opacity = "0";
      inSpan.style.pointerEvents = "none";
      inSpan.style.transition =
        "opacity " + textTweenMs + "ms cubic-bezier(0.65, 0, 0.35, 1)";
      inSpan.innerHTML = nextHtml;

      el.innerHTML = "";
      el.appendChild(outSpan);
      el.appendChild(inSpan);

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.style.opacity = hide ? "0" : "1";
          if (isSubText) el.style.marginTop = hide ? subGapOffset : "0px";
          outSpan.style.opacity = "0";
          inSpan.style.opacity = hide ? "0" : "1";
          if (animateHeight) el.style.height = targetH + "px";
            var tidCleanup = setTimeout(function () {
            el.innerHTML = nextHtml;
            if (hide) {
              el.style.display = el === subEl ? "none" : "";
              el.style.removeProperty("opacity");
              el.style.height = "0px";
              el.style.minHeight = "0";
              el.setAttribute("hidden", "");
              if (isSubText) bar.setAttribute("data-meta-sub-hidden", "1");
            } else {
              el.style.display = el === subEl ? "block" : "";
              el.style.removeProperty("opacity");
              el.style.removeProperty("height");
              el.style.removeProperty("min-height");
              el.removeAttribute("hidden");
              if (isSubText) bar.removeAttribute("data-meta-sub-hidden");
            }
            if (animateHeight || lockHeight) {
              el.style.removeProperty("overflow");
              el.style.removeProperty("height");
            }
            if (animateHeight) {
              el.style.removeProperty("transition");
            }
            if (!hide) {
              el.style.removeProperty("opacity");
            }
            if (isSubText) {
              el.style.removeProperty("margin-top");
            }
            if (prevTransition) {
              el.style.transition = prevTransition;
            }
            el.style.removeProperty("position");
          }, textTweenMs + 35);
          pushMetaTierTimer(bar, tidCleanup);
        });
      });
    }

    function transitionPinnedMaxEl(el, text, opts) {
      if (!el) return;
      opts = opts || {};
      var hide = !!opts.hide;
      var animate = !!opts.animate;
      var nextText = hide ? "" : String(text || "");
      var curText = String(el.textContent || "");
      var isHiddenNow = el.hasAttribute("hidden") || el.classList.contains("is-hidden");
      var alreadyInTargetVisibility = hide ? isHiddenNow : !isHiddenNow;
      if (curText === nextText && alreadyInTargetVisibility) return;

      if (!animate) {
        el.textContent = nextText;
        if (hide) {
          el.classList.add("is-hidden");
          el.setAttribute("hidden", "");
        } else {
          el.classList.remove("is-hidden");
          el.removeAttribute("hidden");
        }
        el.style.removeProperty("opacity");
        el.style.removeProperty("transition");
        return;
      }

      var ms = 220;
      function measureTextWidth(sample) {
        var probe = document.createElement("span");
        probe.style.position = "absolute";
        probe.style.visibility = "hidden";
        probe.style.pointerEvents = "none";
        probe.style.whiteSpace = "nowrap";
        probe.style.left = "-9999px";
        probe.style.top = "0";
        probe.style.font = window.getComputedStyle(el).font;
        probe.style.letterSpacing = window.getComputedStyle(el).letterSpacing;
        probe.textContent = sample || "";
        document.body.appendChild(probe);
        var w = Math.ceil(probe.getBoundingClientRect().width || 0);
        probe.remove();
        return w;
      }

      if (!curText) {
        el.textContent = nextText;
        el.removeAttribute("hidden");
        el.classList.remove("is-hidden");
        el.style.opacity = hide ? "1" : "0";
        el.style.transition = "opacity " + ms + "ms cubic-bezier(0.65, 0, 0.35, 1)";
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            el.style.opacity = hide ? "0" : "1";
            var tidNoCur = setTimeout(function () {
              if (hide) {
                el.classList.add("is-hidden");
                el.setAttribute("hidden", "");
              } else {
                el.classList.remove("is-hidden");
                el.removeAttribute("hidden");
              }
              el.style.removeProperty("opacity");
              el.style.removeProperty("transition");
            }, ms + 35);
            pushMetaTierTimer(bar, tidNoCur);
          });
        });
        return;
      }

      el.removeAttribute("hidden");
      el.classList.remove("is-hidden");
      var lockW = Math.max(measureTextWidth(curText), measureTextWidth(nextText));
      if (lockW > 0) el.style.minWidth = lockW + "px";
      el.style.display = "inline-grid";
      el.style.gridTemplateAreas = '"stack"';

      var outSpan = document.createElement("span");
      outSpan.style.gridArea = "stack";
      outSpan.style.opacity = "1";
      outSpan.style.transition = "opacity " + ms + "ms cubic-bezier(0.65, 0, 0.35, 1)";
      outSpan.textContent = curText;

      var inSpan = document.createElement("span");
      inSpan.style.gridArea = "stack";
      inSpan.style.opacity = "0";
      inSpan.style.transition = "opacity " + ms + "ms cubic-bezier(0.65, 0, 0.35, 1)";
      inSpan.textContent = nextText;

      el.textContent = "";
      el.appendChild(outSpan);
      el.appendChild(inSpan);

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          outSpan.style.opacity = "0";
          inSpan.style.opacity = hide ? "0" : "1";
          var tidSwap = setTimeout(function () {
            el.textContent = nextText;
            el.style.removeProperty("display");
            el.style.removeProperty("grid-template-areas");
            el.style.removeProperty("min-width");
            var tidDone = setTimeout(function () {
              if (hide) {
                el.classList.add("is-hidden");
                el.setAttribute("hidden", "");
              } else {
                el.classList.remove("is-hidden");
                el.removeAttribute("hidden");
              }
            }, ms + 35);
            pushMetaTierTimer(bar, tidDone);
          }, ms);
          pushMetaTierTimer(bar, tidSwap);
        });
      });
    }

    function applyPendingBarTexts(animate) {
      if (pendingTextsApplied) return;
      pendingTextsApplied = true;
      var prevMsgHtmlForAnim =
        prevTextMem && typeof prevTextMem.msgHtml === "string"
          ? prevTextMem.msgHtml
          : (msgEl && msgEl.innerHTML) || "";
      var prevMsgIsSuccess =
        prevMsgHtmlForAnim.indexOf("free-shipping-bar__msg-inner--success") !== -1;
      var nextMsgIsSuccess =
        (pendingMsgHtml || "").indexOf("free-shipping-bar__msg-inner--success") !== -1;
      var msgHeightShouldAnimate =
        prevMsgIsSuccess !== nextMsgIsSuccess || !!animate;
      transitionTextEl(msgEl, pendingMsgHtml, {
        animate: !!animate,
        allowHeightChange: msgHeightShouldAnimate,
      });
      var prevSubVisibleForAnim =
        prevTextMem && typeof prevTextMem.subVisible === "boolean"
          ? !!prevTextMem.subVisible
          : !!(subEl && !subEl.hasAttribute("hidden"));
      transitionTextEl(subEl, pendingSubHtml, {
        animate: !!animate,
        hide: !pendingSubVisible,
        allowHeightChange: true,
      });
      transitionPinnedMaxEl(maxEl, pendingMaxText, {
        animate: !!animate,
        hide: !pendingMaxVisible,
      });
      metaBarLastTextState[barUid] = {
        msgHtml: pendingMsgHtml || "",
        subHtml: pendingSubVisible ? (pendingSubHtml || "") : "",
        subVisible: !!pendingSubVisible,
        maxText: pendingMaxVisible ? pendingMaxText : "",
        maxVisible: !!pendingMaxVisible,
      };
    }

    function applyMetaBarProgressDirect() {
      bar.classList.remove("free-shipping-bar--meta-lead-in");
      bar.removeAttribute("data-meta-knob-fadeout");
      if (isLastDisplayedTier && !wasLastTier && (prevGi == null || prevGi <= 0)) {
        bar.setAttribute("data-meta-no-knob", "1");
      } else {
        bar.removeAttribute("data-meta-no-knob");
      }
      if (isLastDisplayedTier) {
        bar.classList.add("free-shipping-bar--meta-last-tier");
      } else {
        bar.classList.remove("free-shipping-bar--meta-last-tier");
      }
      var knobShouldFadeOut =
        !metaTierAnimPrefersReduced() &&
        prevLeadPct > 0.06 &&
        leadPctTarget <= 0.05;
      if (knobShouldFadeOut) {
        /* Évite la disparition instantanée au retour au 1er palier : on fade puis on retire. */
        bar.setAttribute("data-meta-knob-fadeout", "1");
        setMetaLeadPct(bar, leadPctTarget, { forceMid: true });
        var tidDirectKnobOff = setTimeout(function () {
          bar.removeAttribute("data-meta-knob-fadeout");
          setMetaLeadPct(bar, leadPctTarget, { forceMidOff: true });
        }, META_GREEN_MS);
        pushMetaTierTimer(bar, tidDirectKnobOff);
      } else {
        setMetaLeadPct(bar, leadPctTarget);
      }
      applyMetaProgressPreservingTransition(
        prog,
        barUid,
        newTierKey,
        pct / 100,
        isMidProgress,
        leadPctTarget,
        cartC,
        gi,
        isLastDisplayedTier,
        prevFill
      );
      bar.setAttribute("data-meta-tier-key", newTierKey);
      bar.setAttribute("data-meta-was-mid", isMidProgress ? "1" : "0");
      bar.removeAttribute("data-meta-tier-anim");
      setLastAppliedMetaGi(barUid, gi);
      applyPendingBarTexts(false);
    }

    function runLeadTweenOnly() {
      if (hasNoIntermediateTiers) {
        setMetaLeadPct(bar, 0, { forceMidOff: true });
        return;
      }
      var needsGreenTween =
        Math.abs(leadPctTarget - prevLeadPct) > 0.001;
      if (!needsGreenTween) {
        setMetaLeadPct(bar, leadPctTarget);
        return;
      }
      var fromZero =
        leadPctTarget > prevLeadPct + 0.001 && prevLeadPct <= 0.06;
      var enteringLastFromStart =
        !!runEnterLastTierAnim &&
        !!isLastDisplayedTier &&
        (prevGi == null || prevGi <= 0 || prevLeadPct <= 0.06);
      var shrinkToZeroOrTiny =
        prevLeadPct > 0.06 && leadPctTarget <= 0.05;
      if (fromZero) {
        if (enteringLastFromStart) {
          setMetaLeadPct(bar, leadPctTarget, { forceMidOff: true });
          return;
        }
        /* 1er palier → autre palier : segment vert de 0 % jusqu’au % cible (transition CSS). */
        setMetaLeadPct(bar, 0, { forceMid: true });
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            setMetaLeadPct(bar, leadPctTarget);
          });
        });
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            if (shrinkToZeroOrTiny) {
              setMetaLeadPct(bar, leadPctTarget, { forceMid: true });
              if (leadPctTarget <= 0.05) {
                var tidKnobOff = setTimeout(function () {
                  setMetaLeadPct(bar, leadPctTarget, { forceMidOff: true });
                }, META_GREEN_MS);
                pushMetaTierTimer(bar, tidKnobOff);
              }
            } else {
              setMetaLeadPct(bar, leadPctTarget);
            }
          });
        });
      }
    }

    function metaTierAnimDone() {
      bar.removeAttribute("data-meta-no-knob");
      bar.classList.remove("free-shipping-bar--meta-black-at-zero");
      if (leadPctTarget <= 0.05) {
        setMetaLeadPct(bar, leadPctTarget);
      }
      writeMetaBarVisualState(
        barUid,
        pct / 100,
        newTierKey,
        isMidProgress,
        leadPctTarget,
        cartC,
        gi,
        isLastDisplayedTier,
        null
      );
      bar.removeAttribute("data-meta-tier-anim");
      bar.classList.remove("free-shipping-bar--meta-lead-in");
      setLastAppliedMetaGi(barUid, gi);
      applyPendingBarTexts(false);
    }

    /**
     * Avant-dernier → dernier : piste grise % actuel → 100 %, puis morph vert + pastille,
     * puis remplissage jusqu’au % réel du dernier palier.
     */
    function runLastDisplayedTierEnterAnim() {
      clearMetaTierAnim(bar);
      var jumpFirstToLastDirect =
        !!runEnterLastTierAnim &&
        !!isLastDisplayedTier &&
        (prevGi == null || prevGi <= 0);
      if (jumpFirstToLastDirect) bar.setAttribute("data-meta-no-knob", "1");
      else bar.removeAttribute("data-meta-no-knob");
      writeMetaBarVisualState(
        barUid,
        Math.min(1, Math.max(0, Number(prevFill) || 0)),
        newTierKey,
        isMidProgress,
        prevLeadPct,
        cartC,
        gi,
        isLastDisplayedTier,
        "enterLast"
      );
      setLastAppliedMetaGi(barUid, gi);
      bar.setAttribute("data-meta-tier-anim", "1");
      bar.setAttribute("data-meta-tier-key", newTierKey);
      bar.setAttribute("data-meta-was-mid", isMidProgress ? "1" : "0");

      bar.classList.remove("free-shipping-bar--meta-last-tier");
      bar.classList.add("free-shipping-bar--meta-lead-instant");
      setMetaLeadPct(
        bar,
        (hasNoIntermediateTiers || jumpFirstToLastDirect) ? 0 : prevLeadPct,
        (hasNoIntermediateTiers || jumpFirstToLastDirect) ? { forceMidOff: true } : null
      );
      prog.classList.add("free-shipping-bar__progress--instant");
      setMetaShippingProgressFraction(
        prog,
        Math.min(1, Math.max(0, Number(prevFill) || 0))
      );

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          prog.classList.remove("free-shipping-bar__progress--instant");
          setMetaShippingProgressFraction(prog, 1);

          var tidMorph = setTimeout(function phaseMorphLastEnter() {
            applyPendingBarTexts(true);
            bar.classList.remove("free-shipping-bar--meta-lead-instant");
            bar.classList.add("free-shipping-bar--meta-last-tier");
            void prog.offsetWidth;
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                void prog.offsetWidth;
                if (hasNoIntermediateTiers || jumpFirstToLastDirect) {
                  setMetaLeadPct(bar, 0, { forceMidOff: true });
                } else {
                  runLeadTweenOnly();
                }
              });
            });

            var tidToTarget = setTimeout(function () {
              setMetaShippingProgressFromPct(prog, pct);
              var tidDoneAll = setTimeout(metaTierAnimDone, META_TIER_MS);
              pushMetaTierTimer(bar, tidDoneAll);
            }, META_TIER_MS);
            pushMetaTierTimer(bar, tidToTarget);
          }, META_TIER_MS);
          pushMetaTierTimer(bar, tidMorph);
        });
      });
    }

    /**
     * Dernier → avant-dernier : morph piste verte → neutre + pastille, puis 100 % → % cible.
     */
    function runLastDisplayedTierExitAnim() {
      clearMetaTierAnim(bar);
      var jumpLastToFirstDirect =
        !!runExitLastTierAnim && !isLastDisplayedTier && gi === 0;
      if (hasNoIntermediateTiers || jumpLastToFirstDirect) {
        writeMetaBarVisualState(
          barUid,
          1,
          newTierKey,
          isMidProgress,
          0,
          cartC,
          gi,
          isLastDisplayedTier,
          "exitLast"
        );
        setLastAppliedMetaGi(barUid, gi);
        bar.setAttribute("data-meta-tier-anim", "1");
        bar.setAttribute("data-meta-tier-key", newTierKey);
        bar.setAttribute("data-meta-was-mid", isMidProgress ? "1" : "0");

        bar.classList.add("free-shipping-bar--meta-lead-instant");
        bar.classList.add("free-shipping-bar--meta-last-tier");
        setMetaLeadPct(bar, 0, { forceMidOff: true });
        prog.classList.add("free-shipping-bar__progress--instant");
        setMetaShippingProgressFraction(prog, 1);

        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            prog.classList.remove("free-shipping-bar__progress--instant");
            bar.classList.remove("free-shipping-bar--meta-lead-instant");
            applyPendingBarTexts(true);
            bar.classList.remove("free-shipping-bar--meta-last-tier");
            setMetaShippingProgressFromPct(prog, pct);
            var tidDoneSimple = setTimeout(metaTierAnimDone, META_TIER_MS);
            pushMetaTierTimer(bar, tidDoneSimple);
          });
        });
        return;
      }
      /* Objectif phase verte: finir exactement sur --meta-lead-pct (segment vert seul). */
      var greenBridgeFrac = 0;
      writeMetaBarVisualState(
        barUid,
        1,
        newTierKey,
        isMidProgress,
        100,
        cartC,
        gi,
        isLastDisplayedTier,
        "exitLast"
      );
      setLastAppliedMetaGi(barUid, gi);
      bar.setAttribute("data-meta-tier-anim", "1");
      bar.setAttribute("data-meta-tier-key", newTierKey);
      bar.setAttribute("data-meta-was-mid", isMidProgress ? "1" : "0");

      bar.classList.add("free-shipping-bar--meta-lead-instant");
      bar.classList.add("free-shipping-bar--meta-last-tier");
      /* Sortie dernier palier : départ 100% vert, pastille visible au bout de la zone verte. */
      bar.style.setProperty("--meta-lead-pct", "100%");
      bar.classList.add("free-shipping-bar--meta-mid-progress");
      prog.classList.add("free-shipping-bar__progress--instant");
      setMetaShippingProgressFraction(prog, 1);

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          prog.classList.remove("free-shipping-bar__progress--instant");
          bar.classList.remove("free-shipping-bar--meta-lead-instant");

          /* 1) La barre reste 100% verte puis recule jusqu'au nouveau % vert. */
          bar.classList.add("free-shipping-bar--meta-last-tier");
          setMetaLeadPct(bar, leadPctTarget, { forceMid: true });
          setMetaShippingProgressFraction(prog, greenBridgeFrac);

          var tidSwitchToBlack = setTimeout(function () {
            /* Le texte se transitionne quand la barre verte a atteint son nouveau %. */
            applyPendingBarTexts(true);
            /* 2) À 0 % : noir immédiat, puis seulement l’élargissement du remplissage en noir. */
            bar.classList.add("free-shipping-bar--meta-black-at-zero");
            bar.classList.remove("free-shipping-bar--meta-last-tier");
            prog.style.removeProperty("color");
            prog.style.removeProperty("background-color");
            var blackStartFrac = Math.max(0, Math.min(1, greenBridgeFrac));
            prog.classList.add("free-shipping-bar__progress--instant");
            setMetaShippingProgressFraction(prog, blackStartFrac);
            void prog.offsetWidth;
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                bar.classList.remove("free-shipping-bar--meta-black-at-zero");
                prog.classList.remove("free-shipping-bar__progress--instant");
                void prog.offsetWidth;
                setMetaShippingProgressFromPct(prog, pct);
              });
            });
          }, META_TIER_MS);
          pushMetaTierTimer(bar, tidSwitchToBlack);

          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              runLeadTweenOnly();
            });
          });

          var tidDoneEx = setTimeout(metaTierAnimDone, META_TIER_MS * 2);
          pushMetaTierTimer(bar, tidDoneEx);
        });
      });
    }

    function runMetaTierAnimSequence(blackPeak) {
      clearMetaTierAnim(bar);
      writeMetaBarVisualState(
        barUid,
        Math.min(1, Math.max(0, Number(prevFill) || 0)),
        newTierKey,
        isMidProgress,
        prevLeadPct,
        cartC,
        gi,
        isLastDisplayedTier,
        "generic"
      );
      setLastAppliedMetaGi(barUid, gi);
      bar.setAttribute("data-meta-tier-anim", "1");
      bar.setAttribute("data-meta-tier-key", newTierKey);
      bar.setAttribute("data-meta-was-mid", isMidProgress ? "1" : "0");

      bar.classList.add("free-shipping-bar--meta-lead-instant");
      if (wasLastTier) bar.classList.add("free-shipping-bar--meta-last-tier");
      else bar.classList.remove("free-shipping-bar--meta-last-tier");

      setMetaLeadPct(bar, prevLeadPct);
      prog.classList.add("free-shipping-bar__progress--instant");
      setMetaShippingProgressFraction(
        prog,
        Math.min(1, Math.max(0, Number(prevFill) || 0))
      );

      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          prog.classList.remove("free-shipping-bar__progress--instant");
          setMetaShippingProgressFraction(prog, blackPeak);

          var tidB = setTimeout(function phasePeakThenPhaseTwo() {
            applyPendingBarTexts(true);
            bar.classList.remove("free-shipping-bar--meta-lead-instant");

            function parallelFillAndLead() {
              setMetaShippingProgressFromPct(prog, pct);
              runLeadTweenOnly();
            }

            requestAnimationFrame(parallelFillAndLead);

            var tidDone = setTimeout(metaTierAnimDone, META_TIER_MS);
            pushMetaTierTimer(bar, tidDone);
          }, META_TIER_MS);
          pushMetaTierTimer(bar, tidB);
        });
      });
    }

    var maybeResumeInflightTierAnim = function () {
      if (!(tierAnimActive || tierAnimInFlightMem) || tierChanged) return false;
      /* Un 2e cart:updated pendant la séquence ne doit pas annuler les timers ni appliquer l’état final d’un coup
         (après innerHTML le nœud n’a plus data-meta-tier-anim : on s’appuie aussi sur tierAnimInflight en mémoire). */
      var progFillSkip =
        Number(prog.style.getPropertyValue("--meta-progress-fill")) || 0;
      var memFillSkip =
        metaVisualMem && isFinite(metaVisualMem.fill)
          ? Number(metaVisualMem.fill)
          : 0;
      if (
        tierAnimInFlightMem === "enterLast" &&
        memFillSkip > 0.04 &&
        progFillSkip <= 0.03
      ) {
        runLastDisplayedTierEnterAnim();
        return true;
      }
      if (
        tierAnimInFlightMem === "exitLast" &&
        memFillSkip > 0.15 &&
        progFillSkip <= 0.03
      ) {
        runLastDisplayedTierExitAnim();
        return true;
      }
      return false;
    };

    if (!metaTierAnimPrefersReduced()) {
      if (runEnterLastTierAnim) {
        runLastDisplayedTierEnterAnim();
      } else if (runExitLastTierAnim) {
        runLastDisplayedTierExitAnim();
      } else if (tierChanged) {
        var goingUpTier =
          prevGi != null &&
          (gi > prevGi ||
            (!wasLastTier && isLastDisplayedTier && prevGi !== gi));
        var goingDownTier =
          prevGi != null &&
          (gi < prevGi ||
            (wasLastTier && !isLastDisplayedTier));

        if (prevGi == null) {
          goingUpTier =
            leadPctTarget > prevLeadPct + 0.001 ||
            (!wasLastTier && isLastDisplayedTier);
          goingDownTier =
            leadPctTarget + 0.001 < prevLeadPct ||
            (wasLastTier && !isLastDisplayedTier);
        } else if (!goingUpTier && !goingDownTier && prevGi !== gi) {
          goingUpTier = gi > prevGi;
          goingDownTier = gi < prevGi;
        }
        if (!goingUpTier && !goingDownTier) {
          if (leadPctTarget > prevLeadPct + 0.001) goingUpTier = true;
          else if (leadPctTarget + 0.001 < prevLeadPct) goingDownTier = true;
        }

        var blackPeak = cartC >= prevCartC ? 1 : 0;
        if (goingDownTier && !goingUpTier) blackPeak = 0;
        else if (goingUpTier && !goingDownTier) blackPeak = 1;

        runMetaTierAnimSequence(blackPeak);
      } else if (!maybeResumeInflightTierAnim()) {
        applyMetaBarProgressDirect();
      }
    } else {
      if (!maybeResumeInflightTierAnim()) {
        if (tierAnimActive) {
          clearMetaTierAnim(bar);
          bar.removeAttribute("data-meta-tier-anim");
          prog.classList.remove("free-shipping-bar__progress--instant");
        }
        applyMetaBarProgressDirect();
      }
    }
  }

  function refreshCartMetaBars(e) {
    var cart = e && e.detail && e.detail.cart ? e.detail.cart : null;
    var run = function (c) {
      document
        .querySelectorAll("[data-free-shipping-meta]")
        .forEach(function (barEl) {
          applyMetaShippingBar(barEl, c);
        });
    };
    if (cart) {
      run(cart);
      return;
    }
    var url = (theme && theme.routes && theme.routes.cart_url) || "/cart";
    fetch(url.replace(/\/$/, "") + ".js")
      .then(function (r) {
        return r.json();
      })
      .then(run)
      .catch(function () {
        if (typeof Shopify !== "undefined" && Shopify.designMode) {
          console.warn("[CartShippingBar] cart.js request failed");
        }
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refreshCartMetaBars);
  } else {
    refreshCartMetaBars();
  }

  document.addEventListener("cart:updated", refreshCartMetaBars);
  document.addEventListener("shopify:section:load", refreshCartMetaBars);
})();

/* ====================================================================================================
[CUSTOM] HELP DESK — date de livraison dynamique (remplace [date_livraison] dans les textes du bloc)
Utilise les données embarquées via snippets/help-desk-shipping-data.liquid.
==================================================================================================== */
(function () {
  function hdPad2(n) {
    return (n < 10 ? "0" : "") + n;
  }

  function hdDateKeyLocal(d) {
    return (
      d.getFullYear() +
      "-" +
      hdPad2(d.getMonth() + 1) +
      "-" +
      hdPad2(d.getDate())
    );
  }

  function hdHolidayKeyFromEntry(entry) {
    if (entry == null) return "";
    if (typeof entry === "object") {
      if (typeof entry.date === "string") return hdHolidayKeyFromEntry(entry.date);
      if (typeof entry.value === "string") return hdHolidayKeyFromEntry(entry.value);
    }
    var s = String(entry).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    var m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : "";
  }

  function hdHolidaySetFromRow(row) {
    var set = Object.create(null);
    if (!row || !Array.isArray(row.holidays)) return set;
    row.holidays.forEach(function (entry) {
      var k = hdHolidayKeyFromEntry(entry);
      if (k) set[k] = true;
    });
    return set;
  }

  function hdIsBusinessDate(d, holidaySet) {
    var day = d.getDay();
    return day !== 0 && day !== 6 && !holidaySet[hdDateKeyLocal(d)];
  }

  function hdAddCalendarDays(d, n) {
    var out = new Date(d.getTime());
    out.setHours(12, 0, 0, 0);
    var k = Math.max(0, Math.round(Number(n) || 0));
    for (var i = 0; i < k; i++) out.setDate(out.getDate() + 1);
    return out;
  }

  function hdAddWorkingDays(start, n, holidaySet) {
    var d = new Date(start.getTime());
    d.setHours(12, 0, 0, 0);
    while (!hdIsBusinessDate(d, holidaySet)) d.setDate(d.getDate() + 1);
    var target = Math.max(0, Math.round(Number(n) || 0));
    var counted = 0;
    while (counted < target) {
      d.setDate(d.getDate() + 1);
      if (hdIsBusinessDate(d, holidaySet)) counted++;
    }
    return d;
  }

  function hdEstimateDelivery(procCalendar, transitWorking, holidaySet) {
    var now = new Date();
    var extraWorking = now.getHours() >= 12 ? 1 : 0;
    var d = new Date(now.getTime());
    d.setHours(12, 0, 0, 0);
    d = hdAddCalendarDays(d, procCalendar);
    var tw = Math.max(0, Math.round(Number(transitWorking) || 0));
    return hdAddWorkingDays(d, tw + extraWorking, holidaySet);
  }

  function hdFormatDate(d, i18n) {
    var wd = i18n.weekdays_abbr;
    var mo = i18n.months_abbr;
    if (
      !Array.isArray(wd) ||
      !Array.isArray(mo) ||
      wd.length < 7 ||
      mo.length < 12
    )
      return "";
    var weekday = wd[d.getDay()];
    var month = mo[d.getMonth()];
    if (weekday == null || month == null) return "";
    return String(weekday) + " " + d.getDate() + " " + String(month);
  }

  function hdGetEarliestDeliveryStr(row, i18n) {
    if (!row || !i18n) return "";
    var opts = row.shippingOptions;
    if (!Array.isArray(opts) || !opts.length) return "";
    var proc = Number(row.processingDays) || 0;
    var holidaySet = hdHolidaySetFromRow(row);
    var minDmin = null;
    opts.forEach(function (opt) {
      (Array.isArray(opt.tiers) ? opt.tiers : []).forEach(function (t) {
        var dmin = Number(t.dmin);
        if (isFinite(dmin) && (minDmin === null || dmin < minDmin)) {
          minDmin = dmin;
        }
      });
    });
    if (minDmin === null) return "";
    var date = hdEstimateDelivery(proc, minDmin, holidaySet);
    return hdFormatDate(date, i18n);
  }

  function hdUpdateSpans(dateStr) {
    // Global : help desk produit + barre d'annonces (+ tout autre span token)
    document.querySelectorAll("[data-hd-delivery-date]").forEach(function (span) {
      span.textContent = dateStr || "";
    });
  }

  function hdUpdateReturns(row) {
    var isFree = !row || row.returns === true || row.returns === "true";
    // Help desk produit + texte sous bouton de paiement du cart drawer
    document.querySelectorAll("[data-hd-returns-free]").forEach(function (el) {
      el.hidden = !isFree;
    });
    document.querySelectorAll("[data-hd-returns-paid]").forEach(function (el) {
      el.hidden = isFree;
    });
    document.dispatchEvent(new CustomEvent("helpdesk:content-updated"));
  }

  /** Premier palier (seuil min le plus bas) avec frais à 0 parmi les options de la zone. */
  function hdGetFirstFreeShippingThreshold(row) {
    var opts = row && row.shippingOptions;
    if (!Array.isArray(opts) || !opts.length) return null;
    var best = null;
    opts.forEach(function (opt) {
      (Array.isArray(opt.tiers) ? opt.tiers : []).forEach(function (t) {
        if (!t) return;
        var isZero =
          t.feeIsZero === true ||
          t.feeIsZero === "true" ||
          Number(t.feeC) === 0;
        if (!isZero) return;
        var minC =
          t.minC != null && isFinite(Number(t.minC))
            ? Math.round(Number(t.minC))
            : 0;
        if (best === null || minC < best.minC) {
          best = {
            minC: minC,
            formatted:
              theme &&
              theme.Currency &&
              typeof theme.Currency.formatThemeMoney === "function"
                ? theme.Currency.formatThemeMoney(minC)
                : String(t.minFormatted || "").trim(),
          };
        }
      });
    });
    return best;
  }

  function hdUpdateFreeShipping(row) {
    var thr = hdGetFirstFreeShippingThreshold(row);
    var hasFs = !!thr;
    var label = thr && thr.formatted ? thr.formatted : "";

    // Global : help desk produit + barre d'annonces (+ tout autre span token)
    document
      .querySelectorAll("[data-hd-free-shipping-threshold]")
      .forEach(function (span) {
        span.textContent = label;
      });

    document.querySelectorAll("[data-hd-fs-yes]").forEach(function (el) {
      el.hidden = !hasFs;
    });
    document.querySelectorAll("[data-hd-fs-no]").forEach(function (el) {
      el.hidden = hasFs;
    });

    // Barre d'annonces : masquer le slide si mode FS + pas de texte alternatif
    hdSyncAnnouncementFsSlides(hasFs);

    document.dispatchEvent(new CustomEvent("helpdesk:content-updated"));
  }

  /**
   * Si free_shipping_mode est actif et text_no_fs vide : on retire le slide
   * du carrousel / marquee quand le pays n'a pas de palier gratuit.
   * (En éditeur thème, on ne masque pas pour garder le bloc sélectionnable.)
   */
  function hdEnsureAbFsPark(root) {
    var park = root.querySelector("[data-ab-fs-park]");
    if (park) return park;
    park = document.createElement("div");
    park.setAttribute("data-ab-fs-park", "");
    park.hidden = true;
    root.appendChild(park);
    return park;
  }

  function hdSyncAnnouncementFsSlides(hasFs) {
    if (window.Shopify && Shopify.designMode) return;

    document.querySelectorAll(".announcement-bar").forEach(function (root) {
      var park = null;
      var bar = root.querySelector("announcement-bar");
      var marqueeTrack = root.querySelector(".marquee");
      var changed = false;

      root.querySelectorAll("[data-ab-fs-mode][data-ab-fs-hide-when-no]").forEach(
        function (slide) {
          var shouldHide = !hasFs;
          var parked =
            slide.parentElement &&
            slide.parentElement.hasAttribute("data-ab-fs-park");

          if (shouldHide && !parked) {
            park = park || hdEnsureAbFsPark(root);
            if (bar && bar.slider && typeof bar.slider.remove === "function") {
              try {
                bar.slider.remove(slide);
              } catch (e) {
                if (slide.parentNode) slide.parentNode.removeChild(slide);
              }
            } else if (slide.parentNode) {
              slide.parentNode.removeChild(slide);
            }
            park.appendChild(slide);
            if (bar) bar._items = null;
            changed = true;
          } else if (!shouldHide && parked) {
            if (bar) {
              if (bar.slider && typeof bar.slider.append === "function") {
                try {
                  bar.slider.append(slide);
                } catch (e) {
                  bar.appendChild(slide);
                }
              } else {
                bar.appendChild(slide);
              }
              bar._items = null;
            } else if (marqueeTrack) {
              marqueeTrack.appendChild(slide);
            }
            changed = true;
          }
        },
      );

      if (!changed || !bar) return;

      var visibleCount = bar.querySelectorAll(
        ":scope > .announcement__slide",
      ).length;

      if (bar.slider) {
        if (visibleCount <= 1) {
          if (typeof bar.stopAutoplayTimer === "function") {
            bar.stopAutoplayTimer();
          }
          try {
            bar.slider.destroy();
          } catch (e) {}
          bar.slider = undefined;
          bar.initialized = false;
          bar.removeAttribute("loaded");
          if (visibleCount === 1) bar.setAttribute("loaded", "");
        } else if (typeof bar.slider.reloadCells === "function") {
          bar.slider.reloadCells();
          bar.slider.resize();
          if (typeof bar.startAutoplayTimer === "function") {
            bar.startAutoplayTimer();
          }
        }
      } else if (visibleCount > 1 && typeof bar.init === "function") {
        bar.initialized = false;
        bar.init();
      }
    });
  }

  function hdRefreshStandalone() {
    var soloEl = document.querySelector("[data-hd-helpdesk-data]");
    if (!soloEl) {
      hdUpdateSpans("");
      hdUpdateReturns(null);
      hdUpdateFreeShipping(null);
      return;
    }
    var payload;
    try {
      payload = JSON.parse(soloEl.textContent.trim());
    } catch (e) {
      return;
    }
    var row = payload && payload.data;
    if (!row) {
      hdUpdateSpans("");
      hdUpdateReturns(null);
      hdUpdateFreeShipping(null);
      return;
    }
    hdUpdateSpans(hdGetEarliestDeliveryStr(row, payload.i18n));
    hdUpdateReturns(row);
    hdUpdateFreeShipping(row);
  }

  function hdInit() {
    hdRefreshStandalone();
    // Modal / quickview : le contenu (dont le bloc bureau d'assistance) est
    // injecté après coup — on recalcule les dates une fois le contenu en place.
    document.addEventListener("quickview:loaded", hdRefreshStandalone);
    // Cart drawer : le footer (note retour) est re-rendu via Section Rendering API.
    document.addEventListener("cart:updated", hdRefreshStandalone);
    // Theme editor / section re-render (ex. barre d'annonces)
    document.addEventListener("shopify:section:load", hdRefreshStandalone);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hdInit);
  } else {
    hdInit();
  }
})();

/* ====================================================================================================
[CUSTOM] HELP DESK — slider par rangées (1↔3, 2↔4), boucle infinie + swipe manuel
==================================================================================================== */
(function () {
  var hdSliderRoots = [];
  var DRAG_THRESHOLD = 40;

  function hdSliderPairTracks(root) {
    return root.querySelectorAll("[data-hd-row-pair] .help-desk__row-track");
  }

  function hdSliderCellHasVisibleText(cell) {
    if (!cell || cell.hidden) return false;

    var fsYes = cell.querySelector("[data-hd-fs-yes]");
    var fsNo = cell.querySelector("[data-hd-fs-no]");
    if (fsYes || fsNo) {
      var fsVis = null;
      if (fsYes && !fsYes.hidden) fsVis = fsYes;
      else if (fsNo && !fsNo.hidden) fsVis = fsNo;
      return !!(fsVis && String(fsVis.textContent || "").trim());
    }

    var retFree = cell.querySelector("[data-hd-returns-free]");
    var retPaid = cell.querySelector("[data-hd-returns-paid]");
    if (retFree || retPaid) {
      var retVis = null;
      if (retFree && !retFree.hidden) retVis = retFree;
      else if (retPaid && !retPaid.hidden) retVis = retPaid;
      return !!(retVis && String(retVis.textContent || "").trim());
    }

    return String(cell.textContent || "").trim() !== "";
  }

  /**
   * Une rangée ne slide que s'il reste AU MOINS 2 cellules avec du texte visible.
   * Ex. : pas de texte 3 → la rangée du texte 1 n'a plus data-hd-row-pair → pas de slide.
   */
  function hdSliderSyncPairs(root) {
    if (!root) return;
    root.querySelectorAll(".help-desk__row").forEach(function (row) {
      var cells = Array.prototype.slice.call(
        row.querySelectorAll(".help-desk__cell")
      );
      cells.forEach(function (cell) {
        // Masquer les cellules sans texte visible (plus d'icône orpheline)
        cell.hidden = !hdSliderCellHasVisibleText(cell);
      });

      var activeCount = cells.filter(function (cell) {
        return !cell.hidden;
      }).length;
      var track = row.querySelector(".help-desk__row-track");

      if (activeCount >= 2) {
        row.setAttribute("data-hd-row-pair", "");
      } else {
        row.removeAttribute("data-hd-row-pair");
        if (track) {
          track.style.transition = "none";
          track.style.transform = "translate3d(0, 0, 0)";
        }
      }
    });

    // Repositionner uniquement les rangées encore en paire
    hdSliderGoTo(root, root._hdSliderIndex || 0, false);
  }

  function hdSliderSyncAll() {
    document.querySelectorAll(".product__help-desk").forEach(function (root) {
      if (!root.querySelector("[data-hd-slider]")) return;
      hdSliderSyncPairs(root);
    });
  }

  function hdSliderGoTo(root, index, animate) {
    var slider = root.querySelector("[data-hd-slider]");
    if (!slider) return;
    var dots = root.querySelectorAll("[data-hd-dot]");
    var i = ((index % 2) + 2) % 2;
    root._hdSliderIndex = i;

    var width = slider.clientWidth || 0;
    var tracks = hdSliderPairTracks(root);
    tracks.forEach(function (track) {
      if (animate === false) {
        var prev = track.style.transition;
        track.style.transition = "none";
        track.style.transform = "translate3d(-" + i * width + "px, 0, 0)";
        void track.offsetWidth;
        track.style.transition = prev || "";
      } else {
        track.style.transition = "";
        track.style.transform = "translate3d(-" + i * width + "px, 0, 0)";
      }
    });

    // Rangées sans paire : forcer position 0
    root.querySelectorAll(".help-desk__row:not([data-hd-row-pair]) .help-desk__row-track").forEach(function (track) {
      track.style.transform = "translate3d(0, 0, 0)";
    });

    dots.forEach(function (dot) {
      var di = parseInt(dot.getAttribute("data-hd-dot"), 10);
      var active = di === i;
      dot.classList.toggle("is-active", active);
      dot.setAttribute("aria-selected", active ? "true" : "false");
    });
  }

  function hdSliderStop(root) {
    if (root._hdSliderTimer) {
      clearTimeout(root._hdSliderTimer);
      root._hdSliderTimer = null;
    }
  }

  function hdSliderSchedule(root) {
    hdSliderStop(root);
    var slider = root.querySelector("[data-hd-slider]");
    if (!slider || document.hidden) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!hdSliderPairTracks(root).length) return;

    var speed = parseFloat(slider.getAttribute("data-hd-slider-speed")) || 3;
    if (speed < 1) speed = 3;

    root._hdSliderTimer = setTimeout(function () {
      hdSliderGoTo(root, (root._hdSliderIndex || 0) + 1);
      hdSliderSchedule(root);
    }, speed * 1000);
  }

  function hdSliderBindWheel(root, slider) {
    var acc = 0;
    var locked = false;
    var WHEEL_THRESHOLD = 28;
    var COOLDOWN_MS = 480;

    slider.addEventListener(
      "wheel",
      function (e) {
        if (!hdSliderPairTracks(root).length) return;

        var dx = e.deltaX;
        var dy = e.deltaY;
        var absX = Math.abs(dx);
        var absY = Math.abs(dy);

        // Trackpad horizontal, ou Shift + molette verticale
        if (e.shiftKey && absY >= absX) {
          dx = dy;
          absX = absY;
        }

        // Scroll vertical de page : ne pas intercepter
        if (absX < 2 || absX < absY) return;

        e.preventDefault();
        if (locked) return;

        acc += dx;
        if (Math.abs(acc) < WHEEL_THRESHOLD) return;

        // Swipe trackpad vers la gauche (deltaX > 0) → slide suivant
        var dir = acc > 0 ? 1 : -1;
        acc = 0;
        locked = true;
        hdSliderGoTo(root, (root._hdSliderIndex || 0) + dir);
        hdSliderSchedule(root);
        setTimeout(function () {
          locked = false;
          acc = 0;
        }, COOLDOWN_MS);
      },
      { passive: false }
    );
  }

  function hdSliderBindDrag(root, slider) {
    var startX = 0;
    var startY = 0;
    var deltaX = 0;
    var dragging = false;
    var decided = false;
    var horizontal = false;
    var width = 0;
    var baseIndex = 0;
    var pointerId = null;

    function tracks() {
      return hdSliderPairTracks(root);
    }

    function onPointerDown(e) {
      // Desktop : le slide manuel se fait au trackpad (wheel).
      // Pointer drag reste pour le tactile (doigt).
      if (e.pointerType === "mouse") return;
      if (e.button != null && e.button !== 0) return;
      if (e.target.closest && e.target.closest(".help-desk__dot, a, button.link")) return;
      if (!tracks().length) return;

      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      deltaX = 0;
      dragging = true;
      decided = false;
      horizontal = false;
      width = slider.clientWidth || 1;
      baseIndex = root._hdSliderIndex || 0;
      hdSliderStop(root);

      tracks().forEach(function (track) {
        track.style.transition = "none";
      });

      if (slider.setPointerCapture) {
        try {
          slider.setPointerCapture(pointerId);
        } catch (err) {}
      }
    }

    function onPointerMove(e) {
      if (!dragging) return;
      if (pointerId != null && e.pointerId !== pointerId) return;

      var dx = e.clientX - startX;
      var dy = e.clientY - startY;

      if (!decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        decided = true;
        horizontal = Math.abs(dx) > Math.abs(dy);
        if (!horizontal) {
          dragging = false;
          tracks().forEach(function (track) {
            track.style.transition = "";
          });
          hdSliderSchedule(root);
          return;
        }
        slider.classList.add("is-dragging");
      }

      if (!horizontal) return;
      e.preventDefault();

      deltaX = dx;
      var offset = -baseIndex * width + deltaX;
      var min = -width;
      var max = 0;
      if (offset > max) offset = max + (offset - max) * 0.25;
      if (offset < min) offset = min + (offset - min) * 0.25;

      tracks().forEach(function (track) {
        track.style.transform = "translate3d(" + offset + "px, 0, 0)";
      });
    }

    function onPointerUp(e) {
      if (!dragging && !decided) return;
      if (pointerId != null && e.pointerId !== pointerId) return;

      var wasHorizontal = horizontal;
      dragging = false;
      slider.classList.remove("is-dragging");
      pointerId = null;

      tracks().forEach(function (track) {
        track.style.transition = "";
      });

      if (wasHorizontal) {
        var next = baseIndex;
        if (deltaX <= -DRAG_THRESHOLD) next = baseIndex + 1;
        else if (deltaX >= DRAG_THRESHOLD) next = baseIndex - 1;
        hdSliderGoTo(root, next);
      } else {
        hdSliderGoTo(root, baseIndex, false);
      }
      hdSliderSchedule(root);
    }

    slider.addEventListener("pointerdown", onPointerDown);
    slider.addEventListener("pointermove", onPointerMove);
    slider.addEventListener("pointerup", onPointerUp);
    slider.addEventListener("pointercancel", onPointerUp);
  }

  function hdSliderInit(root) {
    if (!root || root.dataset.hdSliderInit === "1") return;
    var slider = root.querySelector("[data-hd-slider]");
    if (!slider) return;
    root.dataset.hdSliderInit = "1";
    root._hdSliderIndex = 0;
    hdSliderRoots.push(root);

    root.querySelectorAll("[data-hd-dot]").forEach(function (dot) {
      dot.addEventListener("click", function () {
        var i = parseInt(dot.getAttribute("data-hd-dot"), 10) || 0;
        hdSliderGoTo(root, i);
        hdSliderSchedule(root);
      });
    });

    hdSliderBindWheel(root, slider);
    hdSliderBindDrag(root, slider);

    window.addEventListener("resize", function () {
      hdSliderGoTo(root, root._hdSliderIndex || 0, false);
    });

    hdSliderSyncPairs(root);
    hdSliderSchedule(root);
  }

  function hdSliderInitAll() {
    hdSliderRoots = hdSliderRoots.filter(function (root) {
      if (!document.contains(root)) {
        hdSliderStop(root);
        return false;
      }
      return true;
    });
    document.querySelectorAll("[data-hd-slider]").forEach(function (slider) {
      var root = slider.closest(".product__help-desk");
      if (root) hdSliderInit(root);
    });
  }

  function hdSliderBoot() {
    hdSliderInitAll();
    document.addEventListener("shopify:section:load", hdSliderInitAll);
    document.addEventListener("quickview:loaded", hdSliderInitAll);
    document.addEventListener("helpdesk:content-updated", function () {
      hdSliderSyncAll();
      hdSliderRoots.forEach(function (root) {
        if (document.contains(root)) hdSliderSchedule(root);
      });
    });
    document.addEventListener("visibilitychange", function () {
      hdSliderRoots.forEach(function (root) {
        if (!document.contains(root)) return;
        if (document.hidden) hdSliderStop(root);
        else hdSliderSchedule(root);
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", hdSliderBoot);
  } else {
    hdSliderBoot();
  }
})();

/* ====================================================================================================
[STOYIK] BBcode global parser — converts [b]/[/b], [i]/[/i], [u]/[/u] in all text nodes
==================================================================================================== */
(() => {
  const BBCODE_RE = /\[b\]|\[\/b\]|\[i\]|\[\/i\]|\[u\]|\[\/u\]/;
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'CODE', 'PRE']);

  function parseBBCode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      if (BBCODE_RE.test(node.textContent)) {
        const span = document.createElement('span');
        span.innerHTML = node.textContent
          .replace(/\[b\]/g, '<strong>')
          .replace(/\[\/b\]/g, '</strong>')
          .replace(/\[i\]/g, '<em>')
          .replace(/\[\/i\]/g, '</em>')
          .replace(/\[u\]/g, '<u>')
          .replace(/\[\/u\]/g, '</u>');
        node.parentNode.replaceChild(span, node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && !SKIP_TAGS.has(node.tagName)) {
      Array.from(node.childNodes).forEach(parseBBCode);
    }
  }

  function runParser() {
    parseBBCode(document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runParser);
  } else {
    runParser();
  }

  // [CUSTOM] Le cart drawer (et autres sections) sont re-rendus dynamiquement
  // (Section Rendering) : les nouveaux nœuds contiennent des [b] bruts. On
  // ré-applique le parser après chaque mise à jour du panier.
  document.addEventListener('cart:updated', runParser);
  document.addEventListener('cart:refresh', runParser);
  if (window.theme && theme.pubsub && theme.pubsub.subscribe && theme.pubsub.PUB_SUB_EVENTS && theme.pubsub.PUB_SUB_EVENTS.cartUpdate) {
    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function () {
      requestAnimationFrame(runParser);
    });
  }
})();

// ADDED (STOYIK) — Titre / avis PDP : marge de stack dynamique
// Remplace le snippet Liquid product-title-suppress-stack-margin (statique, non mis à jour au changement de variante).
// Supprime la margin-block-start du .product__title / .product__rating-above s'il n'y a aucun bloc rempli au-dessus.
// Un MutationObserver surveille les changements de contenu dans product-info (badges peuplés tardivement,
// mises à jour au changement de variante, etc.) pour rester synchronisé en permanence.
(() => {
  function isBlockFilled(el) {
    const tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE') return false;
    if (getComputedStyle(el).display === 'none') return false;
    if (el.offsetHeight === 0) return false;
    // <hr> : élément visuel sans contenu texte, compte quand même
    if (tag === 'HR') return true;
    // Élément avec des enfants (variant picker, badges avec contenu, etc.)
    if (el.children.length > 0) return true;
    // Sinon vérifier qu'il y a du texte (ex: SKU vide → <p> sans texte ne compte pas)
    return el.textContent.trim().length > 0;
  }

  function updateStackMargins(productInfo) {
    const siblings = Array.from(productInfo.children);

    const ratingAboveEl = productInfo.querySelector(':scope > .product__rating-above');
    if (ratingAboveEl) {
      const ratingIndex = siblings.indexOf(ratingAboveEl);
      const hasPrecedingContent =
        ratingIndex > 0 && siblings.slice(0, ratingIndex).some(isBlockFilled);
      ratingAboveEl.classList.toggle(
        'product__rating-above--no-stack-margin',
        !hasPrecedingContent
      );
    }

    const titleEl = productInfo.querySelector(':scope > .product__title');
    if (titleEl) {
      const titleIndex = siblings.indexOf(titleEl);
      const hasPrecedingContent =
        titleIndex > 0 && siblings.slice(0, titleIndex).some(isBlockFilled);
      titleEl.classList.toggle('product__title--no-stack-margin', !hasPrecedingContent);
    }
  }

  function observe(productInfo) {
    // childList + subtree : détecte les insertions/suppressions de nœuds dans tout le sous-arbre
    // (ex: updateSourceFromDestination remplace le innerHTML des badges → mutation childList).
    // Les changements d'attribut class sur .product__title (notre propre toggle) ne sont pas
    // des mutations childList, donc pas de boucle infinie.
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => updateStackMargins(productInfo));
    });
    observer.observe(productInfo, { childList: true, subtree: true });
  }

  document.querySelectorAll('product-info').forEach(pi => {
    updateStackMargins(pi);
    observe(pi);
  });

  // Re-applique sur les product-info injectés par le modal (quickview)
  document.addEventListener('quickview:loaded', () => {
    document.querySelectorAll('.quick-view product-info').forEach(pi => {
      updateStackMargins(pi);
      observe(pi);
    });
  });
})();

// ADDED (STOYIK) — Gestion HelpDrawer en contexte modal
(function () {
  // Monte le z-index du HelpDrawer au-dessus du modal (z-40) quand le modal est ouvert
  function liftHelpDrawer() {
    var drawer = document.getElementById('HelpDrawer');
    if (drawer) {
      drawer.classList.remove('z-30');
      drawer.classList.add('z-50');
    }
  }

  // Restaure le z-index normal du HelpDrawer quand le modal se ferme
  function restoreHelpDrawer() {
    var drawer = document.getElementById('HelpDrawer');
    if (drawer) {
      drawer.classList.remove('z-50');
      drawer.classList.add('z-30');
    }
  }

  // Force le re-binding natif du tiroir sur les boutons actuellement dans le DOM.
  //
  // Problème : DrawerElement.connectedCallback() bind les [aria-controls="HelpDrawer"]
  // présents au moment de sa connexion. Comme le tiroir est dans l'overlay-group (rendu
  // au chargement de la page), son connectedCallback s'est exécuté AVANT que les boutons
  // du quickview n'existent → aucun bouton n'est bindé.
  //
  // Solution : on détache puis on réattache l'élément à son parent. Cela déclenche
  // disconnectedCallback (qui annule tous les anciens listeners) puis connectedCallback
  // (qui re-scanne le DOM et trouve le bouton du quickview). On obtient exactement UN
  // listener natif sur le bouton, sans jamais en ajouter un second depuis notre code.
  // Cela évite le double-listener qui causait l'ouverture-fermeture instantanée.
  function rebindHelpDrawer() {
    var drawer = document.getElementById('HelpDrawer');
    if (!drawer || drawer.open) return;
    var parent = drawer.parentElement;
    if (!parent || parent === document.body) return;
    parent.removeChild(drawer);
    parent.appendChild(drawer);
  }

  document.addEventListener('quickview:loaded', function () {
    liftHelpDrawer();
    rebindHelpDrawer();
  });

  // Restaure le z-index quand le modal (quick-view) se ferme
  document.addEventListener('modal:afterHide', function (e) {
    if (e.target && e.target.classList.contains('quick-view')) {
      restoreHelpDrawer();
    }
  });
})();

// ADDED (STOYIK) — Avis fiche produit : repli sur 1 étoile si le contenu dépasse la cellule grid
(() => {
  const SELECTOR = '.product__rating--all-stars';
  const COMPACT_CLASS = 'product__rating--stars-compact';
  const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');
  let resizeFrame = null;

  function getAvailableWidth(el) {
    const savedWidth = el.style.width;
    const savedMaxWidth = el.style.maxWidth;
    el.style.width = '100%';
    el.style.maxWidth = '100%';
    void el.offsetWidth;
    const available = el.offsetWidth;
    el.style.width = savedWidth;
    el.style.maxWidth = savedMaxWidth;
    return available;
  }

  function measureNeededWidth(el) {
    const clone = el.cloneNode(true);
    clone.classList.remove(COMPACT_CLASS);
    clone.setAttribute('aria-hidden', 'true');

    const mount = document.createElement('div');
    mount.className = 'product__info';
    mount.setAttribute('aria-hidden', 'true');
    mount.style.cssText = [
      'position:fixed',
      'left:-9999px',
      'top:0',
      'visibility:hidden',
      'pointer-events:none',
      'width:max-content',
      'max-width:none',
    ].join(';');
    mount.appendChild(clone);
    document.body.appendChild(mount);

    const needed = clone.getBoundingClientRect().width;
    document.body.removeChild(mount);
    return needed;
  }

  function updateRating(el) {
    if (DESKTOP_MQ.matches) {
      el.classList.remove(COMPACT_CLASS);
      return;
    }

    const available = getAvailableWidth(el);
    el.classList.remove(COMPACT_CLASS);
    void el.offsetWidth;
    const needed = measureNeededWidth(el);
    el.classList.toggle(COMPACT_CLASS, needed > available + 1);
  }

  function updateAll() {
    document.querySelectorAll(SELECTOR).forEach(updateRating);
  }

  function scheduleUpdate() {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = requestAnimationFrame(updateAll);
    });
  }

  function observeRatings(root) {
    if (!root) return;
    root.querySelectorAll(SELECTOR).forEach(updateRating);
  }

  if (!window.__ratingStarsAdaptiveInit) {
    window.__ratingStarsAdaptiveInit = true;

    scheduleUpdate();

    window.addEventListener('resize', scheduleUpdate);
    DESKTOP_MQ.addEventListener('change', scheduleUpdate);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(scheduleUpdate);
      const watched = new WeakSet();

      const watchElement = (node) => {
        if (!node || watched.has(node)) return;
        watched.add(node);
        observer.observe(node);
      };

      const watch = (root) => {
        root.querySelectorAll(SELECTOR).forEach((el) => {
          watchElement(el);
          if (el.parentElement) {
            watchElement(el.parentElement);
          }
        });
      };

      watch(document);
      document.addEventListener('quickview:loaded', () => {
        document.querySelectorAll('.quick-view').forEach(watch);
        scheduleUpdate();
      });
    }

    document.addEventListener('shopify:section:load', (e) => {
      observeRatings(e.target);
      scheduleUpdate();
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleUpdate);
    }
  }
})();

/* ====================================================================================================
SMOOTH SCROLL — same-page anchor links
==================================================================================================== */

(() => {
  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const getSamePageAnchorUrl = (href) => {
    if (!href) return null;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return null;
    }

    if (!url.hash || url.hash === "#") return null;
    if (url.origin !== window.location.origin) return null;
    if (url.pathname !== window.location.pathname) return null;
    if (url.search !== window.location.search) return null;

    return url;
  };

  const scrollToAnchor = (hash) => {
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return false;

    target.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });

    return true;
  };

  if (!window.__samePageAnchorScrollInit) {
    window.__samePageAnchorScrollInit = true;

    document.addEventListener("click", (event) => {
      const link = event.target.closest("a[href]");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) {
        return;
      }

      const url = getSamePageAnchorUrl(link.getAttribute("href"));
      if (!url) return;

      if (!scrollToAnchor(url.hash)) return;

      event.preventDefault();

      const nextUrl = url.pathname + url.search + url.hash;
      if (window.location.pathname + window.location.search + window.location.hash !== nextUrl) {
        history.pushState(null, "", nextUrl);
      }
    });
  }
})();
